/**
 * App shell. Initializes the three-panel layout, wires state store,
 * renderer, bridge, section manager, palette, toolbar, and property panel.
 * This is the entry module loaded by index.html.
 */

import { editableIdRepairAllocationReady } from '../editing/editable-id-repair-admission.js';
import { SAVE_LABELS } from '../shared/ui-vocabulary.js';
import { onProjectIdentityChange } from '../core/project-identity.js';
import { sectionHasRuntime } from '../shared/behaviour-contract.js';
import { ANIM_MS } from '../shared/timing.js';
import { INITIAL_AUTH_TIMEOUT_MS, shouldDegradeInitialAuth } from '../shared/auth-boot-policy.js';
import {
  initViewportService,
  isPhoneViewport,
  onViewportChange
} from '../shared/viewport-service.js';
import {
  store,
  createDefaultState,
  WRITE_BLOCKER,
  LOCAL_PRINCIPAL_SCOPE,
  readOnlyReasonFor
} from '../core/state.js';
import { readLastOpenedProject, writeLastOpenedProject } from '../core/last-opened-project.js';
import { refreshUnseenShares } from '../core/share-inbox.js';
import {
  LIFECYCLE,
  LIFECYCLE_OPERATION,
  classifyProjectSignal,
  configureProjectLifecycleObserver,
  unavailableMessageFor
} from '../core/project-lifecycle.js';
import { recordSessionBoundaryMetric } from '../core/session-boundary-observability.js';
import {
  LIFECYCLE_MESSAGE,
  configureLifecycleChannel,
  subscribeLifecycle,
  postSignedOut
} from '../core/lifecycle-channel.js';
import { eventBus } from '../core/events.js';
import { isOwnedPopoverTarget } from './popover-menu.js';
import { initUnloadGuard } from '../core/unload-guard.js';
import { createTabGuard } from '../core/tab-guard.js';
import { initReportError, reportError } from '../core/report-error.js';
import { runLogoutSequence } from '../core/logout-flow.js';
import { showUnsavedOnLogoutDialog } from './unsaved-on-logout-dialog.js';
import { installBreadcrumbTaps, installFetchTap } from '../core/breadcrumbs.js';
import { normalizeErrorPayload } from '../shared/error-payload.js';
import {
  initExportEventQueue,
  stageExportEvent,
  finalizeExportEvent
} from '../core/export-event-queue.js';
import { setAssetFetchCachePrincipal } from '../core/asset-fetch-cache.js';
import { showToast, dismissToastById, dismissToastsByPrefix, showProgressToast } from './toast.js';
import { isAgentReachable } from './agent-chat/agent-origin.js';
import { showUndoToast } from './undo-toast.js';
import {
  renderToIframe,
  flushPendingBlobRevoke,
  buildPreviewSectionInnerHTML,
  getLastRenderedButtonSelector
} from '../renderer/iframe-renderer.js';
import { findPageForHref, isOwnSiteHref } from '../shared/preview-link-routing.js';
import {
  assembleOverridesCSS,
  assembleButtonCSS,
  assembleFormCSS
} from '../renderer/css-assembler.js';
import { hasButtonRootRules } from '../shared/template-support-css.js';
import { getButtonEffectJS } from '../renderer/animation-injector.js';
import { bridge } from '../editing/bridge.js';
import * as sectionManager from '../sections/section-manager.js';
import { MOBILE_MENU_LABELS } from '../sections/mobile-menu-merge.js';
import { getActiveViewportName } from './viewport-controls.js';
import {
  _initSectionClipboard,
  getSectionClipboard as _csGet,
  hasSectionClipboard as _csHas,
  clearSectionClipboard as _csClear,
  copySectionById as _csCopyById,
  pasteSectionAt as _csPasteAt
} from './section-clipboard.js';
// Re-export the clipboard API so existing importers of shell.js keep working.
export {
  getSectionClipboard,
  hasSectionClipboard,
  clearSectionClipboard,
  copySectionById,
  pasteSectionAt
} from './section-clipboard.js';
import * as pageManager from '../pages/page-manager.js';
// BC-8: every destructive section/page operation goes through the ONE policy
// source (act, then Undo). `destructive-policy-tests.js` fails on a direct call.
import * as sectionService from './command-bar/section-command-service.js';
import { initDragReorder } from '../sections/drag-reorder.js';
import {
  initTextEditor,
  applyFormatCommand,
  getLatestSelectionState,
  isBulkFormattingEnabled,
  getActiveEdit,
  getLastCompletedTextEditMeta,
  commitActiveTextEdit,
  cancelActiveTextEdit,
  handleLinkFormat
} from '../editing/text-editor.js';
import { initImageEditor, openSvgLibraryForInsert } from '../editing/image-editor.js';
// Namespace import: the command-bar image service takes the whole module as its
// injected implementation rather than a hand-listed subset, so adding an action
// there does not need an edit here too.
import * as imageEditorModule from '../editing/image-editor.js';
import { setTextEditorImpl } from './command-bar/text-command-service.js';
import { setImageEditorImpl } from './command-bar/image-command-service.js';
import { setLogoEditorImpl } from './command-bar/logo-command-service.js';
import { insertDecorativeSvg } from '../editing/insert-decorative-svg.js';
import { formatSectionName, getCategoryMap } from '../shared/section-display-name.js';
import { kindLabel } from '../shared/element-kind.js';
import { readActiveEditable } from '../editing/active-editable-store.js';
import { applyDecorativeMutation } from '../editing/decorative-mutations.js';
import { buildPageEdgeSlots } from '../editing/decorative-slot-queue.js';
import { initLogoEditor } from '../editing/logo-editor.js';
import * as logoEditorModule from '../editing/logo-editor.js';
import { initElementContextMenu } from './element-context-menu.js';
import { initDecorativeContextMenu } from './decorative-context-menu.js';
import { initSectionContextMenu } from './section-context-menu.js';
import { initDecorativeActionHandlers } from './decorative-action-handlers.js';
import {
  initElementActions,
  copyElementFromPayload,
  cutElementFromPayload,
  duplicateElementFromPayload,
  deleteElementFromPayload,
  pasteElementFromPayload,
  applyPaintFromPayload
} from '../editing/element-actions.js';
import { isPaintModeActive, exitPaintMode } from '../editing/format-painter.js';
import { isPickModeActive, exitPickMode } from '../editing/ai-context-picker.js';
import { initSelectionController, resetSelection, candidateOrder } from './selection-controller.js';
import {
  initActiveEditableController,
  resetActiveEditable
} from '../editing/active-editable-controller.js';
import { selection } from '../core/selection-store.js';
import {
  initElementSelection,
  bulkDeleteSelectedElements,
  bulkDuplicateSelectedElements,
  clearElementSelection,
  collapseElementSelection,
  isElementSelectionMulti
} from '../editing/element-selection.js';
import { initElementRestyle } from '../editing/element-restyle.js';
import { initElementAlignBar } from './element-align-bar.js';
import {
  initCommentController,
  setCommentNavigator,
  getUnseenOpenCount
} from './comment-controller.js';
import { initCommentPins } from './comment-pins.js';
import { initCommentSidebar, showCommentsPanel, stepCommentSelection } from './comment-sidebar.js';
import { FindReplaceWidget } from './find-replace-widget.js';
import { initFindReplaceActions } from './find-replace-actions.js';
import {
  register as registerAdapter,
  resolveAdapter,
  chainProbe,
  getReorderableHistoryLabels,
  getAdapterByKind
} from '../editing/pattern-adapters.js';
import { socialLinkAdapter, setSocialPlatforms } from '../editing/adapters/social-link-adapter.js';
import { freeformBlockAdapter } from '../editing/adapters/freeform-block-adapter.js';
import { navLinkAdapter } from '../editing/adapters/nav-link-adapter.js';
import { headerElementAdapter } from '../editing/adapters/header-element-adapter.js';
import { faqItemAdapter } from '../editing/adapters/faq-item-adapter.js';
import { footerLinkAdapter } from '../editing/adapters/footer-link-adapter.js';
import { footerColumnAdapter } from '../editing/adapters/footer-column-adapter.js';
import { buttonGroupItemAdapter } from '../editing/adapters/button-group-item-adapter.js';
import { cardItemAdapter } from '../editing/adapters/card-item-adapter.js';
import { galleryImageAdapter } from '../editing/adapters/gallery-image-adapter.js';
import { csItemCardAdapter } from '../editing/adapters/cs-item-card-adapter.js';
import { genericRepeatItemAdapter } from '../editing/adapters/generic-repeat-item-adapter.js';
import { initThemeEditor, mirrorThemeToParentRoot } from '../theme/theme-editor.js';
import { buildFontUpdatePayload, initFontSelector } from '../theme/font-selector.js';
import {
  quickSave,
  fullSave,
  cloudSave,
  getCloudSyncRecord,
  tryRestoreFromCloudIfFresher,
  everConfirmedInCloud,
  restoreCloudVersion,
  loadFromFile,
  checkAutoSave,
  restoreAutoSave,
  dismissAutoSave,
  applyLoadedProjectState,
  canonicalizeLoadedState,
  configureLoadedProjectGuard,
  isLoadSupersededError,
  registerThumbnailLoader
} from '../core/save-load.js';

// CE-5: the post-save thumbnail pipeline is wired here, at the ONE surface
// that owns cloud saves — the guest surfaces never register it, which is what
// keeps the exporter graph out of their static-graph gates.
registerThumbnailLoader(() => import('../preview/project-thumbnail.js'));
import { cloudSyncSkipReason } from '../core/cloud-sync-gate.js';
import { SectionList } from './section-list.js';
import { Toolbar } from './toolbar.js';
import { mountBootSkeleton, unmountBootSkeleton } from './boot-skeleton.js';
import { setSavePopoverSources } from './command-bar/save-pill.js';
import { CommandBar } from './command-bar/command-bar.js';
import {
  beginOperation,
  endOperation,
  isOperationTokenActive
} from './command-bar/command-operation-state.js';
import { PropertyPanel } from './property-panel.js';
import { NavSidebar } from './nav-sidebar.js';
import { ClientSeoPanel } from './client-seo-panel.js';
import { showCodeInjectionModal } from './code-injection-panel.js';
import { showLinkManager } from './link-manager.js';
import { RevisionStore } from '../core/revision-store.js';
import { showRevisionPanel } from './revision-panel.js';
import { showPageSelectionModal } from './page-selection-modal.js';
import { showAddPageChooserModal } from './add-page-chooser-modal.js';
import { showMyPreviewsModal, showSavePreviewModal } from './preview-modal.js';
import { showSaveTemplateModal } from './template-save-modal.js';
import { showAuthModal } from './auth-modal.js';
import { showAuthScreen, isAuthScreenForced, consumeAuthScreenForce } from './auth-screen.js';
import { showProjectSwitcherModal } from './project-history-modal.js';
// HR-0 — History Rail. The ONE import from ui/history-rail/; everything else in
// that directory is reachable only through it, which is what lets the whole
// initiative be removed by deleting the directory and this line.
import {
  openHistoryRail,
  restoreLeftRailAfterCanvas,
  yieldLeftRailForCanvas
} from './history-rail/index.js';
import { showInviteAcceptModal, showShareModal } from './share-modal.js';
import { maybeActivateCollabAssets } from '../collab/collab-assets.js';
import {
  configureCollabSession,
  maybeConnectCollab,
  disconnectCollab,
  collabConnected,
  collabSessionActive,
  collabIsSaveLeader,
  collabIsSynced,
  collabPeerCount,
  collabCaptureSaveCheckpoint,
  collabNotifySaved,
  collabFlushOutbox,
  collabLockTreatmentRows,
  collabSendPresence,
  collabSaveLeaderStalled,
  collabTakeOverEditor
} from '../collab/collab-session.js';
import { initCollabUI } from '../collab/collab-ui.js';
import {
  checkAuth,
  isRealUser,
  logoutAccount,
  markProjectPublished,
  getProject
} from '../templates/auth-api.js';
import { loadProfile, clearProfile } from '../core/profile-store.js';
import { setUnauthorizedHandler } from '../shared/authed-fetch.js';
import {
  clearListTemplatesCache,
  TemplateApiError,
  listTemplates
} from '../templates/template-api.js';
import { prefersReducedMotion, createDelayedLoader } from './feedback.js';
import { openCommandPalette, replaceActionsForType } from './command-palette.js';
import { loadJSON } from '../core/asset-loader.js';
import { initTooltips } from './tooltip.js';
import {
  initModalStacking,
  removeModalOverlay,
  trapFocus,
  isAnyModalOpen
} from './modal-primitives.js';
import { createConfirmDialog, createModal, createButton } from './components.js';
import { CanvasEmptyStage, bindEmptyStageRail } from './canvas-empty-stage.js';
import { isTextOnlyDiff } from '../editing/diff-predicate.js';
import { initPrePublishAuditActions, readProjectReadiness } from './pre-publish-audit-actions.js';
import { showShortcutsHelp } from './shortcuts-help.js';
import { isRectOccludedByRail, toViewportRect } from './rail-occlusion.js';

export { trapFocus, isAnyModalOpen, removeModalOverlay };

let sectionList = null;
let _toolbar = null;
let commandBar = null;
let _propertyPanel = null;
let agentPanel = null;
let previewIframe = null;
let _primaryPreviewIframe = null;
let _secondaryPreviewIframe = null;
let _doubleBufferFinalizeTimer = null;
let _doubleBufferSwapCancel = null;
let rendering = false;
// Phase 16A0.5: the section-selection scalar that lived here is gone. The
// selection store (core/selection-store.js) is the only selection state, and
// ui/selection-controller.js is its only writer. Read via selection.getPrimary().
_initSectionClipboard({
  // The seam survives for API compatibility, but the write it performed is
  // redundant: paste emits `section:landed` for the clone and selectAfterRender
  // selects from that. Writing here too would make shell a second
  // store-writer for no observable difference.
  setSelectedInstanceId: () => {},
  // PP-2: the ONE shell capability the clipboard needs, injected rather than
  // imported — the module no longer reaches into shell.js at all.
  renderPreview: (opts) => renderPreview(opts)
});
// PP-2: whatever creates a section also finds it and marks it. Paste and
// duplicate emit this after their render; the picker's add already calls
// selectAfterRender directly.
eventBus.on('section:landed', ({ instanceId }) => {
  if (instanceId) selectAfterRender(instanceId);
});
// Tracks the most recent iframe element-clicked payload so the keyboard
// shortcuts can claim Cmd/Ctrl+C/X/V for the element clipboard when an
// editable is selected. Cleared on canvas / section deselect.
let selectedElementPayload = null;
const revisionStore = new RevisionStore();
let _templateSelectorModulePromise = null;
let _skinWizardModulePromise = null;
let _agentPanelModulePromise = null;
let _flatExporterModulePromise = null;
let _eleventyExporterModulePromise = null;
let _exportSettingsModulePromise = null;
let _glassEditorModulesPromise = null;

function loadTemplateSelectorModule() {
  if (!_templateSelectorModulePromise) _templateSelectorModulePromise = import('./palette.js');
  return _templateSelectorModulePromise;
}

function loadSkinWizardModule() {
  if (!_skinWizardModulePromise) _skinWizardModulePromise = import('./skin-wizard/index.js');
  return _skinWizardModulePromise;
}

function loadAgentPanelModule() {
  if (!_agentPanelModulePromise) {
    _agentPanelModulePromise = import('./agent-chat/agent-panel.js');
  }
  return _agentPanelModulePromise;
}

function loadFlatExporterModule() {
  if (!_flatExporterModulePromise) _flatExporterModulePromise = import('../export/exporter.js');
  return _flatExporterModulePromise;
}

function loadEleventyExporterModule() {
  if (!_eleventyExporterModulePromise) {
    _eleventyExporterModulePromise = import('../export/eleventy-exporter.js');
  }
  return _eleventyExporterModulePromise;
}

let _astroExporterModulePromise = null;
// Lazily imported behind a module promise, like the other two exporters, so the
// Astro generator graph never enters the boot import chain.
function loadAstroExporterModule() {
  if (!_astroExporterModulePromise) {
    _astroExporterModulePromise = import('../export/astro-exporter.js');
  }
  return _astroExporterModulePromise;
}

function loadExportSettingsModule() {
  if (!_exportSettingsModulePromise) {
    _exportSettingsModulePromise = import('./export-settings-modal.js');
  }
  return _exportSettingsModulePromise;
}

function loadGlassEditorModules() {
  if (!_glassEditorModulesPromise) {
    _glassEditorModulesPromise = Promise.all([
      import('../editing/glass-panels.js'),
      import('../editing/style-verify.js'),
      import('../editing/glass-panel-section.js'),
      import('../editing/glass-panel-text.js'),
      import('../editing/glass-panel-image.js'),
      import('../editing/glass-panel-logo.js'),
      import('../editing/glass-panel-button.js'),
      import('../editing/glass-panel-container.js'),
      import('../editing/glass-panel-svg.js'),
      import('../editing/glass-panel-decoration.js'),
      import('../editing/glass-panel-background.js'),
      import('../editing/glass-panel-inert-family.js'),
      import('../editing/glass-block-menu.js'),
      import('../editing/glass-overlay.js'),
      import('../editing/glass-toolbar.js'),
      import('../editing/glass-spacing.js'),
      import('../editing/glass-spacing-badges.js'),
      import('../editing/glass-grid-lines.js'),
      import('../editing/glass-resize-handles.js'),
      import('../editing/glass-logo-handles.js')
    ]);
  }
  return _glassEditorModulesPromise;
}

async function openTemplateSelectorLazy(options = {}) {
  try {
    const { openTemplateSelector } = await loadTemplateSelectorModule();
    await openTemplateSelector(options || {});
  } catch (err) {
    reportError('Open templates', err, {
      user: 'status',
      message: 'Could not open templates',
      detail: err?.message || 'template selector failed to load'
    });
  }
}

async function showSkinWizardLazy(options = {}) {
  try {
    const { showSkinWizard } = await loadSkinWizardModule();
    showSkinWizard(options || {});
  } catch (err) {
    reportError('Open Import brand from URL', err, {
      user: 'status',
      message: 'Could not open Import brand from URL',
      detail: err?.message || 'skin wizard failed to load'
    });
  }
}

async function ensureAgentPanel() {
  if (agentPanel) return agentPanel;
  const { AgentChatPanel } = await loadAgentPanelModule();
  if (agentPanel) return agentPanel;
  agentPanel = new AgentChatPanel({
    ensureWritable: ensureWritableTab,
    getCurrentUser,
    getCurrentSelection: () => ({
      instanceId: selection.getPrimary(),
      element: selectedElementPayload
    })
  });
  return agentPanel;
}

async function initGlassEditorSurface(iframeWrap) {
  if (!iframeWrap || !previewIframe || _glassOverlay) return;
  try {
    const [
      glassPanelsModule,
      styleVerifyModule,
      glassPanelSectionModule,
      glassPanelTextModule,
      glassPanelImageModule,
      glassPanelLogoModule,
      glassPanelButtonModule,
      glassPanelContainerModule,
      glassPanelSvgModule,
      glassPanelDecorationModule,
      glassPanelBackgroundModule,
      glassPanelInertFamilyModule,
      glassBlockMenuModule,
      glassOverlayModule,
      glassToolbarModule,
      glassSpacingModule,
      glassSpacingBadgesModule,
      glassGridLinesModule,
      glassResizeHandlesModule,
      glassLogoHandlesModule
    ] = await loadGlassEditorModules();
    if (_glassOverlay || !previewIframe) return;

    const { initGlassPanels, registerPanel } = glassPanelsModule;
    const { initStyleVerify } = styleVerifyModule;
    const { GlassPanelSection } = glassPanelSectionModule;
    const { GlassPanelText } = glassPanelTextModule;
    const { GlassPanelImage } = glassPanelImageModule;
    const { GlassPanelLogo } = glassPanelLogoModule;
    const { GlassPanelButton } = glassPanelButtonModule;
    const { GlassPanelContainer } = glassPanelContainerModule;
    const { GlassPanelSvg } = glassPanelSvgModule;
    const { GlassPanelDecoration } = glassPanelDecorationModule;
    const { GlassPanelBackground } = glassPanelBackgroundModule;
    const { GlassPanelEmbed, GlassPanelMedia, GlassPanelField } = glassPanelInertFamilyModule;
    const { GlassBlockMenu } = glassBlockMenuModule;
    const { GlassOverlay } = glassOverlayModule;
    const { GlassToolbar } = glassToolbarModule;
    const { GlassSpacing } = glassSpacingModule;
    const { GlassSpacingBadges } = glassSpacingBadgesModule;
    const { GlassGridLines } = glassGridLinesModule;
    const { GlassResizeHandles } = glassResizeHandlesModule;
    const { GlassLogoHandles } = glassLogoHandlesModule;

    const glassOverlay = new GlassOverlay(iframeWrap, previewIframe);
    _glassOverlay = glassOverlay;
    glassOverlay.init();
    const glassToolbar = new GlassToolbar(glassOverlay);
    glassToolbar.init();
    const glassSpacing = new GlassSpacing(glassOverlay);
    glassSpacing.init();
    const glassSpacingBadges = new GlassSpacingBadges(glassOverlay);
    glassSpacingBadges.init();
    const glassGridLines = new GlassGridLines(glassOverlay);
    glassGridLines.init();
    const glassResizeHandles = new GlassResizeHandles(glassOverlay);
    glassResizeHandles.init();
    glassResizeHandles.setGridLines(glassGridLines);
    const glassLogoHandles = new GlassLogoHandles(glassOverlay);
    glassLogoHandles.init();
    // Register floating panel types and init panel system
    registerPanel('section', GlassPanelSection);
    registerPanel('text', GlassPanelText);
    registerPanel('image', GlassPanelImage);
    registerPanel('logo', GlassPanelLogo);
    registerPanel('button', GlassPanelButton);
    registerPanel('container', GlassPanelContainer);
    registerPanel('svg', GlassPanelSvg);
    registerPanel('decoration', GlassPanelDecoration);
    registerPanel('background', GlassPanelBackground);
    // Tier 4 Phase 5: formerly-inert families get minimal focused editors.
    registerPanel('embed', GlassPanelEmbed);
    registerPanel('media', GlassPanelMedia);
    registerPanel('field', GlassPanelField);
    initGlassPanels(glassOverlay);
    // Phase 6 apply-and-verify: after each targeted override push, read the
    // computed style back and escalate to !important when the template won.
    initStyleVerify();
    const glassBlockMenu = new GlassBlockMenu(glassOverlay, renderPreview);
    glassBlockMenu.init();
    // Request initial section positions now that iframe has rendered.
    bridge.send('query-section-positions', {});
  } catch (err) {
    reportError('Canvas editor initialization', err, {
      user: 'status',
      message: 'Could not initialize canvas editing controls',
      detail: err?.message || 'glass editor modules failed to load'
    });
  }
}

/**
 * Save an automatic revision snapshot on behalf of flows that live outside
 * shell.js (e.g. Import brand from URL saves "Before Import brand from URL" before replacing
 * the project). Mirrors the inline `revisionStore.save(..., 'auto')` calls
 * used before file load / export / collab join.
 */
export function saveAutoRevision(name, note = '') {
  return revisionStore.save(name, note, 'auto');
}

// Phase 6 UI: current authenticated user. Populated on boot via /api/auth/check
// and updated by the auth-modal on signup/login. Null while the boot check is
// in flight; an object once resolved (may be the dev/legacy admin payload).
let currentUser = null;
export function getCurrentUser() {
  return currentUser;
}
function setCurrentUser(payload) {
  const previousSub = isRealUser(currentUser) ? String(currentUser.sub) : null;
  const nextUser = payload || null;
  const nextSub = isRealUser(nextUser) ? String(nextUser.sub) : null;
  // Clear synchronously before auth:state listeners repaint persistent UI;
  // otherwise a mid-session A→B re-auth can flash A's profile on B's chip.
  if (previousSub !== nextSub) clearProfile();
  currentUser = nextUser;
  eventBus.emit('auth:state', currentUser);
  if (isRealUser(currentUser)) {
    startCloudSync();
    startSessionKeepalive();
    // A fresh real user after a paused session (re-auth) resumes sync.
    if (_cloudSyncPaused) void resumeCloudSync();
    // Fire-and-forget: display name / avatar for the chip + account surfaces.
    // Failure degrades to initials/email (profile-store's documented fallback).
    void loadProfile({ authPayload: currentUser });
    // Fleet outbox: flush any export manifests persisted while offline or in a
    // closed tab. Idempotent; a flush before sign-in would only 401-retry.
    initExportEventQueue({ principalSub: currentUser.sub });
    // P1b: partition the export-asset fetch cache by principal. A switch drops
    // the outgoing account's rows, so two people on one browser cannot observe
    // each other's asset reuse.
    void setAssetFetchCachePrincipal(currentUser.sub);
  } else {
    initExportEventQueue({ principalSub: null });
    // Sign-out drops the cache partition (see above).
    void setAssetFetchCachePrincipal(null);
    stopCloudSync();
    stopSessionKeepalive();
    clearProfile();
  }
  // Collab Phase 4: (de)activate the asset mirror for the new principal.
  // Async + self-gating (signed-in, cloud-synced, actually shared).
  void maybeActivateCollabAssets(currentUser);
  // Collab Phase 8: (de)activate the realtime session (dev-flag / shared gated).
  void maybeConnectCollab(currentUser);
}

/**
 * Collab Phase 8: whole-state replacements (Start over, template load, composer
 * apply, project switch) would blow away the shared document mid-session. While
 * peers are connected, block with a toast offering a Disconnect escape hatch.
 * Returns true when the action should be BLOCKED.
 */
function collabBlocksWholeStateReplace(actionLabel = 'This action') {
  if (collabPeerCount() === 0) return false;
  showToast(`${actionLabel} ends the shared session`, {
    type: 'warning',
    id: 'collab-whole-replace-blocked',
    duration: 8000,
    action: { label: 'Disconnect', onClick: () => void disconnectCollab({ flush: false }) }
  });
  return true;
}

function clearCurrentUserOnUnauthorized(err) {
  if (!(err instanceof TemplateApiError && err.status === 401)) return false;
  clearListTemplatesCache();
  setCurrentUser(null);
  return true;
}

// Phase 6 UI: cloud auto-sync loop. Every 30s, if the user is signed in, the
// app is online, and the state has changed since the last cloud save, POST a
// new version. Silent on success; toast on failure (rate-limited).
const CLOUD_SYNC_INTERVAL_MS = 30_000;
// Hourly keepalive: hit /api/auth/check so the Worker re-mints the sliding
// 7-day session. An active user never hits the expiry wall; a 401 (past the
// 30-day cap or a revoked session) fires the shared unauthorized handler,
// which pauses sync and prompts re-auth.
const SESSION_KEEPALIVE_INTERVAL_MS = 60 * 60_000;
const REQUIRED_PROJECT_NAME_MAX_LEN = 80;
let _cloudSyncTimer = 0;
let _lastCloudSyncedAt = 0;
let _cloudSyncErrorAt = 0;
let _keepaliveTimer = 0;
// Set when an authed call 401s. While paused, tickCloudSync no-ops (local
// autosave continues untouched); the chip + toast prompt re-auth. Cleared on a
// successful re-auth, which fires an immediate sync.
let _cloudSyncPaused = false;
let _retryCloudSaveAfterAuth = false;
// Persistence Phase E: set when a save 409s (the cloud copy advanced past our sync
// base on another device). While pending, tickCloudSync no-ops so it doesn't spam
// the conflict; cleared only by resolving the conflict (Keep mine / Load cloud).
let _cloudConflictPending = false;
// BC-2: the fresh project row from the last 409, so the save indicator's
// "Choose a version…" can open the same resolve dialog the conflict toast did.
let _cloudConflictProject = null;
// BC-2: facts the sync loop used to keep to itself. The save indicator could
// not tell "waiting for the next tick" from "in your account", and a failing
// tick was a rate-limited console.warn nobody saw.
let _cloudSyncInFlight = false;
let _cloudSyncLastError = '';
let _emittedCloudSyncState = null;
// BC-2: whether this browser's autosave slot is currently failing. A download
// is a backup, not a save — its toast must not imply the work is now safe.
let _localSaveFailing = false;
// Set for the duration of an AI agent turn. The agent captures an
// optimistic-concurrency base when it downloads the project and validates it
// only when it saves, minutes later — any version this tab mints in between
// guarantees a 409. Deliberately NOT _cloudSyncPaused, which is auth-scoped and
// toasts "session expired" on pause / force-saves on resume.
let _agentRunActive = false;

function projectNeedsName(state = store.getState()) {
  const name = String(state?.projectName || '').trim();
  return !name || name.toLowerCase() === 'untitled';
}

function startCloudSync() {
  if (_cloudSyncTimer) return;
  _cloudSyncTimer = setInterval(tickCloudSync, CLOUD_SYNC_INTERVAL_MS);
}
function stopCloudSync() {
  if (_cloudSyncTimer) {
    clearInterval(_cloudSyncTimer);
    _cloudSyncTimer = 0;
  }
}
function startSessionKeepalive() {
  if (_keepaliveTimer) return;
  _keepaliveTimer = setInterval(() => {
    // A 401 routes through the unauthorized handler (pauseCloudSync), so the
    // expected-flow outcome is already handled elsewhere. What reaches here is
    // a transport failure on a background tick the user did not trigger:
    // recorded, never surfaced, and never discarded unexamined.
    checkAuth().catch((err) => reportError('Session keepalive', err, { user: 'silent' }));
  }, SESSION_KEEPALIVE_INTERVAL_MS);
}
function stopSessionKeepalive() {
  if (_keepaliveTimer) {
    clearInterval(_keepaliveTimer);
    _keepaliveTimer = 0;
  }
}

/**
 * Fired by the shared authed-fetch unauthorized handler on ANY 401, and by the
 * manual cloud-save 401 catch. Suspends the auto-sync tick and surfaces a
 * re-auth prompt. Guarded on a real current user so boot-time "not signed in"
 * 401s (expected during ensureAuthenticated) don't false-trigger.
 */
function pauseCloudSync({ retryCloudSave = false } = {}) {
  if (retryCloudSave) _retryCloudSaveAfterAuth = true;
  if (_cloudSyncPaused && !retryCloudSave) return;
  if (!isRealUser(currentUser)) return;
  const wasPaused = _cloudSyncPaused;
  _cloudSyncPaused = true;
  if (!wasPaused) eventBus.emit('auth:sync-paused');
  showToast('Cloud sync paused — your session expired', {
    type: 'error',
    duration: 10000,
    id: 'cloud-sync-paused',
    action: {
      label: _retryCloudSaveAfterAuth ? 'Sign in & retry' : 'Sign in',
      onClick: () => eventBus.emit('toolbar:auth-show')
    }
  });
}

async function resumeCloudSync() {
  if (!_cloudSyncPaused) return;
  _cloudSyncPaused = false;
  eventBus.emit('auth:sync-resumed');
  const retryManualSave = _retryCloudSaveAfterAuth;
  _retryCloudSaveAfterAuth = false;
  if (retryManualSave) {
    eventBus.emit('toolbar:cloud-save');
    return;
  }
  // Push the user's pending edits immediately rather than waiting for the tick.
  await tickCloudSync();
}

/**
 * BC-2. A download is a BACKUP. It never says "Saved", and while this browser's
 * autosave is failing it says plainly that the project itself still is not.
 */
function showBackupDownloadedToast(filename) {
  if (_localSaveFailing) {
    showToast(
      `Downloaded ${filename} — a recovery copy. Your latest changes are still not saved on this device.`,
      {
        type: 'info',
        duration: 10000,
        action: { label: SAVE_LABELS.retry, onClick: () => eventBus.emit('toolbar:retry-save') }
      }
    );
    return;
  }
  showToast(`Downloaded ${filename}`, 'success');
}

/**
 * BC-2. The cloud axis of the save indicator, derived from the loop's own
 * facts and emitted ONLY on change. The auth pause and connectivity are not
 * here: the pill owns those two facts itself and ranks them.
 *
 * Order matters and matches builder-coherence contracts §2: a conflict is not
 * a failure (retrying it re-hits it), and a gone project is neither.
 */
function deriveCloudSyncState() {
  const state = store?.getState ? store.getState() : null;
  if (!isRealUser(currentUser) || !state) return { state: 'none' };
  if (store.hasBlocker?.(WRITE_BLOCKER.PROJECT_UNAVAILABLE)) return { state: 'unavailable' };
  if (_cloudConflictPending) return { state: 'conflict' };
  if (_cloudSyncInFlight) return { state: 'syncing' };
  if (_cloudSyncLastError) return { state: 'failed', detail: _cloudSyncLastError };
  // An unnamed project is never auto-synced (tickCloudSync skips it), so
  // "waiting to sync" would be a promise the loop does not keep.
  if (projectNeedsName(state)) return { state: 'none' };
  const updatedAt = Number(state.updatedAt || 0);
  const hasAccountCopy = _lastCloudSyncedAt > 0 || state.cloudVersion != null;
  if (updatedAt > _lastCloudSyncedAt) return { state: 'pending' };
  return { state: hasAccountCopy ? 'synced' : 'none' };
}

function refreshCloudSyncState() {
  const next = deriveCloudSyncState();
  const key = `${next.state}|${next.detail || ''}`;
  if (key === _emittedCloudSyncState) return;
  _emittedCloudSyncState = key;
  eventBus.emit('cloud-sync:state', next);
}

async function tickCloudSync() {
  const state = store?.getState ? store.getState() : null;
  const updatedAt = Number(state?.updatedAt || 0);
  if (
    cloudSyncSkipReason({
      paused: _cloudSyncPaused,
      conflictPending: _cloudConflictPending,
      agentRunActive: _agentRunActive,
      updatedAt,
      lastSyncedAt: _lastCloudSyncedAt
    })
  )
    return;
  if (!isRealUser(currentUser)) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  // Collab Phase 8/11: in a live session only the elected save leader auto-saves,
  // and only while fully converged — a save must describe room state, never one
  // still carrying unacked local ops that may yet be rejected. Phase 11: a
  // non-leader may self-promote once if the leader has stalled (no save activity
  // past the threshold), so a frozen leader can't wedge saves indefinitely.
  if (collabSessionActive()) {
    if (!collabConnected() || !collabIsSynced()) return;
    if (!collabIsSaveLeader() && !collabSaveLeaderStalled()) return;
  }
  if (projectNeedsName(state)) return;
  _cloudSyncInFlight = true;
  refreshCloudSyncState();
  try {
    const result = await cloudSave({ captureSnapshotContext: collabCaptureSaveCheckpoint });
    // Stamp from what the save actually uploaded, not the value read before the
    // single-flight chain ran — a queued save snapshots state later than this tick
    // did, and that content is synced too.
    _lastCloudSyncedAt = Number(result?.syncedUpdatedAt) || updatedAt;
    _cloudSyncLastError = '';
    collabNotifySaved(Number(result?.version?.version), result?.snapshotContext);
  } catch (err) {
    clearCurrentUserOnUnauthorized(err);
    if (err instanceof TemplateApiError) {
      // SB-1. Classify BEFORE the 409 branch. A 409 carrying `project: null`
      // means the server had no live row to describe — the project is gone, not
      // contended — and routing it into the conflict dialog produced an
      // unresolvable loop: "Load cloud version" was inert (latest was NaN) and
      // "Keep mine" re-fired the identical save into the identical 409 forever.
      const { lifecycle, signal } = classifyProjectSignal({
        operation: LIFECYCLE_OPERATION.SAVE,
        status: err.status ?? null,
        payload: err.data ?? null,
        everConfirmedInCloud: everConfirmedInCloud(store.getState())
      });
      if (lifecycle === LIFECYCLE.UNAVAILABLE) {
        await enterProjectUnavailable(signal);
        return;
      }
      if (err.status === 409) {
        // A genuine concurrent-edit conflict: the cloud advanced on another
        // device and there IS a project row to reconcile against. Pause
        // auto-sync and offer one Resolve action rather than re-hitting the
        // conflict every tick.
        _cloudConflictPending = true;
        _cloudConflictProject = err.data?.project || null;
        showToast('This project changed on another device', {
          type: 'error',
          duration: 12000,
          action: {
            label: 'Resolve',
            onClick: () => void resolveCloudConflict(err.data?.project || null)
          }
        });
        return;
      }
    }
    const detail =
      err instanceof TemplateApiError ? err.message : err?.message || 'cloud sync failed';
    // BC-2: the failure reaches the save indicator every time (as a state, not
    // a toast — a background tick the user did not trigger must not interrupt
    // them), and the console only once every 5 minutes.
    if (!(err instanceof TemplateApiError && err.status === 401)) _cloudSyncLastError = detail;
    const now = Date.now();
    if (now - _cloudSyncErrorAt > 5 * 60_000) {
      _cloudSyncErrorAt = now;
      console.warn('[Shell] cloud sync:', detail);
    }
  } finally {
    _cloudSyncInFlight = false;
    refreshCloudSyncState();
  }
}

/**
 * Persistence Phase E: resolve a cloud version conflict (a 409 from a save whose
 * baseVersion no longer matches the server's latest — another device saved). Three
 * choices: Keep mine (adopt the cloud latest as the base and force a re-save so our
 * content becomes the next version), Load cloud version (discard local, load the
 * cloud latest), or Cancel (leave local; auto-sync stays paused until resolved).
 * @param {object|null} project the server's fresh public project row from the 409
 */
async function resolveCloudConflict(project) {
  _cloudConflictPending = true;
  if (project) _cloudConflictProject = project;
  refreshCloudSyncState();
  const latest = Number(project?.latestVersion);
  const projectId = project?.id || store.getState()?.projectId;
  // SB-1 (I3): every control this dialog renders must be able to change the
  // outcome. Without a usable `latest` there is no version to load and no base
  // to adopt, so "Load cloud version" was inert and "Keep mine" re-fired the
  // save that produced this dialog — the user could never leave. Routing now
  // sends the gone-project case to the unavailable surface instead, and this
  // guard is the independent backstop: no resolvable version, no resolve
  // controls. Hardened separately from the routing on purpose, because a dialog
  // that can trap the user is a defect regardless of who called it.
  const resolvable = Number.isInteger(latest) && !!projectId;
  let defaultBtn = null;
  const { body, footer, close } = createModal({
    title: 'This project changed on another device',
    overlayClassName: 'pb-confirm-overlay',
    withFooter: true,
    maxWidth: 480,
    initialFocus: () => defaultBtn
  });

  const msg = document.createElement('p');
  msg.className = 'pb-confirm-msg';
  msg.textContent = resolvable
    ? `The cloud copy is now v${latest}. Keep your local changes (saved as the next version) or load the cloud version?`
    : 'The cloud copy changed, but the server did not report a version to compare against. Your local changes are safe here; try saving again in a moment.';
  body.appendChild(msg);

  footer.classList.add('pb-confirm-footer');
  const cancelBtn = createButton({
    variant: resolvable ? 'ghost' : 'primary',
    label: resolvable ? 'Cancel' : 'OK',
    onClick: () => close()
  });
  if (!resolvable) {
    footer.append(cancelBtn);
    defaultBtn = cancelBtn;
    return;
  }
  const loadBtn = createButton({
    variant: 'ghost',
    label: 'Load cloud version',
    onClick: () => {
      close();
      _cloudConflictPending = false;
      _cloudConflictProject = null;
      refreshCloudSyncState();
      void loadPendingCloudVersion(projectId, latest);
    }
  });
  const keepBtn = createButton({
    variant: 'primary',
    label: 'Keep mine',
    onClick: () => {
      close();
      _cloudConflictPending = false;
      _cloudConflictProject = null;
      refreshCloudSyncState();
      // Adopt the cloud latest as our base so the forced re-save lands as latest+1.
      const live = store.getState();
      if (live) live.cloudVersion = latest;
      eventBus.emit('toolbar:cloud-save');
    }
  });
  footer.append(cancelBtn, loadBtn, keepBtn);
  defaultBtn = keepBtn;
}
let isEditing = false;
let isPreviewMode = false;
// MM-3: viewport to restore when a mobile-menu preview session ends.
let _mobileMenuRestoreViewport = null;
let _glassOverlay = null;
let _sidebarWasOpenBeforePreview = null;
let _editStatusLiveRegion = null;
// Toast durations + visible cap now live in the adopted engine (./toast.js).
// Phase 5: the text-edit chip uses the constant id `undo-text-edit` via showUndoToast,
// and the history-changed handler sweeps the whole `undo-` prefix (D9), so the old
// per-session TEXT_EDIT_UNDO_TOAST_PREFIX is gone.
const IFRAME_ERROR_TOAST_PREFIX = 'renderer-iframe-error-';
const IFRAME_ERROR_DEDUPE_WINDOW_MS = 5000;
const IFRAME_ERROR_TOAST_CAP = 3;
const REVEAL_OVERLAY_FADE_MS = 120;
const DOUBLE_BUFFER_FADE_MS = 180;
const LEFT_PANEL_COLLAPSE_KEY = 'pb:left-panel-collapsed';
const SELECT_AFTER_RENDER_FALLBACK_MS = ANIM_MS * 8;

function isIframeInteractionDisabled() {
  return isPreviewMode || !store.isWritable();
}

function syncIframeInteractionMode() {
  const disabled = isIframeInteractionDisabled();
  // Preview mode disables the editing stylesheet, so a crosshair with no hover
  // outlines would be a mode the user cannot see. Leave it rather than strand it.
  if (disabled && isPickModeActive()) exitPickMode();
  if (_glassOverlay) {
    const overlayEl = _glassOverlay.getOverlayElement();
    if (overlayEl) overlayEl.classList.toggle('pb-preview-mode', disabled);
  }
  if (!bridge.nonce) return;
  // EA-3. `enabled` has always meant "iframe interaction is off", which is true
  // for BOTH the real Preview toggle and a read-only tab (no write access, an
  // agent review, a history preview). Entrances must arm for the first and not
  // the second, so the message carries the two facts SEPARATELY rather than
  // overloading one flag — a read-only tab is still the editing canvas, and the
  // locked decision is that the canvas stays static.
  //
  // `enabled` is kept for wire compatibility with an older iframe document.
  bridge.send('set-preview-mode', {
    enabled: disabled,
    interactionDisabled: disabled,
    visualPreview: isPreviewMode,
    // The parent owns project state; the iframe must not have to reason about
    // it. `!== false` mirrors the normalizer's fail-open default.
    templateEntrances: store.getState()?.entrance?.template !== false
  });
}
const LOADER_PHASE_LABELS = Object.freeze({
  assembling: 'Assembling preview…',
  loading: 'Loading preview…',
  restoringScroll: 'Restoring position…',
  default: 'Rendering preview…'
});
let _iframeErrorToastCounter = 0;
const _iframeErrorNonces = new Set();
let _revealOverlay = null;
let _activeRenderVisualToken = 0;
let _loaderPhase = null;
let _loaderPhaseProgress = null;
let _pendingPageHydrationLoads = 0;
let _appEntrancePlayed = false;
let _bootShellRevealed = false;

class ErrorToastCoalescer {
  constructor({
    windowMs = IFRAME_ERROR_DEDUPE_WINDOW_MS,
    maxVisible = IFRAME_ERROR_TOAST_CAP
  } = {}) {
    this.windowMs = windowMs;
    this.maxVisible = maxVisible;
    this.entries = new Map();
    this.visibleToastIds = [];
  }

  _pruneVisible() {
    this.visibleToastIds = this.visibleToastIds.filter((id) => {
      const selector = `.pb-toast[data-toast-id="${CSS.escape(id)}"]`;
      const node = document.querySelector(selector);
      return !!(node && !node.classList.contains('pb-toast-exit'));
    });
  }

  _composeLabel(message, count) {
    return count > 1 ? `Preview error: ${message} (×${count})` : `Preview error: ${message}`;
  }

  _startWindow(key, entry) {
    if (entry.windowTimer) clearTimeout(entry.windowTimer);
    entry.windowTimer = setTimeout(() => {
      const current = this.entries.get(key);
      if (current !== entry) return;
      this.entries.delete(key);
    }, this.windowMs);
  }

  _removeByToastId(toastId) {
    for (const [key, entry] of this.entries.entries()) {
      if (entry.toastId !== toastId) continue;
      if (entry.windowTimer) clearTimeout(entry.windowTimer);
      this.entries.delete(key);
      break;
    }
    this.visibleToastIds = this.visibleToastIds.filter((id) => id !== toastId);
  }

  _show(entry, message) {
    // The engine's stable-id replace path updates the text and resets the timer,
    // so a repeat error re-renders in place without any manual DOM mutation.
    showToast(this._composeLabel(message, entry.count), {
      type: 'error',
      id: entry.toastId,
      duration: this.windowMs,
      action: {
        label: 'Dismiss',
        onClick: () => {
          this._removeByToastId(entry.toastId);
        }
      }
    });
  }

  record({ message, source, line }) {
    const normalizedMessage =
      String(message || 'Unknown preview error').trim() || 'Unknown preview error';
    const normalizedSource = String(source || '').trim();
    const normalizedLine = Number.isFinite(Number(line)) ? Number(line) : 0;
    const key = `${normalizedMessage}|${normalizedSource}|${normalizedLine}`;
    const now = Date.now();
    const existing = this.entries.get(key);
    if (existing && now - existing.lastSeen <= this.windowMs) {
      existing.count += 1;
      existing.lastSeen = now;
      this._startWindow(key, existing);
      this._show(existing, normalizedMessage);
      return;
    }

    if (existing) {
      if (existing.windowTimer) clearTimeout(existing.windowTimer);
      this.entries.delete(key);
    }

    this._pruneVisible();
    if (this.visibleToastIds.length >= this.maxVisible) {
      const oldestToastId = this.visibleToastIds.shift();
      if (oldestToastId) {
        dismissToastById(oldestToastId);
        this._removeByToastId(oldestToastId);
      }
    }

    const entry = {
      toastId: `${IFRAME_ERROR_TOAST_PREFIX}${++_iframeErrorToastCounter}`,
      count: 1,
      lastSeen: now,
      windowTimer: null
    };
    this.entries.set(key, entry);
    this.visibleToastIds.push(entry.toastId);
    this._startWindow(key, entry);
    this._show(entry, normalizedMessage);
  }

  reset() {
    for (const entry of this.entries.values()) {
      if (entry.windowTimer) clearTimeout(entry.windowTimer);
    }
    this.entries.clear();
  }
}

const _iframeErrorCoalescer = new ErrorToastCoalescer();

/**
 * IR-4 — the PARENT window's unhandled failures get their own toast, carrying a
 * Report action.
 *
 * Deliberately not folded into `ErrorToastCoalescer` above. A toast has exactly
 * one action slot (`renderToastAction` renders a single `actionLabel` button),
 * and the iframe coalescer already spends its on Dismiss. Sharing the class
 * would mean choosing between the preview's Dismiss and this one's Report, and
 * these are different failures reported by different people for different
 * reasons.
 *
 * Until IR-1 there was no capture path for these at all: no `window.onerror`,
 * no `unhandledrejection` in any production module. So this is the first time a
 * crash in the builder itself is something a user can even see, let alone send.
 */
const PARENT_ERROR_TOAST_PREFIX = 'pb-parent-error-';
const PARENT_ERROR_WINDOW_MS = 8000;

/**
 * The browser's ResizeObserver loop notification is not a fault (see BENIGN_IFRAME_ERROR_RE
 * below, which withholds it on the iframe path for the same reason). The builder document's
 * own grids raise it too — the template selector on every open, the image picker on a phone —
 * and through this path it reached users as a red error toast with Report (design system
 * SYS-6, finding 2). The browser has already logged it; only the toast is withheld. Narrower
 * than the iframe filter on purpose: a parent "Script error." still toasts.
 */
const BENIGN_PARENT_ERROR_RE = /^ResizeObserver loop\b/i;
const _parentErrorEntries = new Map();
let _parentErrorCounter = 0;
let _parentErrorInstalled = false;

export function installUnhandledErrorToasts({ win = window, show = showToast } = {}) {
  if (_parentErrorInstalled || typeof win?.addEventListener !== 'function') return () => {};
  _parentErrorInstalled = true;

  const ctx = () => ({ origin: win.location?.origin || '', href: win.location?.href || '' });

  const record = (kind, event) => {
    const payload = normalizeErrorPayload(kind, event, ctx());
    if (BENIGN_PARENT_ERROR_RE.test(String(payload.message || '').trim())) return;
    const message = payload.message || 'Something went wrong';
    // Source and line are part of the key, so two different faults with the
    // same generic message ("undefined is not a function") stay two toasts.
    const key = `${message}|${payload.source}|${payload.line}`;
    const now = Date.now();
    const existing = _parentErrorEntries.get(key);
    const entry =
      existing && now - existing.lastSeen <= PARENT_ERROR_WINDOW_MS
        ? existing
        : { toastId: `${PARENT_ERROR_TOAST_PREFIX}${++_parentErrorCounter}`, count: 0 };
    entry.count += 1;
    entry.lastSeen = now;
    _parentErrorEntries.set(key, entry);

    show(entry.count > 1 ? `${message} (×${entry.count})` : message, {
      type: 'error',
      id: entry.toastId,
      duration: PARENT_ERROR_WINDOW_MS,
      action: {
        label: 'Report',
        onClick: () => {
          dismissToastById(entry.toastId);
          eventBus.emit('toolbar:report-issue', {
            cause: {
              scope: payload.type === 'unhandledrejection' ? 'Unhandled rejection' : 'Crash',
              message,
              detail: payload.source ? `${payload.source}:${payload.line}` : '',
              stack: payload.stack
            }
          });
        }
      }
    });
  };

  const onError = (event) => record('error', event);
  const onRejection = (event) => record('unhandledrejection', event);
  win.addEventListener('error', onError);
  win.addEventListener('unhandledrejection', onRejection);
  return () => {
    win.removeEventListener('error', onError);
    win.removeEventListener('unhandledrejection', onRejection);
    _parentErrorEntries.clear();
    _parentErrorInstalled = false;
  };
}

function _refreshPreviewAfterLayoutShift() {
  if (!previewIframe) return;

  // Nudge compositing to avoid rare stale iframe paints during sidebar
  // open/close transforms on some engines.
  previewIframe.style.willChange = 'transform';
  previewIframe.style.transform = 'translateZ(0)';
  void previewIframe.offsetHeight;
  previewIframe.style.transform = '';
  previewIframe.style.willChange = '';

  if (bridge.nonce) {
    bridge.send('query-section-positions', {});
    bridge.send('query-element-rect', {});
  }
}

function _schedulePreviewLayoutRefresh() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      _refreshPreviewAfterLayoutShift();
    });
  });
}

function _readLeftPanelCollapsed() {
  try {
    return localStorage.getItem(LEFT_PANEL_COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function _writeLeftPanelCollapsed(collapsed) {
  try {
    if (collapsed) localStorage.setItem(LEFT_PANEL_COLLAPSE_KEY, '1');
    else localStorage.removeItem(LEFT_PANEL_COLLAPSE_KEY);
  } catch {
    // Ignore storage errors (private mode, quota, etc.).
  }
}

/**
 * @returns {string|null} The currently selected section instanceId.
 * Phase 16A0: now the selection store's primary. At size 1 — the only size the
 * app can currently produce — this is identical to the scalar it replaced; once
 * multi-select lands it is the lead, which is what every single-target consumer
 * (property panel, glass overlay, command palette, collab presence) wants.
 */
export function getSelectedInstanceId() {
  return selection.getPrimary();
}
export function __setPreviewIframeForTests(iframe) {
  previewIframe = iframe;
  _primaryPreviewIframe = iframe;
}
export function __getPendingRenderWhileEditingForTests() {
  const queued = _getHighestQueuedRenderEntry();
  return queued ? queued.opts : null;
}
export function __getRenderQueueDepthForTests() {
  return _renderQueue.length;
}
/**
 * HR-2 — the neutral detached-preview seam.
 *
 * Thin wrappers, deliberately: the machine underneath is AR-5's and stays named
 * for it, so the agent census, the frame-vocabulary gate and
 * `AGENT_REVIEW_BASELINE_SHA` do not move. Any future surface that must show the
 * canvas a state that is not the store's goes through HERE — it holds a write
 * blocker, it is token-guarded, and there must never be a second one.
 *
 * @param {{detachedState: object, assetSource: {get(id: string): Promise<any>},
 *          pageId?: string, previewToken: string, owner?: string}} payload
 */
export function requestDetachedPreview(payload = {}) {
  return requestAgentReviewRender({
    ...payload,
    owner: payload.owner || 'history',
    side: 'before',
    reviewToken: payload.previewToken || payload.reviewToken
  });
}

/** Restore the live canvas. Safe to call when nothing is being previewed. */
export function exitDetachedPreview(reason = 'close', options = {}) {
  return exitAgentReview(reason, options);
}

/** Which owner, if any, currently holds the canvas. */
export function detachedPreviewOwner() {
  return _agentReviewSession?.owner || null;
}

export function __requestAgentReviewRenderForTests(payload) {
  return requestAgentReviewRender(payload);
}
export function __exitAgentReviewForTests(reason = 'test', options = {}) {
  return exitAgentReview(reason, options);
}
export function __getAgentReviewStateForTests() {
  return _agentReviewSession ? { ..._agentReviewSession } : null;
}
export function __setAgentReviewHooksForTests(hooks = null) {
  _agentReviewTestHooks = hooks;
}
export function __wireAgentReviewEventsForTests() {
  wireAgentReviewEvents();
}
export function __handleIframeErrorForTests(payload) {
  const error = normalizeIframeErrorPayload(payload);
  // A benign notification must not mark the render as failed either: the nonce
  // set is what `hadIframeError` reads to decide a render went wrong, so
  // recording one here would keep the symptom after removing the toast.
  if (isBenignIframeError(error)) return;
  const errorNonce = error.nonce || bridge.nonce || '';
  if (errorNonce) _iframeErrorNonces.add(errorNonce);
  _iframeErrorCoalescer.record(error);
}
export function __routeIframeErrorEventForTests(payload) {
  const error = normalizeIframeErrorPayload(payload);
  // Logged BEFORE the benign filter: the console keeps everything, so a real
  // diagnosis is never hidden by the toast policy.
  logIframeErrorToConsole(error);
  __handleIframeErrorForTests(error);
}
export function __getVisibleIframeErrorToastCountForTests() {
  return document.querySelectorAll(
    `.pb-toast[data-toast-id^="${IFRAME_ERROR_TOAST_PREFIX}"]:not(.pb-toast-exit)`
  ).length;
}
export function __getIframeErrorEntryCountForTests() {
  return _iframeErrorCoalescer.entries.size;
}
export function __tryApplySurgicalSyncForTests(args) {
  return tryApplySurgicalSync(args || {});
}
export function __handleGlobalShortcutForTests(rawEvent, opts = {}) {
  return handleGlobalShortcut(rawEvent, opts);
}
export function __normalizeElementClipboardPayloadForTests(data) {
  return normalizeElementClipboardPayload(data);
}
export function __setSelectedElementPayloadForTests(data) {
  selectedElementPayload = normalizeElementClipboardPayload(data);
}
export function __setEditingForTests(value) {
  isEditing = !!value;
}
export function __queueOverridePushForTests() {
  queueOverridePush();
}
export function __flushOverridePushForTests() {
  flushOverridePush();
}
export function __getOverrideAssembleCountForTests() {
  return _overrideAssembleCount;
}
export function __resetOverrideAssembleCountForTests() {
  _overrideAssembleCount = 0;
}
export function __collectSessionAssetRefsForTests(listRevisions) {
  return collectSessionAssetRefs(listRevisions ? { listRevisions } : undefined);
}
export function __handlePreviewSyncSectionHTMLForTests(payload) {
  return handlePreviewSyncSectionHTML(payload || {});
}
export function __resetRenderStateForTests() {
  releaseAgentReview('test-reset');
  _agentReviewChromeRows = [];
  _agentReviewTestHooks = null;
  rendering = false;
  _renderQueue = [];
  if (_currentRenderAbortController) _currentRenderAbortController.abort();
  _currentRenderAbortController = null;
  _activeRenderRevision = -1;
  _currentRenderReviewToken = null;
  _overridePushQueued = false;
  _lastOverridesCSS = null;
  _lastButtonCSS = null;
  _lastFormCSS = null;
  _lastButtonEffectJS = null;
  _overrideAssembleCount = 0;
  _activeRenderVisualToken = 0;
  _loaderPhase = null;
  _loaderPhaseProgress = null;
  if (_doubleBufferFinalizeTimer) {
    clearTimeout(_doubleBufferFinalizeTimer);
    _doubleBufferFinalizeTimer = null;
  }
  _doubleBufferSwapCancel = null;
  _iframeErrorNonces.clear();
  _iframeErrorCoalescer.reset();
  dismissToastsByPrefix(IFRAME_ERROR_TOAST_PREFIX);
  showIframeLoading(false);
  document.querySelectorAll('.pb-reveal-overlay').forEach((el) => el.remove());
  _revealOverlay = null;
  if (_secondaryPreviewIframe && _secondaryPreviewIframe.isConnected) {
    const inactiveIframe =
      previewIframe === _secondaryPreviewIframe ? _primaryPreviewIframe : _secondaryPreviewIframe;
    if (inactiveIframe && inactiveIframe !== previewIframe) inactiveIframe.remove();
  }
  _secondaryPreviewIframe = null;
  _primaryPreviewIframe =
    (previewIframe?.isConnected ? previewIframe : null) || document.getElementById('pb-preview');
  const basePreview = _primaryPreviewIframe;
  if (basePreview) {
    basePreview.id = 'pb-preview';
    delete basePreview.dataset.pbRendered;
    markPreviewIframeAsActive(basePreview);
    previewIframe = basePreview;
  }
}

// ── Post-action auto-select helper ──

let _pendingSelectCancel = null;

/**
 * After the next iframe render completes, select and flash the given section.
 * Cancelable — each call cancels any pending previous one, preventing stale
 * select/flash from rapid back-to-back actions.
 */
function selectAfterRender(instanceId, { restoreElement = null } = {}) {
  if (_pendingSelectCancel) {
    _pendingSelectCancel();
    _pendingSelectCancel = null;
  }

  let cancelled = false;
  let fired = false;
  let timer = null;

  const cancel = () => {
    cancelled = true;
    clearTimeout(timer);
    offBridgeReady();
  };
  _pendingSelectCancel = cancel;

  const fire = (options = {}) => {
    const restoreSelection = options?.restoreSelection !== false;
    if (cancelled || fired) return;
    fired = true;
    _pendingSelectCancel = null;
    clearTimeout(timer);
    offBridgeReady();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        // The selection controller consumes this and writes the store; the
        // scalar write that used to precede it is gone (16A0.5).
        eventBus.emit('section:select', { instanceId });
        bridge.send('set-selected-section', { instanceId, flash: false });
        if (restoreSelection && restoreElement?.eid && restoreElement.instanceId === instanceId) {
          bridge.send('select-by-eid', {
            instanceId,
            eid: restoreElement.eid
          });
          bridge.send('scroll-to-eid', {
            instanceId,
            eid: restoreElement.eid
          });
        } else {
          bridge.send('scroll-to-section', { instanceId });
        }
        bridge.send('flash-section', { instanceId });
      });
    });
  };

  const offBridgeReady = eventBus.once('iframe:bridge-ready', fire);
  timer = setTimeout(() => {
    fire();
  }, SELECT_AFTER_RENDER_FALLBACK_MS);

  return { fire, cancel };
}

function previewSectionsById(state) {
  const sections = collectPreviewOverrideSections(state);
  return new Map(
    sections
      .filter((section) => section?.instanceId)
      .map((section) => [section.instanceId, section])
  );
}

function sectionFingerprint(section) {
  if (!section) return '';
  try {
    return JSON.stringify(section);
  } catch {
    return String(section.instanceId || '');
  }
}

/** Resolve visible section instances changed by a history transition. */
function deriveHistoryAffectedInstanceIds(patches, oldState, newState) {
  const before = previewSectionsById(oldState);
  const after = previewSectionsById(newState || store.getState());
  const ids = [];
  const add = (id) => {
    if (id && after.has(id) && !ids.includes(id)) ids.push(id);
  };

  for (const [id, section] of after) {
    const previous = before.get(id);
    if (!previous || sectionFingerprint(previous) !== sectionFingerprint(section)) add(id);
  }

  const removedVisibleSection = Array.from(before.keys()).some((id) => !after.has(id));

  const state = newState || store.getState();
  for (const patch of patches || []) {
    if (!patch || typeof patch.path !== 'string') continue;
    if (patch.item?.instanceId) add(patch.item.instanceId);
    if (patch.value?.instanceId) add(patch.value.instanceId);

    const sectionPath = patch.path.match(/^pages\.(\d+)\.sections(?:\.(\d+))?(?:\.|$)/);
    if (!sectionPath) continue;
    const pageIndex = Number.parseInt(sectionPath[1], 10);
    const explicitIndex = sectionPath[2] == null ? null : Number.parseInt(sectionPath[2], 10);
    const page = state?.pages?.[pageIndex];
    if (!page || page.id !== state.activePage || !Array.isArray(page.sections)) continue;
    const candidateIndex = explicitIndex ?? patch.to ?? patch.index ?? patch.from;
    if (!Number.isInteger(candidateIndex) || page.sections.length === 0) continue;
    const nearest = page.sections[Math.min(Math.max(candidateIndex, 0), page.sections.length - 1)];
    add(nearest?.instanceId);
  }

  if (ids.length === 0 && removedVisibleSection) add(after.keys().next().value);

  return ids;
}

export function __deriveHistoryAffectedInstanceIdsForTests(patches, oldState, newState) {
  return deriveHistoryAffectedInstanceIds(patches, oldState, newState);
}

// showToast / dismissToastById / dismissToastsByPrefix / showProgressToast now come
// from the adopted engine (./toast.js). Re-exported so the ~20 consumer files, the
// glass-panels lazy import, and the DI hub keep a single toast API surface at shell.js.
export { showToast, dismissToastById, dismissToastsByPrefix, showProgressToast };

/**
 * Guard load/replace entry points against a read-only (non-owner) tab.
 *
 * The tab-guard makes a secondary tab non-writable so it can't clobber the
 * shared autosave slot. store.init() itself stays ungated (tab promotion depends
 * on it), so the write barrier lives at the load *entry points*: any flow that
 * would swap this tab's in-memory project — file load, start over, Import brand from URL,
 * version restore — calls this first. Returns true when the tab may load; when
 * read-only it shows an info toast and returns false.
 * @returns {boolean}
 */

export function ensureWritableTab() {
  if (store.isWritable()) return true;
  const blocker = store.getPrimaryBlocker();
  showToast(readOnlyReasonFor(blocker), {
    type: 'info',
    duration: 6000,
    id: 'read-only-tab'
  });
  return false;
}

/**
 * Toast for a destructive action with an Undo button.
 *
 * Captures the history pointer immediately after the action committed; clicking
 * Undo only reverts when still at that exact position, so a later user action
 * doesn't get clobbered by a stale toast click.
 *
 * Phase 5: this is now a thin wrapper over the engine's transactional undo
 * (showTransactionalUndoToast) — the chip gains a visible countdown, an inert
 * body, exactly-once settlement, hover/focus/hidden pause and Escape-commits.
 * The store transaction has ALREADY committed by the time this is called, so
 * there is no onCommit; onUndo reverts (history-position-guarded) (D4). The
 * explicit/derived id keeps the `undo-` prefix (D9), and same-id replacement
 * (divergence 6) means a repeated drag simply supersedes its own chip.
 *
 * Call AFTER the destructive transaction has committed.
 *
 * @param {string} message - human-readable label, e.g., "Section removed"
 * @param {object} [opts]
 * @param {number} [opts.duration=5000] - undo window in ms (converted to seconds)
 * @param {string} [opts.id] - dedupe id; commits+replaces an existing undo with the same id
 */
// PP-2: moved verbatim to ui/undo-toast.js so shell-free modules can import
// it; re-exported here so every existing call site is untouched.
export { showUndoToast };

/**
 * App-native confirmation modal for destructive actions.
 * Resolves true when user confirms, false when cancelled/dismissed.
 *
 * Thin wrapper over the component kit's `createConfirmDialog` (Polish Phase 7):
 * one confirm implementation, kept here as a stable export for existing callers.
 * Markup (`.pb-confirm-*`) and behavior are unchanged.
 */
export function showConfirmModal(opts = {}) {
  return createConfirmDialog(opts);
}

/**
 * Require a real project name before user-triggered save/export flows.
 * Returns true when the project is named, false when the modal is cancelled.
 */
async function ensureProjectNamedBeforeAction() {
  const state = store.getState();
  if (!projectNeedsName(state)) return true;
  const name = await showProjectNameRequiredModal();
  if (!name) return false;
  store.update('projectName', name, 'Rename project');
  showToast(`Project renamed to "${name}"`, 'success');
  return true;
}

function showProjectNameRequiredModal() {
  const existing = document.querySelector('.pb-project-name-required-overlay');
  if (existing) {
    existing.querySelector('input')?.focus();
    return Promise.resolve(null);
  }

  // The value the promise will settle with. Every dismissal route the kit owns
  // \u2014 backdrop, Escape, the close button, and on a phone a downward fling \u2014
  // converges on close(), so onClose is the single settlement point and the
  // old `done` latch is now the kit's `closed` guard.
  let outcome = null;
  let settle;
  const result = new Promise((resolve) => {
    settle = resolve;
  });
  let input = null;

  // The overlay class stays: the re-entrancy guard above reads it back, and
  // `.pb-project-name-required-overlay` carries CSS. What changes is who builds
  // the shell \u2014 the kit, which brings the phone sheet with it.
  const { body, footer, close } = createModal({
    title: 'Name This Project',
    maxWidth: 440,
    overlayClassName: 'pb-confirm-overlay pb-project-name-required-overlay',
    initialFocus: () => input,
    onClose: () => settle(outcome)
  });
  footer.classList.add('pb-confirm-footer');

  const finish = (value) => {
    outcome = value;
    close();
  };

  const form = document.createElement('form');
  form.className = 'pb-project-name-required-form';
  form.innerHTML = `
    <p class="pb-confirm-msg">Give this project a name before saving or exporting it.</p>
    <label class="pb-project-name-required-field">
      <span>Project name</span>
      <input class="pb-input" name="projectName" maxlength="${REQUIRED_PROJECT_NAME_MAX_LEN}" placeholder="My Website" autocomplete="off">
      <span class="pb-confirm-detail" data-field-error="projectName" aria-live="polite"></span>
    </label>
  `;
  body.appendChild(form);

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'pb-btn pb-btn-ghost';
  cancelBtn.textContent = 'Cancel';
  const saveBtn = document.createElement('button');
  saveBtn.type = 'submit';
  saveBtn.className = 'pb-btn pb-btn-primary';
  saveBtn.textContent = 'Save Name';
  footer.append(cancelBtn, saveBtn);

  input = form.elements.projectName;
  const errorEl = form.querySelector('[data-field-error="projectName"]');

  function setError(message) {
    errorEl.textContent = message;
    input.classList.add('pb-input-error');
    input.focus();
    input.select();
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    input.classList.remove('pb-input-error');
    errorEl.textContent = '';
    const value = String(input.value || '').trim();
    if (!value) {
      setError('Enter a project name.');
      return;
    }
    if (value.toLowerCase() === 'untitled') {
      setError('Choose a name other than Untitled.');
      return;
    }
    finish(value);
  });

  input.addEventListener('input', () => {
    input.classList.remove('pb-input-error');
    errorEl.textContent = '';
  });
  saveBtn.addEventListener('click', () => {
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });
  cancelBtn.addEventListener('click', () => finish(null));

  return result;
}

/**
 * Swap choice modal — shown when a variant swap has both user edits and
 * structural risk. Returns { choice, remember }.
 */
export function showSwapChoiceModal({
  category: _category,
  sectionLabel,
  details,
  hasContentEdits = true,
  hasUnsafeOverrides = false
} = {}) {
  let outcome = 'cancel';
  let settle;
  const result = new Promise((resolve) => {
    settle = resolve;
  });

  const { body, footer, close } = createModal({
    title: 'Swap variant?',
    // PP-2: the target, under the title — "Replacing Split hero".
    ...(sectionLabel ? { context: `Replacing ${sectionLabel}` } : {}),
    maxWidth: 480,
    overlayClassName: 'pb-confirm-overlay',
    initialFocus: '.pb-confirm-footer .pb-btn-primary',
    // Cancel is the default outcome, so every dismissal route the kit owns
    // settles the promise the same way the old backdrop/Escape handlers did.
    onClose: () => settle({ choice: outcome, remember: checkbox.checked })
  });
  footer.classList.add('pb-confirm-footer');
  footer.style.gap = '8px';

  const finish = (choice) => {
    outcome = choice;
    close();
  };

  const msg = document.createElement('p');
  msg.className = 'pb-confirm-msg';
  msg.textContent = hasContentEdits
    ? 'Content edits will be mapped onto the new variant where its structure allows.'
    : 'The new variant has a different structure.';
  body.appendChild(msg);

  if (details) {
    const detailEl = document.createElement('p');
    detailEl.className = 'pb-confirm-detail';
    detailEl.textContent = details;
    body.appendChild(detailEl);
  }

  const rememberWrap = document.createElement('label');
  rememberWrap.className = 'pb-confirm-remember';
  rememberWrap.style.cssText =
    'display:flex;align-items:center;gap:8px;margin-top:12px;font-size:13px;color:var(--pb-text-dim);cursor:pointer';
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  rememberWrap.appendChild(checkbox);
  rememberWrap.appendChild(document.createTextNode('Remember my choice for this session'));
  if (!hasUnsafeOverrides) body.appendChild(rememberWrap);

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'pb-btn pb-btn-ghost';
  cancelBtn.textContent = 'Cancel';
  const defaultsBtn = document.createElement('button');
  defaultsBtn.className = 'pb-btn pb-btn-ghost';
  defaultsBtn.textContent = 'Use new defaults';
  const keepBtn = document.createElement('button');
  keepBtn.className = 'pb-btn pb-btn-primary';
  keepBtn.textContent = 'Keep content edits';
  footer.appendChild(cancelBtn);
  footer.appendChild(defaultsBtn);
  footer.appendChild(keepBtn);

  keepBtn.addEventListener('click', () => finish('keep-edits'));
  defaultsBtn.addEventListener('click', () => finish('use-defaults'));
  cancelBtn.addEventListener('click', () => finish('cancel'));

  return result;
}

function _formatEditedTagLabel(tagName) {
  const raw = String(tagName || '')
    .trim()
    .toUpperCase();
  if (!raw || raw === 'TEXT') return 'text';
  return raw;
}

function _showTextEditUndoChip(patches) {
  if (!Array.isArray(patches) || patches.length === 0) return;
  const meta = getLastCompletedTextEditMeta();
  const tagLabel = _formatEditedTagLabel(meta?.tagName);
  // Phase 5: route through the transactional wrapper (history-position guard +
  // exactly-once settlement). The constant id `undo-text-edit` means same-id
  // replacement (divergence 6) supersedes the previous chip — no manual prefix
  // pre-clear, and the previously-UNGUARDED store.undo() is now guarded.
  showUndoToast(`Edited ${tagLabel} · Undo`, { id: 'undo-text-edit', duration: 4000 });
}

// dismissToastById / dismissToastsByPrefix are imported from ./toast.js and
// re-exported above (the engine's prefix sweep also catches queued records).

// The export progress dialog used to be `_createExportModal` here \u2014 the best
// progress surface in the repo, reachable from exactly one file. It now lives in
// the kit as createProgressModal (components.js), promoted verbatim including the
// aborting-defaults-true contract, so Save Preview and template upload can stop
// hand-rolling their own phase surfaces. Both export call sites build it inline.

function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  if (el.contentEditable === 'true') return true;
  return false;
}

function normalizeElementClipboardPayload(data) {
  if (!data || typeof data !== 'object') return null;
  const adapter = data.adapterKind ? null : resolveAdapter(chainProbe(data.ancestorChain || []));
  return {
    ...data,
    adapterKind: data.adapterKind || adapter?.kind || null
  };
}

function isHelpShortcutKey(key, shiftKey) {
  return key === '?' || (key === '/' && shiftKey);
}

function getSelectedSectionContext() {
  const primary = selection.getPrimary();
  if (!primary) return null;

  const sharedType = sectionManager.getSharedType(primary);
  if (sharedType) return { sharedType };

  const state = store.getState();
  const page = store.getActivePage();
  if (!state || !page) return null;

  const pageIndex = state.pages.findIndex((p) => p.id === page.id);
  if (pageIndex === -1) return null;

  const sectionIndex = page.sections.findIndex((s) => s.instanceId === primary);
  if (sectionIndex === -1) return null;

  return {
    sharedType: null,
    state,
    page,
    pageIndex,
    sectionsPath: `pages.${pageIndex}.sections`,
    sectionIndex,
    section: page.sections[sectionIndex]
  };
}

function copySelectedSection() {
  const primary = selection.getPrimary();
  if (!primary) return false;
  return _csCopyById(primary);
}

async function pasteSectionAfterSelection() {
  const state = store.getState();
  const page = store.getActivePage();
  if (!state || !page) return false;

  const pageIndex = state.pages.findIndex((p) => p.id === page.id);
  if (pageIndex === -1) return false;

  let insertIndex = page.sections.length;
  const ctx = getSelectedSectionContext();
  if (ctx && !ctx.sharedType) {
    insertIndex = ctx.sectionIndex + 1;
  }

  return _csPasteAt(pageIndex, insertIndex);
}

async function moveSelectedSectionByShortcut(direction) {
  const ctx = getSelectedSectionContext();
  if (!ctx || ctx.sharedType) return false;
  // Captured once: moveSection awaits a preview render, and re-reading the
  // primary across that await could act on a section the user has since moved to.
  const primary = selection.getPrimary();

  if (direction === 'up') {
    if (ctx.sectionIndex <= 0) return true;
    await sectionManager.moveSection(primary, ctx.sectionIndex - 1);
    return true;
  }

  if (direction === 'down') {
    if (ctx.sectionIndex >= ctx.page.sections.length - 1) return true;
    await sectionManager.moveSection(primary, ctx.sectionIndex + 2);
    return true;
  }

  return false;
}

async function handleGlobalShortcut(rawEvent, { fromIframe = false } = {}) {
  if (!rawEvent) return false;

  const prevent = () => {
    if (typeof rawEvent.preventDefault === 'function') rawEvent.preventDefault();
  };

  const key = String(rawEvent.key || '').toLowerCase();
  const mod = !!(rawEvent.ctrlKey || rawEvent.metaKey);
  const shift = !!rawEvent.shiftKey;
  const composing = !!rawEvent.isComposing;

  if (composing) return false;

  // Local mirror of the selection store's primary, hoisted so the section-tier
  // guard expressions below stay textually stable — element-clipboard-tests.js
  // asserts element-before-section tier precedence by source index against
  // these exact strings. Also deliberately a snapshot: branches that await
  // (delete's confirm modal, move's render) must act on the section that was
  // selected when the key went down, not whatever is selected when they resume.
  const selectedInstanceId = selection.getPrimary();

  if (!mod && key === 'escape' && isPaintModeActive()) {
    prevent();
    exitPaintMode();
    return true;
  }

  if (!mod && key === 'escape' && isPickModeActive()) {
    prevent();
    exitPickMode();
    return true;
  }

  // Cmd/Ctrl+K edits the current link during text editing and opens the
  // command palette everywhere else.
  if (mod && key === 'k') {
    prevent();
    if (isEditing) {
      handleLinkFormat();
      return true;
    }
    openCommandPalette();
    return true;
  }

  // Cmd/Ctrl+J toggles the agent panel. Above the typing gate on purpose,
  // like Cmd+K: the panel is reachable while a field has focus.
  if (mod && key === 'j') {
    prevent();
    eventBus.emit('agent:toggle');
    return true;
  }

  // Shared shortcuts (work in parent and when forwarded from iframe focus).
  // Let native per-character undo/redo through when a parent-side field is focused
  // (SEO/alt/custom-CSS/head/body inputs) — otherwise preventDefault() + store.undo()
  // would steal the browser's text undo and (with an open field transaction) revert
  // canvas state instead of the field. The general typing gate below (~1323) sits
  // AFTER these branches, so the guard must be repeated here.
  if (mod && key === 'z' && !shift) {
    if (!fromIframe && isTypingTarget(document.activeElement)) return false;
    if (isEditing) {
      if (fromIframe) return true;
      prevent();
      bridge.send('text-history-shortcut', { command: 'undo' });
      return true;
    }
    prevent();
    store.undo();
    return true;
  }
  if (mod && ((key === 'z' && shift) || key === 'y')) {
    if (!fromIframe && isTypingTarget(document.activeElement)) return false;
    if (isEditing) {
      if (fromIframe) return true;
      prevent();
      bridge.send('text-history-shortcut', { command: 'redo' });
      return true;
    }
    prevent();
    store.redo();
    return true;
  }
  if (mod && key === 's') {
    prevent();
    const outputBtn = document.getElementById('pb-export-btn');
    if (outputBtn?.getAttribute('aria-busy') === 'true') return true;
    eventBus.emit('toolbar:quick-save');
    return true;
  }
  if (mod && key === 'f') {
    prevent();
    if (isEditing) await commitActiveTextEdit();
    else store.commitTransaction();
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    eventBus.emit('find-replace:open', { expandReplace: !!shift });
    return true;
  }
  if (mod && key === 'n') {
    prevent();
    eventBus.emit('toolbar:add-page');
    return true;
  }
  if (mod && (key === '=' || key === '+' || key === 'add')) {
    prevent();
    commandBar?.viewportControls?.zoomIn?.();
    return true;
  }
  if (mod && (key === '-' || key === 'subtract')) {
    prevent();
    commandBar?.viewportControls?.zoomOut?.();
    return true;
  }
  if (mod && key === '0') {
    prevent();
    commandBar?.viewportControls?.zoomReset?.();
    return true;
  }

  // Commands below should not fire while typing into parent-side fields or with an open modal.
  if (!fromIframe && isTypingTarget(document.activeElement)) return false;
  if (isEditing) return false;
  if (document.querySelector('.pb-modal-overlay')) return false;

  // `/` opens the command palette — single-keystroke discoverability for
  // search-first users. Sits below the gates above so it never hijacks text
  // input, edit mode, or a focused modal.
  if (!mod && !shift && !rawEvent.altKey && key === '/') {
    prevent();
    openCommandPalette();
    return true;
  }

  // J/K (and [ ]) walk the comment list while the panel is open. They are
  // deliberately CONDITIONAL: stepCommentSelection returns false when there is
  // no panel, and the key falls through rather than being silently eaten by a
  // surface the user cannot see.
  if (!mod && !shift && !rawEvent.altKey && (key === 'j' || key === ']')) {
    if (stepCommentSelection(1)) {
      prevent();
      return true;
    }
  }
  if (!mod && !shift && !rawEvent.altKey && (key === 'k' || key === '[')) {
    if (stepCommentSelection(-1)) {
      prevent();
      return true;
    }
  }

  // Element clipboard takes precedence over section clipboard when an
  // editable is selected in the iframe. Browser-native inputs were already
  // filtered out above by `isTypingTarget` / `isEditing` gates.
  //
  // 16C1: the clipboard tier stays SINGLE — the element clipboard holds one
  // subtree, exactly as the section clipboard holds one section — so each of
  // these first collapses a sibling multi-selection to its lead and then runs
  // unchanged. Cmd+D and Delete are the two that go bulk.
  if (mod && key === 'c' && selectedElementPayload) {
    prevent();
    collapseElementSelection('shortcut:copy');
    copyElementFromPayload(selectedElementPayload, selectedElementPayload.adapterKind || null);
    return true;
  }
  if (mod && key === 'x' && selectedElementPayload) {
    prevent();
    collapseElementSelection('shortcut:cut');
    cutElementFromPayload(selectedElementPayload, selectedElementPayload.adapterKind || null);
    return true;
  }
  if (mod && key === 'v' && selectedElementPayload) {
    prevent();
    collapseElementSelection('shortcut:paste');
    pasteElementFromPayload(selectedElementPayload, selectedElementPayload.adapterKind || null);
    return true;
  }
  if (mod && key === 'd' && selectedElementPayload) {
    prevent();
    if (isElementSelectionMulti() && bulkDuplicateSelectedElements()) return true;
    duplicateElementFromPayload(selectedElementPayload, selectedElementPayload.adapterKind || null);
    return true;
  }

  if ((key === 'delete' || key === 'backspace') && selectedElementPayload) {
    prevent();
    const deletingPayload = selectedElementPayload;
    selectedElementPayload = null;
    if (!(isElementSelectionMulti() && bulkDeleteSelectedElements())) {
      await deleteElementFromPayload(deletingPayload, deletingPayload.adapterKind || null);
    }
    bridge.send('clear-selected-editable', { reason: 'keyboard-delete' });
    eventBus.emit('iframe:selected-editable-cleared', { reason: 'keyboard-delete' });
    return true;
  }

  if (key === 'escape' && selectedElementPayload) {
    prevent();
    selectedElementPayload = null;
    clearElementSelection('keyboard-escape');
    bridge.send('clear-selected-editable', { reason: 'keyboard-escape' });
    eventBus.emit('iframe:selected-editable-cleared', { reason: 'keyboard-escape' });
    return true;
  }

  // Alt+Arrow reorders the selected reorderable element by one slot (same commit
  // the drag gesture produces). Only hijack the key when the selected element's
  // adapter is actually reorderable, so Alt+Left/Right stays browser-native
  // (back/forward) everywhere else. Left/Up = toward previous sibling.
  if (
    rawEvent.altKey &&
    !mod &&
    !shift &&
    selectedElementPayload &&
    (key === 'arrowleft' || key === 'arrowright' || key === 'arrowup' || key === 'arrowdown')
  ) {
    const adapter = getAdapterByKind(selectedElementPayload.adapterKind);
    if (adapter?.reorderable) {
      prevent();
      // Single-target: element reorder moves ONE repeat root by one slot, so a
      // sibling multi-selection collapses to the lead first (16C1).
      collapseElementSelection('shortcut:reorder');
      const direction = key === 'arrowleft' || key === 'arrowup' ? 'prev' : 'next';
      eventBus.emit(`element-menu:move-${direction}`, {
        payload: selectedElementPayload,
        adapterKind: selectedElementPayload.adapterKind || null
      });
      return true;
    }
  }

  // Cmd/Ctrl+A — select every page section (16A1). First branch of the
  // section tier, after the whole element tier and below the typing/modal
  // gates above, so inputs, text editing and open modals keep native
  // select-all. Not gated on a current selection. Falls through to native
  // when the page has no sections.
  if (mod && key === 'a' && !shift) {
    const pageSections = candidateOrder();
    if (!pageSections.length) return false;
    prevent();
    eventBus.emit('selection:request', { intent: 'select-all', source: 'cmd-a' });
    eventBus.emit('panel:focus-sections');
    return true;
  }

  // Bulk tier (16A2b): Delete/Backspace, Cmd+D and Cmd+Shift+↑/↓ act on the
  // WHOLE selection at size > 1 — one confirm, one compound history entry,
  // one undo. Captured before any await, like the single-target snapshot.
  if (selection.isMulti() && selectedInstanceId) {
    const bulkIds = selection.getIds();
    if (mod && shift && (key === 'arrowup' || key === 'arrowdown')) {
      prevent();
      await sectionManager.bulkMoveSections(bulkIds, {
        direction: key === 'arrowup' ? 'up' : 'down'
      });
      return true;
    }
    if ((key === 'delete' || key === 'backspace') && !mod) {
      prevent();
      const removed = await sectionService.bulkRemove({ instanceIds: bulkIds, announce: true });
      if (removed) eventBus.emit('section-deselected');
      return true;
    }
    if (mod && key === 'd') {
      prevent();
      await sectionManager.bulkDuplicateSections(bulkIds);
      return true;
    }
  }

  // Single-target rule (16A1): the remaining section-tier branches below act
  // on ONE section — the lead. Reaching one while multi-selected first
  // collapses the selection to that lead, then runs unchanged, so the UI
  // never suggests an action covered five sections when it touched one.
  // Escape is exempt: it clears the whole set. (Delete/Cmd+D/Cmd+Shift+Arrow
  // left this list in 16A2b — they are bulk now, handled above.)
  if (selection.isMulti() && selectedInstanceId && mod && (key === 'c' || key === 'v')) {
    eventBus.emit('selection:request', {
      intent: 'replace',
      id: selectedInstanceId,
      source: 'single-target-collapse'
    });
  }

  if (mod && key === 'c' && selectedInstanceId) {
    prevent();
    return copySelectedSection();
  }

  if (mod && key === 'v') {
    prevent();
    return pasteSectionAfterSelection();
  }

  if (mod && shift && key === 'arrowup' && selectedInstanceId) {
    prevent();
    return moveSelectedSectionByShortcut('up');
  }

  if (mod && shift && key === 'arrowdown' && selectedInstanceId) {
    prevent();
    return moveSelectedSectionByShortcut('down');
  }

  if ((key === 'delete' || key === 'backspace') && selectedInstanceId) {
    prevent();
    const { ran: removed } = await sectionService.remove(selectedInstanceId, { announce: true });
    if (removed) {
      // The state-changed prune has usually cleared the store already; this
      // emit still runs unconditionally so every legacy listener (sidebar
      // highlight, panel, glass, iframe clear via the shell's own
      // section-deselected handler) hears about it, exactly as before.
      eventBus.emit('section-deselected');
    }
    return true;
  }

  if (mod && key === 'd' && selectedInstanceId) {
    prevent();
    sectionManager.duplicateSection(selectedInstanceId);
    return true;
  }

  if (key === 'escape' && selectedInstanceId) {
    prevent();
    eventBus.emit('section-deselected');
    return true;
  }

  if ((!mod && isHelpShortcutKey(key, shift)) || (mod && key === '/')) {
    prevent();
    showShortcutsHelp();
    return true;
  }

  return false;
}

/**
 * P8: the export gate no longer asks anything.
 *
 * Gate v2 already conceded the point — once every blocking finding was
 * acknowledgeable with one click, the prompt was a decision that cost time and
 * bought nothing. Worse, its primary, focus-holding button ABORTED the export,
 * so the only route to fixing anything threw away every asset just downloaded.
 * That was the loop.
 *
 * Recoverable findings now ship stamped `autoOverridden` and are disclosed in
 * export-report.html; on PUBLISH anything not on AUTO_SHIPPABLE_CHECKS still
 * throws. A ZIP DOWNLOAD (gate v4, 2026-10-01) always completes and stamps
 * those `exportedAnyway` — the toast and the Publish checks panel say so.
 */
const EXPORT_PREFLIGHT_MODE = 'interactive-auto-policy';
const DOWNLOAD_PREFLIGHT_MODE = 'download';

/**
 * Surface an export failure. Pre-flight blocks (ExportPreflightError) get a
 * longer toast with the readable problem list and the full machine-readable
 * findings logged for debugging / the Phase 13 report. AbortError is a quiet
 * cancel. Everything else is a generic failure toast.
 */
function reportExportFailure(err, kind) {
  if (err?.name === 'AbortError') {
    showToast('Export cancelled');
    return;
  }
  if (err?.name === 'ExportPreflightError') {
    if (err.report) {
      presentExportReport(err.report, null, kind === '11ty export' ? '11ty' : 'flat', {
        message: `${kind} needs attention`,
        type: 'error',
        stoppedKind: kind
      });
      reportError('Shell export', err.findings || err, {
        user: 'silent',
        message: err.message,
        detail: `${kind} blocked by pre-flight`,
        id: `export-preflight-${kind}`
      });
      return;
    }
    reportError('Shell export', err.findings || err, {
      user: 'toast',
      message: err.message,
      detail: `${kind} blocked by pre-flight`,
      duration: 8000,
      id: `export-preflight-${kind}`
    });
    return;
  }
  reportError('Shell export', err, {
    user: 'toast',
    message: `${kind} failed: ` + (err?.message || err),
    detail: `${kind} failed`
  });
}

function createAmbientExportProgress(controller) {
  const cancel = () => controller.abort();
  eventBus.on('toolbar:cancel-export', cancel);
  return {
    update(message, percent = null) {
      eventBus.emit('toolbar:operation-state', {
        type: 'export',
        busy: true,
        label: message || 'Exporting…',
        percent,
        cancelable: true
      });
    },
    close() {
      eventBus.off('toolbar:cancel-export', cancel);
    }
  };
}

function presentExportReport(
  report,
  summary,
  mode,
  { message = null, type = null, stoppedKind = null } = {}
) {
  eventBus.emit('ship:export-report', { report, summary, mode, stoppedKind });
  // A download that shipped something the gate could not recover must not toast
  // a plain green "Export ready" — that is the one result worth reviewing.
  const exportedAnyway = (report?.findings || []).filter(
    (f) => f?.disposition === 'exportedAnyway'
  ).length;
  if (!message) {
    message = exportedAnyway
      ? `Exported with ${exportedAnyway} problem${exportedAnyway === 1 ? '' : 's'} that may break the site`
      : 'Export ready';
  }
  if (!type) type = exportedAnyway ? 'warning' : 'success';
  showToast(message, {
    type,
    id: `export-complete-${mode}`,
    duration: 6500,
    action: {
      label: 'Review',
      onClick: () => {
        eventBus.emit('sidebar:ensure-open');
        eventBus.emit('props:request-tab', { tab: 'audit' });
      }
    }
  });
}

let _tabGuard = null;
let _tabGuardHadSecondaryRole = false;

/**
 * Show (or refresh) the "open in another tab" banner. Idempotent — one banner
 * at a time. `reason: 'yielded'` means this tab was just taken over by another;
 * 'init' means another tab already owned the project when this one opened.
 */
function showTabGuardBanner(reason) {
  removeTabGuardBanner();
  const banner = document.createElement('div');
  banner.className = 'pb-tabguard-banner';
  banner.setAttribute('role', 'alert');
  banner.dataset.tabguardBanner = '1';

  const msg =
    reason === 'yielded'
      ? 'Editing was taken over by another tab. This tab is now read-only.'
      : 'This project is already open in another tab. This tab is read-only.';

  banner.innerHTML = `
    <div class="pb-tabguard-content">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="4" width="18" height="14" rx="2"/><path d="M8 20h8M12 18v2"/>
      </svg>
      <span>${msg}</span>
    </div>
    <div class="pb-tabguard-actions">
      <button class="pb-btn pb-btn-primary" data-tabguard="takeover">Edit here</button>
    </div>
  `;

  banner.addEventListener('click', (e) => {
    if (e.target.dataset.tabguard === 'takeover') _tabGuard?.requestTakeover();
  });

  const app = document.querySelector('.pb-app');
  (app || document.body).prepend(banner);
}

function removeTabGuardBanner() {
  document.querySelectorAll('[data-tabguard-banner]').forEach((el) => el.remove());
}

function removeSessionClaimBanner() {
  document.querySelectorAll('[data-session-claim-banner]').forEach((el) => el.remove());
}

function showSessionClaimBanner(editorClaim) {
  removeSessionClaimBanner();
  const banner = document.createElement('div');
  banner.className = 'pb-tabguard-banner';
  banner.dataset.sessionClaimBanner = '1';
  const content = document.createElement('div');
  content.className = 'pb-tabguard-content';
  const message = document.createElement('span');
  message.textContent = editorClaim
    ? `Read-only here — ${editorClaim.deviceLabel || 'another window'} is editing this project.`
    : 'Editing is available — choose Edit here to claim this project.';
  content.appendChild(message);
  const actions = document.createElement('div');
  actions.className = 'pb-tabguard-actions';
  actions.appendChild(
    createButton({
      variant: 'primary',
      label: 'Edit here',
      onClick: () => collabTakeOverEditor()
    })
  );
  banner.append(content, actions);
  (document.querySelector('.pb-app') || document.body).prepend(banner);
}

// ── Project unavailable (SB-1) ──────────────────────────────────────────────
// The state the builder never had. The server distinguishes nothing on a read
// (404 covers deleted, access-lost and never-existed alike), so the CAUSE is
// only stated when the signal stated it — see unavailableMessageFor. Copy that
// guesses "deleted" from a read 404 would be a guess presented as a fact.

/** Set once we enter the state, so a second signal cannot re-enter or re-toast. */
let _projectUnavailableSignal = null;

/**
 * Leave the unavailable state. SB-3b: this latch is per-PROJECT state that lives
 * in module scope, so every project transition must clear it — otherwise a
 * healthy project opened after an unavailable one inherits its banner and its
 * write blocker, and the user cannot edit a project that is perfectly fine.
 * Idempotent: safe to call on transitions that were never in the state.
 */
function clearProjectUnavailable() {
  if (!_projectUnavailableSignal) return;
  _projectUnavailableSignal = null;
  store.removeBlocker(WRITE_BLOCKER.PROJECT_UNAVAILABLE);
  _cloudSyncPaused = false;
  document.querySelectorAll('[data-project-unavailable]').forEach((el) => el.remove());
  syncIframeInteractionMode();
}

async function enterProjectUnavailable(signal) {
  const state = store.getState();
  if (!state?.projectId || _projectUnavailableSignal) return;
  _projectUnavailableSignal = signal || 'read-missing';
  _cloudSyncPaused = true;

  // Finish the editor's pending iframe value while the store is still
  // writable. Cancelling here aborts the transaction and restores its opening
  // snapshot, which silently loses the final text edit the rescue action exists
  // to preserve.
  try {
    await commitActiveTextEdit();
  } catch (err) {
    // The authoritative lifecycle transition must still complete if the
    // iframe cannot commit. Keep the rescue surface available and make the
    // failed final-edit capture observable.
    reportError('Project unavailable text commit', err, {
      user: 'silent',
      detail: 'active text edit could not be committed before the project became unavailable'
    });
  }

  // PRESERVE the user's work. This is why SB-0 made the transition
  // non-destructive: `setWritable(false)` would discard the unsaved edits that
  // "Save as a new project" exists to rescue.
  store.addBlocker(WRITE_BLOCKER.PROJECT_UNAVAILABLE);
  syncIframeInteractionMode();
  void disconnectCollab({ flush: false });
  showProjectUnavailableBanner(_projectUnavailableSignal);
  void renderPreview({ preserveScroll: true, transition: null, hideDuringLoad: false });
}

function showProjectUnavailableBanner(signal) {
  removeTabGuardBanner();
  const banner = document.createElement('div');
  banner.className = 'pb-tabguard-banner pb-tabguard-banner-unavailable';
  banner.setAttribute('role', 'alert');
  banner.dataset.tabguardBanner = '1';
  banner.dataset.projectUnavailable = '1';

  const content = document.createElement('div');
  content.className = 'pb-tabguard-content';
  content.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16v.01"/>
    </svg>`;
  const span = document.createElement('span');
  // textContent, never innerHTML — the message is chosen from a fixed table,
  // but the standing rule is that no string reaches the DOM as markup.
  span.textContent = `${unavailableMessageFor(signal)} Your unsaved changes are still here.`;
  content.appendChild(span);

  const actions = document.createElement('div');
  actions.className = 'pb-tabguard-actions';
  const saveCopyBtn = createButton({
    variant: 'primary',
    label: 'Save as a new project',
    onClick: () => void saveUnavailableProjectAsNew()
  });
  const backBtn = createButton({
    variant: 'ghost',
    label: 'Back to projects',
    onClick: () =>
      void showProjectSwitcherModal({
        canOpenProject: () => true,
        beforeOpenProject: ({ id }) => prepareTabGuardForProject(id)
      })
  });
  actions.append(saveCopyBtn, backBtn);

  banner.append(content, actions);
  const app = document.querySelector('.pb-app');
  (app || document.body).prepend(banner);
}

/**
 * Rescue the in-memory project into a NEW cloud project. The old id is gone
 * server-side, so reusing it would 404 forever; a fresh id plus a null
 * cloudVersion makes the next save a create rather than a doomed update.
 */
async function saveUnavailableProjectAsNew() {
  const live = store.getState();
  if (!live) return;
  const oldProjectId = live.projectId;
  const oldCloudVersion = live.cloudVersion;
  const newProjectId = crypto.randomUUID();
  const progress = showProgressToast('Saving a copy…', { id: 'save-copy' });
  try {
    if (!(await prepareTabGuardForProject(newProjectId))) {
      progress.dismiss();
      showToast('Could not reserve a local slot for the new project.', 'error');
      return;
    }
    // Direct mutation, matching how cloudVersion is handled everywhere else:
    // neither field belongs in undo history.
    live.projectId = newProjectId;
    live.cloudVersion = null;
    clearProjectUnavailable();
    await cloudSave();
    progress.success('Saved as a new project');
    eventBus.emit('project:restored', { projectId: live.projectId, version: 1 });
  } catch (err) {
    // Restore the old identity only if its lock can still be reclaimed. A
    // sibling may have promoted while cloudSave was in flight; restoring the
    // old id while still holding the new lock would recreate cross-slot writes.
    const restoredOldGuard = await prepareTabGuardForProject(oldProjectId);
    if (restoredOldGuard) {
      live.projectId = oldProjectId;
      live.cloudVersion = oldCloudVersion;
      _projectUnavailableSignal = 'read-missing';
      store.addBlocker(WRITE_BLOCKER.PROJECT_UNAVAILABLE);
      _cloudSyncPaused = true;
      showProjectUnavailableBanner(_projectUnavailableSignal);
    } else {
      // Keep a coherent local copy under the newly reserved id. The cloud save
      // failed, but the rescued edits remain available and autosaved locally.
      live.projectId = newProjectId;
      live.cloudVersion = null;
      clearProjectUnavailable();
      await store.saveToIDB();
      writeLastOpenedProject(store.getPrincipalScope(), newProjectId);
    }
    progress.dismiss();
    reportError('Save a copy', err, {
      user: 'toast',
      message: restoredOldGuard
        ? 'Could not save a copy — your original is unchanged'
        : 'Cloud save failed — your local copy is still available',
      detail: err?.message || 'save-as-new after project became unavailable failed'
    });
  }
}

async function refreshLatestAutosaveAfterPromotion() {
  const autoSave = await checkAutoSave();
  if (!autoSave?.state) return;
  await restoreAutoSave(autoSave.state);
  resetSelection('tab-guard-promotion');
  resetActiveEditable('tab-guard-promotion');
  selectedElementPayload = null;
  bridge.clearScroll();
  await renderPreview({ preserveScroll: false, transition: null, hideDuringLoad: false });
}

async function becomeWritableAfterTabGuardPromotion() {
  const shouldRefresh = _tabGuardHadSecondaryRole;
  _tabGuardHadSecondaryRole = false;

  if (shouldRefresh) {
    try {
      await refreshLatestAutosaveAfterPromotion();
    } catch (err) {
      reportError('Tab guard promotion', err, {
        user: 'status',
        message: 'Could not refresh latest saved state',
        detail: err?.message || 'Failed to refresh the latest autosave before taking over editing.'
      });
      showTabGuardBanner('init');
      return;
    }
  }

  store.setWritable(true);
  syncIframeInteractionMode();
  removeTabGuardBanner();
  // Collab Phase 8: this tab is now the writer — (re)join the realtime session.
  void maybeConnectCollab(currentUser);
}

/**
 * Elect one writer tab PER (principal, project), so two tabs on the same project
 * cannot clobber its autosave slot — and two tabs on DIFFERENT projects no
 * longer contend at all. The non-owner tab suppresses its autosave and shows a
 * persistent banner; "Edit here" flushes the current owner and takes over.
 *
 * SB-3b: until a project is known the guard holds the legacy GLOBAL lock. That
 * is deliberately conservative — it over-locks briefly (two booting tabs contend
 * for a moment) rather than under-locking, which would let two tabs both believe
 * they own the same slot during the window before the project resolves.
 */
function tabGuardLockName() {
  const projectId = store.getState()?.projectId;
  if (!projectId) return 'pb-builder';
  return `pb-builder:${store.getPrincipalScope()}:${projectId}`;
}

// Serializes dispose→create→start. Two project transitions arriving close
// together (a deep link resolving while the switcher opens) must not interleave
// and leave two live guards holding two different locks — which would be a NEW
// session-boundary bug of exactly the class this initiative removes.
let _tabGuardRebind = Promise.resolve();
let _tabGuardLockName = null;

function buildTabGuard(lockName) {
  let guard = null;
  guard = createTabGuard({
    lockName,
    channelName: `pb-tab-guard:${lockName}`,
    onBecomePrimary: () => {
      if (_tabGuard !== guard) return;
      void becomeWritableAfterTabGuardPromotion();
    },
    onBecomeSecondary: ({ reason } = {}) => {
      if (_tabGuard !== guard) return;
      _tabGuardHadSecondaryRole = true;
      // Collab Phase 8: a read-only tab must never hold a live session (single
      // writer per browser). Disconnect before flipping writable off.
      void disconnectCollab({ flush: false });
      store.setWritable(false);
      cancelActiveTextEdit();
      syncIframeInteractionMode();
      showTabGuardBanner(reason);
      // Reconcile any live-only iframe DOM/override updates with the state that
      // survived transaction abort, then keep the replacement iframe read-only.
      void renderPreview({ preserveScroll: true, transition: null, hideDuringLoad: false });
    },
    onBeforeYield: async () => {
      if (_tabGuard !== guard) return;
      await commitActiveTextEdit();
      await store.flushAutoSave();
    }
  });
  return guard;
}

function initTabGuard() {
  _tabGuardLockName = tabGuardLockName();
  _tabGuard = buildTabGuard(_tabGuardLockName);
  // Non-blocking: role is decided asynchronously (instant with Web Locks, ~300ms
  // for the BroadcastChannel-fallback election) and applied via the callbacks.
  void _tabGuard.start();
}

/**
 * Rebind the guard onto the now-open project's lock. Called from EVERY project
 * transition — boot restore, deep link, switcher open, Save-as-a-new-project,
 * sign-out — because `initTabGuard()` runs during boot before any project
 * exists, so the lock name cannot simply be passed at construction.
 *
 * Awaited and chained: the previous guard is fully disposed (releasing its lock,
 * which lets a waiting tab promote) before the next one starts.
 */
function rebindTabGuardForProject() {
  return prepareTabGuardForProject(store.getState()?.projectId);
}

/**
 * Acquire the destination project's guard before its state is installed. The
 * candidate's callbacks are inert until it wins, so failure leaves the current
 * project, current lock, and autosave slot untouched.
 */
function prepareTabGuardForProject(projectId) {
  _tabGuardRebind = _tabGuardRebind
    .catch(() => false)
    .then(async () => {
      const next = projectId
        ? `pb-builder:${store.getPrincipalScope()}:${projectId}`
        : 'pb-builder';
      if (next === _tabGuardLockName && _tabGuard) {
        return _tabGuard.getRole() === 'primary';
      }
      const candidate = buildTabGuard(next);
      await candidate.start();
      if (candidate.getRole() !== 'primary') {
        candidate.dispose();
        return false;
      }
      // Flush under the OLD lock while this tab still owns it. Yielding without
      // flushing would strand whatever the debounce had not written yet.
      try {
        await commitActiveTextEdit();
        if (_tabGuard && store.isWritable()) await store.flushAutoSave();
      } catch {
        // A flush failure must not strand the guard on a stale lock — the next
        // mutation re-triggers autosave under the new one.
      }
      // The destination can receive a takeover while the old-project flush
      // above awaits. Never install a candidate that yielded in that window.
      if (candidate.getRole() !== 'primary') {
        candidate.dispose();
        return false;
      }
      _tabGuard?.dispose();
      _tabGuardLockName = next;
      _tabGuard = candidate;
      await becomeWritableAfterTabGuardPromotion();
      return true;
    });
  return _tabGuardRebind;
}

/**
 * Discard the auto-restored session and start from a blank project, from the boot
 * "Start fresh" toast action. Snapshots a recoverable revision first so the user
 * can undo the decision from history, then clears the autosave slot and resets.
 */
async function startFreshFromBoot() {
  // Pre-snapshot so "Start fresh" is never destructive. Await the queued save so
  // it clones the restored session before store.init() replaces it.
  try {
    await revisionStore.save('Before start fresh', '', 'auto');
  } catch (err) {
    console.warn('[Shell] start-fresh pre-snapshot failed:', err?.message || err);
  }
  try {
    await dismissAutoSave();
  } catch (err) {
    console.warn('[Shell] start-fresh slot clear failed:', err?.message || err);
  }
  sectionManager.stopBackgroundPrefetch({ invalidate: true });
  resetSelection('become-writable-start-fresh');
  resetActiveEditable('become-writable-start-fresh');
  selectedElementPayload = null;
  store.init(createDefaultState());
  bridge.clearScroll();
  await renderPreview({ preserveScroll: false, transition: null, hideDuringLoad: false });
}

/**
 * Load a specific cloud version, from the boot "Load cloud version" toast action.
 * Offered when the background fresher-check found a newer cloud copy but the store
 * changed during the download (so auto-apply was suppressed to protect the edit).
 */
async function loadPendingCloudVersion(projectId, version) {
  if (!ensureWritableTab()) return;
  const state = store.getState();
  if (!state?.projectId) return;
  if (state.projectId !== projectId) {
    showToast('That cloud version is no longer for the open project.', 'info');
    return;
  }
  try {
    await restoreCloudVersion(projectId, version, { requireWritable: true });
    eventBus.emit('project:restored', { projectId, version });
    showToast(`Loaded cloud version v${version}`, 'success');
  } catch (err) {
    if (isLoadSupersededError(err)) {
      showToast(err.message, 'info');
      return;
    }
    reportError('Cloud restore', err, {
      user: 'toast',
      message: 'Could not load the cloud version',
      detail: err?.message || 'cloud version bundle load failed'
    });
  }
}

/**
 * ?openProject=<id> deep link (crm-bridge B2 — the import response's openUrl).
 * Fetches the project's latest version and opens it through the SAME cloud-open
 * path as the switcher (restoreCloudVersion → stamping/migration → cloudVersion
 * set), so an imported project behaves exactly like one opened by hand. The
 * param is stripped afterwards so a refresh doesn't re-trigger the open.
 */
/**
 * @returns {Promise<{opened: boolean, suppressAutosaveRestore: boolean}>}
 *   `suppressAutosaveRestore` is SB-1 (A7): when the linked project is
 *   authoritatively unavailable, boot must NOT quietly fall through to the
 *   autosave slot. The user asked for a specific project; restoring a different
 *   one behind a vague toast is how a deep link to a deleted project ended up
 *   looking like it had worked. A TRANSIENT failure still falls through, because
 *   there the session restore is the right thing to keep.
 */
async function openProjectFromUrl(projectId) {
  try {
    const out = await getProject(projectId);
    const row = out?.project ?? out;
    const latest = Number(row?.latestVersion);
    if (!Number.isInteger(latest) || latest < 1) {
      showToast('The linked project has no versions yet.', 'info');
      return { opened: false, suppressAutosaveRestore: false };
    }
    await restoreCloudVersion(projectId, latest, { requireWritable: true });
    eventBus.emit('project:restored', { projectId, version: latest });
    showToast('Opened project from link', 'success');
    return { opened: true, suppressAutosaveRestore: false };
  } catch (err) {
    if (isLoadSupersededError(err)) {
      showToast(err.message, 'info');
      return { opened: false, suppressAutosaveRestore: false };
    }
    // A link is itself evidence the project existed once, so a 404 here is
    // treated as confirmed-then-gone rather than never-synced.
    const { lifecycle, signal } = classifyProjectSignal({
      operation: LIFECYCLE_OPERATION.READ,
      status: err?.status ?? null,
      everConfirmedInCloud: true
    });
    if (lifecycle === LIFECYCLE.UNAVAILABLE) {
      showToast(unavailableMessageFor(signal), { type: 'error', duration: 10000 });
      return { opened: false, suppressAutosaveRestore: true };
    }
    reportError('Open from link', err, {
      user: 'toast',
      message: 'Could not open the linked project',
      detail: err?.message || 'openProject deep link failed'
    });
    return { opened: false, suppressAutosaveRestore: false };
  } finally {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('openProject');
      window.history.replaceState({}, '', url);
    } catch {
      // URL cleanup only — never blocks boot.
    }
  }
}

const INVITE_TOKEN_SESSION_KEY = 'pb-pending-invite-token';
let _pendingInviteToken = '';

function inviteTokenForSession() {
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get('invite') || '';
    if (fromUrl) {
      _pendingInviteToken = fromUrl;
      try {
        window.sessionStorage.setItem(INVITE_TOKEN_SESSION_KEY, fromUrl);
      } catch {
        // The in-memory copy still carries an uninterrupted auth handoff.
      }
      // The bearer must not remain in browser history, screenshots, or
      // same-origin Referer headers while the sign-in flow is open.
      url.searchParams.delete('invite');
      window.history.replaceState({}, '', url);
    }
    if (_pendingInviteToken) return _pendingInviteToken;
    try {
      _pendingInviteToken = window.sessionStorage.getItem(INVITE_TOKEN_SESSION_KEY) || '';
    } catch {
      // Session storage can be unavailable in privacy-restricted contexts.
    }
    return _pendingInviteToken;
  } catch {
    return _pendingInviteToken;
  }
}

function clearPendingInviteToken() {
  _pendingInviteToken = '';
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('invite');
    window.history.replaceState({}, '', url);
  } catch {
    // The in-memory credential was still cleared.
  }
  try {
    window.sessionStorage.removeItem(INVITE_TOKEN_SESSION_KEY);
  } catch {
    // Session storage may be unavailable; the in-memory credential is gone.
  }
}

function maybeShowInviteAccept() {
  const token = inviteTokenForSession();
  if (!token) return;
  showInviteAcceptModal({
    token,
    currentUser,
    onInvalid: clearPendingInviteToken,
    onAccepted: async ({ projectId } = {}) => {
      clearPendingInviteToken();
      if (projectId) await openProjectFromUrl(projectId);
    }
  });
}

function isDoubleBufferEnabled() {
  return store.getState()?.flags?.doubleBuffer === true;
}

function isRenderDebugEnabled() {
  if (store.getState()?.flags?.renderDebug === true) return true;
  try {
    const search = new URLSearchParams(window.location.search || '');
    const debugValues = search.getAll('debug');
    return debugValues.includes('render');
  } catch {
    return false;
  }
}

function emitRendererComplete(payload) {
  const phases = payload?.phases && typeof payload.phases === 'object' ? payload.phases : {};
  const completePayload = {
    renderId: payload?.renderId || '',
    totalMs: Number(payload?.totalMs) || 0,
    phases
  };

  eventBus.emit('renderer:complete', completePayload);

  if (!isRenderDebugEnabled()) return;
  const rows = Object.entries(phases).map(([phase, ms]) => ({
    phase,
    ms: Math.round((Number(ms) || 0) * 100) / 100
  }));
  rows.push({ phase: 'total', ms: Math.round(completePayload.totalMs * 100) / 100 });
  try {
    console.table(rows);
  } catch {
    console.log('[Renderer]', completePayload);
  }
}

function syncViewportIframeBinding() {
  const vp = commandBar?.viewportControls;
  if (!vp || !previewIframe) return;
  if (vp._iframe === previewIframe) return;
  const prevIframe = vp._iframe;
  const width = prevIframe?.style?.width || '';
  const height = prevIframe?.style?.height || '';
  const maxWidth = prevIframe?.style?.maxWidth || '';
  vp._iframe = previewIframe;
  if (width) previewIframe.style.width = width;
  if (height) previewIframe.style.height = height;
  if (maxWidth) previewIframe.style.maxWidth = maxWidth;
}

function syncGlassOverlayIframeBinding() {
  if (!_glassOverlay || !previewIframe || _glassOverlay._iframe === previewIframe) return;
  const prevIframe = _glassOverlay._iframe;
  const obs = _glassOverlay._resizeObs;
  if (obs && prevIframe && typeof obs.unobserve === 'function') {
    try {
      obs.unobserve(prevIframe);
    } catch {
      // Ignore unobserve failures.
    }
  }
  _glassOverlay._iframe = previewIframe;
  if (obs && typeof obs.observe === 'function') {
    try {
      obs.observe(previewIframe);
    } catch {
      // Ignore observe failures.
    }
  }
  if (typeof _glassOverlay._syncDimensions === 'function') {
    _glassOverlay._syncDimensions();
  }
}

function markPreviewIframeAsActive(iframe) {
  if (!iframe) return;
  iframe.classList.add('pb-preview-iframe--active');
  iframe.classList.remove('pb-preview-iframe--buffer');
  iframe.style.opacity = '';
  iframe.style.visibility = '';
  iframe.style.pointerEvents = 'auto';
  iframe.style.transition = '';
  iframe.setAttribute('aria-hidden', 'false');
}

function markPreviewIframeAsBuffer(iframe) {
  if (!iframe) return;
  iframe.classList.remove('pb-preview-iframe--active');
  iframe.classList.add('pb-preview-iframe--buffer');
  iframe.style.opacity = '0';
  iframe.style.visibility = 'hidden';
  iframe.style.pointerEvents = 'none';
  iframe.style.transition = '';
  iframe.setAttribute('aria-hidden', 'true');
}

function ensureDoubleBufferIframe() {
  if (!previewIframe) return null;
  if (!_primaryPreviewIframe) _primaryPreviewIframe = previewIframe;
  if (_secondaryPreviewIframe && _secondaryPreviewIframe.isConnected)
    return _secondaryPreviewIframe;
  const wrap = previewIframe.closest('.pb-iframe-wrap');
  if (!wrap) return null;
  const buffer = document.createElement('iframe');
  buffer.id = 'pb-preview-buffer';
  buffer.className = 'pb-preview-iframe pb-preview-iframe--buffer';
  buffer.setAttribute('title', 'Website preview');
  buffer.setAttribute('aria-hidden', 'true');
  buffer.setAttribute('sandbox', previewIframe.getAttribute('sandbox') || 'allow-scripts');
  buffer.style.width = previewIframe.style.width || '100%';
  buffer.style.height = previewIframe.style.height || '100%';
  buffer.style.maxWidth = previewIframe.style.maxWidth || '100%';
  wrap.insertBefore(buffer, previewIframe.nextSibling);
  _secondaryPreviewIframe = buffer;
  markPreviewIframeAsActive(previewIframe);
  return _secondaryPreviewIframe;
}

function getInactivePreviewIframe(force = false) {
  if (!force && !isDoubleBufferEnabled()) return previewIframe;
  const buffer = ensureDoubleBufferIframe();
  if (!buffer || !_primaryPreviewIframe) return previewIframe;
  return previewIframe === _primaryPreviewIframe ? buffer : _primaryPreviewIframe;
}

function disableDoubleBufferIfNeeded(force = false) {
  if (force || isDoubleBufferEnabled()) return;
  if (_secondaryPreviewIframe && _secondaryPreviewIframe.isConnected) {
    const inactiveIframe =
      previewIframe === _secondaryPreviewIframe ? _primaryPreviewIframe : _secondaryPreviewIframe;
    if (inactiveIframe && inactiveIframe !== previewIframe) inactiveIframe.remove();
  }
  _secondaryPreviewIframe = null;
  if (previewIframe) {
    previewIframe.id = 'pb-preview';
    markPreviewIframeAsActive(previewIframe);
    _primaryPreviewIframe = previewIframe;
    syncViewportIframeBinding();
    syncGlassOverlayIframeBinding();
  }
}

function syncInactivePreviewSizing(inactiveIframe) {
  if (!inactiveIframe || !previewIframe || inactiveIframe === previewIframe) return;
  inactiveIframe.style.width = previewIframe.style.width || '100%';
  inactiveIframe.style.height = previewIframe.style.height || '100%';
  inactiveIframe.style.maxWidth = previewIframe.style.maxWidth || '100%';
}

function finalizeDoubleBufferSwap(currentIframe, nextIframe, renderToken) {
  if (_activeRenderVisualToken !== renderToken) return;
  if (!nextIframe || !currentIframe) return;
  markPreviewIframeAsBuffer(currentIframe);
  markPreviewIframeAsActive(nextIframe);
  if (currentIframe.id === 'pb-preview') currentIframe.id = 'pb-preview-buffer';
  if (nextIframe.id !== 'pb-preview') nextIframe.id = 'pb-preview';
  previewIframe = nextIframe;
  syncViewportIframeBinding();
  syncGlassOverlayIframeBinding();
  flushPendingBlobRevoke();
}

function startDoubleBufferSwap(nextIframe, renderToken) {
  const currentIframe = previewIframe;
  if (!nextIframe || !currentIframe || nextIframe === currentIframe) {
    flushPendingBlobRevoke();
    return;
  }

  if (_doubleBufferSwapCancel) _doubleBufferSwapCancel();

  // Keep outgoing frame interactive-disabled while the incoming frame cross-fades in.
  currentIframe.style.pointerEvents = 'none';
  nextIframe.style.visibility = '';
  nextIframe.style.pointerEvents = 'none';
  nextIframe.style.opacity = '0';
  nextIframe.style.transition = `opacity ${DOUBLE_BUFFER_FADE_MS}ms ease`;
  currentIframe.style.transition = `opacity ${DOUBLE_BUFFER_FADE_MS}ms ease`;
  void nextIframe.offsetWidth;
  nextIframe.style.opacity = '1';
  currentIframe.style.opacity = '0';

  const completeSwap = () => {
    if (_doubleBufferFinalizeTimer) {
      clearTimeout(_doubleBufferFinalizeTimer);
      _doubleBufferFinalizeTimer = null;
    }
    _doubleBufferSwapCancel = null;
    finalizeDoubleBufferSwap(currentIframe, nextIframe, renderToken);
  };

  const onTransitionEnd = (event) => {
    if (event?.target !== nextIframe) return;
    nextIframe.removeEventListener('transitionend', onTransitionEnd);
    completeSwap();
  };
  nextIframe.addEventListener('transitionend', onTransitionEnd);
  _doubleBufferSwapCancel = () => {
    nextIframe.removeEventListener('transitionend', onTransitionEnd);
    if (_doubleBufferFinalizeTimer) {
      clearTimeout(_doubleBufferFinalizeTimer);
      _doubleBufferFinalizeTimer = null;
    }
    markPreviewIframeAsActive(currentIframe);
    markPreviewIframeAsBuffer(nextIframe);
    _doubleBufferSwapCancel = null;
  };
  _doubleBufferFinalizeTimer = setTimeout(() => {
    nextIframe.removeEventListener('transitionend', onTransitionEnd);
    completeSwap();
  }, DOUBLE_BUFFER_FADE_MS + 50);
}

function getLoaderLabel(phase, progress) {
  if (phase === 'hydrating-page') {
    const name = String(progress?.name || '').trim();
    return name ? `Loading ${name}…` : 'Loading page…';
  }
  if (phase === 'resolving-assets') {
    const current = Number(progress?.current);
    const total = Number(progress?.total);
    if (Number.isFinite(current) && Number.isFinite(total) && total > 0) {
      const clampedCurrent = Math.max(0, Math.min(total, current));
      return `Resolving assets (${clampedCurrent}/${total})…`;
    }
    return 'Resolving assets…';
  }
  if (phase === 'assembling') return LOADER_PHASE_LABELS.assembling;
  if (phase === 'loading') return LOADER_PHASE_LABELS.loading;
  if (phase === 'restoring-scroll') return LOADER_PHASE_LABELS.restoringScroll;
  return LOADER_PHASE_LABELS.default;
}

function updateIframeLoadingLabel(phase, progress) {
  const center = document.getElementById('pb-center');
  if (!center) return;
  const loader = center.querySelector('.pb-iframe-loading');
  if (!loader) return;
  const labelEl = loader.querySelector('.pb-iframe-loading-label');
  if (!labelEl) return;
  labelEl.textContent = getLoaderLabel(phase, progress);
}

function showIframeLoading(show, phase = null, progress = null) {
  const center = document.getElementById('pb-center');
  if (!center) return;
  let loader = center.querySelector('.pb-iframe-loading');
  if (show && !loader) {
    loader = document.createElement('div');
    loader.className = 'pb-iframe-loading';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.innerHTML = `<div class="pb-dots-loader"><span></span><span></span><span></span></div><span class="pb-iframe-loading-label">${getLoaderLabel(phase, progress)}</span>`;
    center.appendChild(loader);
  } else if (show && loader) {
    updateIframeLoadingLabel(phase, progress);
  } else if (!show && loader) {
    loader.remove();
  }
}

function ensureRevealOverlay() {
  if (_revealOverlay && _revealOverlay.isConnected) return _revealOverlay;
  const host = previewIframe?.closest('.pb-iframe-wrap');
  if (!host) return null;
  let overlay = host.querySelector('.pb-reveal-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'pb-reveal-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML =
      '<div class="pb-dots-loader pb-reveal-overlay-dots"><span></span><span></span><span></span></div>';
    host.appendChild(overlay);
  }
  _revealOverlay = overlay;
  return _revealOverlay;
}

function prefersReducedMotionSafe() {
  if (typeof window?.matchMedia !== 'function') return false;
  return prefersReducedMotion();
}

function showRevealOverlay(renderToken) {
  if (isDoubleBufferEnabled()) {
    hideRevealOverlay(renderToken, { immediate: true, force: true });
    return;
  }
  const overlay = ensureRevealOverlay();
  if (!overlay) return;
  overlay.dataset.renderToken = String(renderToken);
  if (overlay._hideTimer) {
    clearTimeout(overlay._hideTimer);
    overlay._hideTimer = null;
  }
  if (overlay._hideHandler) {
    overlay.removeEventListener('transitionend', overlay._hideHandler);
    overlay._hideHandler = null;
  }
  overlay.style.display = 'flex';
  overlay.classList.remove('pb-reveal-overlay--fadeout');
  overlay.classList.add('pb-reveal-overlay--active');
}

function hasRenderedPreviewContent(iframe) {
  if (!iframe) return false;
  return (
    iframe.dataset.pbRendered === 'true' ||
    iframe.hasAttribute('src') ||
    iframe.hasAttribute('srcdoc')
  );
}

function shouldShowRevealOverlay(
  renderTarget,
  request,
  doubleBufferMode = isDoubleBufferEnabled()
) {
  if (doubleBufferMode) return false;
  if (request?.hideDuringLoad) return true;
  if (hasRenderedPreviewContent(renderTarget)) return true;
  return Boolean(request?.preserveScroll && bridge.lastScrollY > 0);
}

function hideRevealOverlay(renderToken, { immediate = false, force = false } = {}) {
  const overlay = _revealOverlay || document.querySelector('.pb-reveal-overlay');
  if (!overlay) return;
  const token = String(renderToken);
  if (!force && overlay.dataset.renderToken && overlay.dataset.renderToken !== token) return;

  const finalize = () => {
    if (!force && overlay.dataset.renderToken && overlay.dataset.renderToken !== token) return;
    overlay.classList.remove('pb-reveal-overlay--active', 'pb-reveal-overlay--fadeout');
    overlay.style.display = 'none';
    if (overlay._hideTimer) {
      clearTimeout(overlay._hideTimer);
      overlay._hideTimer = null;
    }
    if (overlay._hideHandler) {
      overlay.removeEventListener('transitionend', overlay._hideHandler);
      overlay._hideHandler = null;
    }
  };

  if (immediate || prefersReducedMotionSafe()) {
    finalize();
    return;
  }

  if (overlay._hideTimer) clearTimeout(overlay._hideTimer);
  if (overlay._hideHandler) {
    overlay.removeEventListener('transitionend', overlay._hideHandler);
  }

  const onTransitionEnd = (event) => {
    if (event?.target && event.target !== overlay) return;
    finalize();
  };

  overlay._hideHandler = onTransitionEnd;
  overlay.addEventListener('transitionend', onTransitionEnd);
  overlay.classList.add('pb-reveal-overlay--fadeout');
  overlay._hideTimer = setTimeout(finalize, REVEAL_OVERLAY_FADE_MS + 60);
}

function ensureEditStatusLiveRegion() {
  if (_editStatusLiveRegion && _editStatusLiveRegion.isConnected) return _editStatusLiveRegion;
  const existing = document.getElementById('pb-edit-status-live');
  if (existing) {
    _editStatusLiveRegion = existing;
    return _editStatusLiveRegion;
  }
  const region = document.createElement('div');
  region.id = 'pb-edit-status-live';
  region.className = 'pb-sr-only';
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  document.body.appendChild(region);
  _editStatusLiveRegion = region;
  return region;
}

function announceEditStatus(message) {
  if (!message) return;
  const region = ensureEditStatusLiveRegion();
  region.textContent = '';
  requestAnimationFrame(() => {
    region.textContent = message;
  });
}

function normalizeIframeErrorPayload(payload) {
  const rawLine = payload?.line ?? payload?.lineno;
  const rawCol = payload?.col ?? payload?.colno;

  return {
    type: String(payload?.type || 'error'),
    name: payload?.name ? String(payload.name) : '',
    message: String(payload?.message || 'Unknown preview error'),
    source: String(payload?.source || ''),
    line: Number.isFinite(Number(rawLine)) ? Number(rawLine) : 0,
    col: Number.isFinite(Number(rawCol)) ? Number(rawCol) : 0,
    stack: payload?.stack ? String(payload.stack) : '',
    href: payload?.href ? String(payload.href) : '',
    nonce: payload?.nonce ? String(payload.nonce) : ''
  };
}

/**
 * Browser notifications that reach `window.onerror` but are NOT preview
 * failures. They are still logged to the console — only the red toast is
 * withheld.
 *
 * `ResizeObserver loop …` is the one that matters. It is not an error at all:
 * the spec fires it whenever an RO callback causes another resize in the same
 * frame, and the iframe runs two observers that do exactly that by design
 * (`overlay-reporters.js` `_resObs` and `_elementRectResObs` measure elements
 * and write back). So it fired on EVERY boot, and on a phone the toast is tall
 * enough to push the bottom command bar's zoom cluster past the viewport —
 * measured at 390x844: zoom buttons at bottom 887 against innerHeight 844.
 * One unactionable warning was costing a control.
 *
 * `Script error.` is the cross-origin redaction of an error the parent cannot
 * see; the sandbox has no `allow-same-origin`, so template scripts produce it
 * with no message, source or line. A toast reading "Script error." tells a user
 * nothing they can act on.
 *
 * Matched on the MESSAGE, deliberately narrowly. Anything a section's own code
 * throws still surfaces — that is what this path exists for.
 */
const BENIGN_IFRAME_ERROR_RE = /^(ResizeObserver loop\b|Script error\.?$)/i;

export function isBenignIframeError(error) {
  return BENIGN_IFRAME_ERROR_RE.test(String(error?.message || '').trim());
}

function formatIframeErrorLocation(error) {
  const source = String(error?.source || error?.href || '').trim();
  const line = Number.isFinite(Number(error?.line)) ? Number(error.line) : 0;
  const col = Number.isFinite(Number(error?.col)) ? Number(error.col) : 0;
  if (!source) return '';
  if (line && col) return `${source}:${line}:${col}`;
  if (line) return `${source}:${line}`;
  return source;
}

function logIframeErrorToConsole(error) {
  const name = error.name ? ` ${error.name}` : '';
  const location = formatIframeErrorLocation(error);
  const locationSuffix = location ? ` @ ${location}` : '';
  const details = {
    type: error.type,
    name: error.name || undefined,
    message: error.message,
    source: error.source || undefined,
    line: error.line || undefined,
    col: error.col || undefined,
    href: error.href || undefined,
    stack: error.stack || undefined,
    nonce: error.nonce || undefined
  };

  console.warn(
    `[Shell] Preview iframe ${error.type}${name}: ${error.message}${locationSuffix}`,
    details
  );
}

let _renderQueue = [];
let _currentRenderAbortController = null;
let _activeRenderRevision = -1;
let _currentRenderReviewToken = null;
let _agentReviewSession = null;
let _agentReviewChromeRows = [];
let _agentReviewEventsWired = false;
let _agentReviewTestHooks = null;
// The canvas loader is where the 140ms anti-flash rule was tuned; it now comes
// from the shared primitive so every other async panel gets the same behaviour
// instead of re-deriving it. `show` reads the phase state at FIRE time, not at
// arm time, which is why it stays a closure rather than a captured value.
const _renderLoader = createDelayedLoader({
  show: () => {
    if (rendering) showIframeLoading(true, _loaderPhase, _loaderPhaseProgress);
  },
  hide: () => showIframeLoading(false)
});
let _overridePushQueued = false;
let _lastOverridesCSS = null;
let _lastButtonCSS = null;
let _lastFormCSS = null;
let _lastButtonEffectJS = null;
// Phase 11 instrumentation: counts assembleOverridesCSS() calls so the edit-path
// perf test can assert text/html-only edits reassemble override CSS zero times.
let _overrideAssembleCount = 0;
const SECTION_HTML_PATCH_RE = /^pages\.(\d+)\.sections\.(\d+)\.html$/;
const SHARED_HTML_PATCH_RE = /^sharedSections\.(navigation|footer)\.html$/;
const THEME_VAR_PATCH_RE = /^theme\.(--[a-zA-Z0-9_-]+)$/;
const STYLE_OVERRIDE_PATCH_RE =
  /^pages\.(\d+)\.sections\.(\d+)\.overrides(?:$|\.(?:marginTop|marginBottom|paddingTop|paddingBottom|backgroundColor|customCSS|elements(?:\.|$)))/;
const SECTION_OVERRIDE_PATCH_RE = /^pages\.(\d+)\.sections\.(\d+)\.overrides(?:$|\.)/;
const SHARED_OVERRIDE_PATCH_RE = /^sharedSections\.(navigation|footer)\.overrides(?:$|\.)/;
const FONT_PATCH_PATH = 'font';
const HEADING_FONT_PATCH_PATH = 'headingFont';
const HEADING_WEIGHT_PATCH_PATH = 'headingFontWeight';
const BUTTON_STYLES_PATCH_PATH = 'buttonStyles';
const FORM_STYLES_PATCH_PATH = 'formStyles';
const RESYNC_CLIENT_INFO_LABEL = 'Resync sections from Client Info';
const FIND_REPLACE_LABEL = 'Find & Replace';
const RESOLVE_DUPLICATE_EIDS_LABEL = 'Resolve duplicate editable IDs';
const NON_VISUAL_SURGICAL_PATCH_PATHS = new Set(['sourceClientProfile']);
// Reorder labels ('Reorder nav link', 'Reorder header element', …) are added
// dynamically from the adapter registry after registration — see the
// getReorderableHistoryLabels() augmentation in the init path below.
const SECTION_HTML_SURGICAL_LABELS = new Set([
  'Add social link',
  'Delete element',
  'Duplicate element',
  'Move element',
  RESYNC_CLIENT_INFO_LABEL,
  FIND_REPLACE_LABEL,
  RESOLVE_DUPLICATE_EIDS_LABEL
]);
const PREVIEW_SECTION_SYNC_APPLIED = 'applied';
const PREVIEW_SECTION_SYNC_STALE = 'stale';
const PREVIEW_SECTION_SYNC_MISSING = 'missing';
// A section whose scoped script binds listeners cannot be surgically synced:
// replacing its innerHTML orphans those listeners with no teardown hook, so FAQ
// toggles / hamburgers / sliders die. The caller must full-render instead.
const PREVIEW_SECTION_SYNC_NEEDS_FULL_RENDER = 'needs-full-render';

function requiresFullRenderForLabel(label) {
  const value = String(label || '');
  return (
    value === 'Reset section' ||
    // MM-2: fragment merges replace the drawer subtree whose script bound the
    // hamburger listeners — surgical innerHTML sync would orphan them.
    value === MOBILE_MENU_LABELS.apply ||
    value === MOBILE_MENU_LABELS.reset ||
    /^(?:Add|Delete|Remove|Duplicate|Move|Reorder)(?: .+)? section(?:s)?$/.test(value) ||
    /^Swap to .+ variant$/.test(value) ||
    /^Swap (?:navigation|footer) variant$/.test(value) ||
    /^(?:Set|Replaced|Remove) (?:navigation|footer)$/.test(value)
  );
}

function getActivePageIndex(state) {
  if (!state || !Array.isArray(state.pages) || !state.pages.length) return -1;
  return state.pages.findIndex((p) => p.id === state.activePage);
}

function sendSurgicalBridgeUpdate(type, data) {
  bridge.send(type, data);
}

function findPreviewSectionByInstance(state, instanceId) {
  if (!state || !instanceId) return null;
  for (const type of ['navigation', 'footer']) {
    const section = state.sharedSections?.[type];
    if (section?.instanceId === instanceId) return section;
  }

  const page = state.pages?.find?.((p) => p.id === state.activePage) || state.pages?.[0];
  if (!page || !Array.isArray(page.sections)) return null;
  return page.sections.find((section) => section?.instanceId === instanceId) || null;
}

function collectPreviewOverrideSections(state) {
  if (!state) return [];
  const sections = [];
  if (state.sharedSections?.navigation) sections.push(state.sharedSections.navigation);
  const page = state.pages?.find?.((p) => p.id === state.activePage) || state.pages?.[0];
  if (page && Array.isArray(page.sections)) sections.push(...page.sections);
  if (state.sharedSections?.footer) sections.push(state.sharedSections.footer);
  return sections;
}

async function pushPreviewSectionHTML(instanceId, state = store.getState(), label = null) {
  const section = findPreviewSectionByInstance(state, instanceId);
  if (!section) return PREVIEW_SECTION_SYNC_MISSING;
  // S2: sections that carry a RUNTIME must take a full render across EVERY sync
  // path (this is the choke point for both the mutation-surgical loop and the
  // direct preview:sync-section-html event). A surgical innerHTML swap would
  // strip the listeners without re-running mount, killing FAQ / hamburger /
  // slider interactivity. Mirrors the guard in
  // tryApplySectionHtmlSurgicalSync but also covers element paste/cut/delete/move.
  //
  // AG-4: `sectionHasRuntime`, not `section.js`. A behaviour-stamped section can
  // have EMPTY js — the runtime lives in data/behaviours/ and is attached from
  // the stamp — so the old test read it as inert, took the surgical path, and
  // replaced the markup under a bound runtime. Nothing would report it: the
  // section would render correctly and simply stop responding to clicks.
  if (sectionHasRuntime(section)) return PREVIEW_SECTION_SYNC_NEEDS_FULL_RENDER;
  const revision = state === store.getState() ? _normalizeRenderRevision(store?._revision) : null;
  const html = await buildPreviewSectionInnerHTML(section, state, {
    offThreadAssets: state?.flags?.offThreadAssets === true
  });
  if (revision !== null && revision !== _normalizeRenderRevision(store?._revision)) {
    return PREVIEW_SECTION_SYNC_STALE;
  }
  // Phase 20: `label` is forwarded to the iframe-side View Transition gate.
  // When undefined/null (most existing callers), the iframe falls through to
  // direct mutation. Only reorder labels trigger the morph.
  sendSurgicalBridgeUpdate('sync-section-html', {
    instanceId,
    html,
    label: label || null
  });
  // Phase 11: override CSS is derived from state.overrides, not section HTML, so
  // an HTML-only sync never changes it. The override push is now the caller's
  // responsibility (fired only when an override-affecting patch was present) so
  // element edits / variant swaps / image replaces stop reassembling all-section
  // override CSS on every keystroke-adjacent mutation.
  return PREVIEW_SECTION_SYNC_APPLIED;
}

function flushOverridePush() {
  _overridePushQueued = false;
  const state = store.getState();
  if (!state) return;
  const page = state.pages.find((p) => p.id === state.activePage) || state.pages[0];
  if (!page) return;

  _overrideAssembleCount++;
  const overridesCSS = assembleOverridesCSS(collectPreviewOverrideSections(state));
  if (overridesCSS !== _lastOverridesCSS) {
    sendSurgicalBridgeUpdate('update-overrides', { css: overridesCSS });
    _lastOverridesCSS = overridesCSS;
  }

  const buttonCSS = assembleButtonCSS(state.buttonStyles, getLastRenderedButtonSelector());
  if (buttonCSS !== _lastButtonCSS) {
    sendSurgicalBridgeUpdate('update-button-styles', { css: buttonCSS });
    _lastButtonCSS = buttonCSS;
  }

  const formCSS = assembleFormCSS(state.formStyles);
  if (formCSS !== _lastFormCSS) {
    sendSurgicalBridgeUpdate('update-form-styles', { css: formCSS });
    _lastFormCSS = formCSS;
  }

  const buttonEffectJS = getButtonEffectJS(state, getLastRenderedButtonSelector());
  if (buttonEffectJS !== _lastButtonEffectJS) {
    sendSurgicalBridgeUpdate('update-button-effect-js', { js: buttonEffectJS });
    _lastButtonEffectJS = buttonEffectJS;
  }

  // Keep overlay geometry in sync after live override updates (no full rerender).
  bridge.send('query-section-positions', {});
  // Keep glass handles/panels synced to latest selected editable geometry.
  bridge.send('query-element-rect', {});
}

function queueOverridePush() {
  if (_overridePushQueued) return;
  _overridePushQueued = true;
  requestAnimationFrame(flushOverridePush);
}

// Phase 11: debounced, undo-safe session GC for orphaned asset blobs.
// Image replace/remove mints a new assetId and orphans the old one; without this
// the orphan only gets collected at the next load-time gc(). CRITICAL: history is
// diff-based, so a replaced image's OLD pb-asset:// id lives in an undo `reverse`
// patch — the ref scan MUST include history.entries or Cmd+Z would restore a dead
// reference. Deleting only unreferenced blobs, debounced so bursts collapse.
const ASSET_GC_REF_RE = /pb-asset:\/\/([a-f0-9-]+)/g;
const ASSET_GC_DEBOUNCE_MS = 4000;
let _assetGcTimer = null;

async function collectSessionAssetRefs({ listRevisions = () => revisionStore.list() } = {}) {
  const ids = new Set();
  const scan = (value) => {
    if (value == null) return;
    let json;
    try {
      json = JSON.stringify(value);
    } catch {
      return;
    }
    if (!json) return;
    ASSET_GC_REF_RE.lastIndex = 0;
    let match;
    while ((match = ASSET_GC_REF_RE.exec(json)) !== null) ids.add(match[1]);
  };
  scan(store.getState());
  scan(store.history?.entries);
  const revisions = await listRevisions();
  for (const revision of revisions || []) scan(revision?.state);
  return ids;
}

async function runSessionAssetGC() {
  _assetGcTimer = null;
  try {
    await store.assets.gc(await collectSessionAssetRefs());
  } catch (err) {
    reportError('AssetGC', err, { user: 'silent', detail: 'session asset gc' });
  }
}

function scheduleSessionAssetGC() {
  if (_assetGcTimer) clearTimeout(_assetGcTimer);
  _assetGcTimer = setTimeout(runSessionAssetGC, ASSET_GC_DEBOUNCE_MS);
}

async function handlePreviewSyncSectionHTML({
  instanceId,
  preserveScroll = true,
  label = null,
  syncOverrides = true
} = {}) {
  try {
    const result = await pushPreviewSectionHTML(instanceId, undefined, label);
    if (result === PREVIEW_SECTION_SYNC_APPLIED) {
      // Structural syncs with no patch context keep the default safe override
      // push. Known HTML-only callers pass syncOverrides:false to avoid the
      // Phase 11 edit-path regression this handler used to reintroduce.
      if (syncOverrides !== false) queueOverridePush();
      return true;
    }
    // The state advanced while this async section build was resolving assets.
    // The newer revision owns preview synchronization; a full render here would
    // repaint stale work and flash the loading overlay.
    if (result === PREVIEW_SECTION_SYNC_STALE) return true;

    // JS-bearing sections (NEEDS_FULL_RENDER) and missing/failed lookups fall
    // through to a correctness-first full render — a surgical innerHTML swap
    // would orphan the section script's listeners. This also fires for element
    // paste / cut / delete / move / reorder inside an FAQ or hamburger nav.
    if (!preserveScroll) bridge.clearScroll();
    await renderPreview({ preserveScroll, transition: null, hideDuringLoad: true });
    return false;
  } catch (err) {
    reportError('Preview section sync', err, {
      user: 'toast',
      message: 'Could not update the preview. Refreshing it now.',
      detail: `section sync failed for ${instanceId || 'unknown section'}`,
      id: 'preview-section-sync-failed'
    });
    if (!preserveScroll) bridge.clearScroll();
    await renderPreview({ preserveScroll, transition: null, hideDuringLoad: true });
    return false;
  }
}

function tryApplyCssSurgicalSync({ patches, state, activePageIndex, oldState = null }) {
  const themeVarsToUpdate = new Set();
  let updateFont = false;
  let updateHeadingFont = false;
  let updateOverrides = false;
  let updateButtons = false;
  let updateForms = false;
  let hasVisibleImpact = false;

  for (const patch of patches) {
    if (!patch || patch.op !== 'replace' || typeof patch.path !== 'string') return false;

    const themeMatch = patch.path.match(THEME_VAR_PATCH_RE);
    if (themeMatch) {
      themeVarsToUpdate.add(themeMatch[1]);
      hasVisibleImpact = true;
      continue;
    }

    if (patch.path === FONT_PATCH_PATH) {
      updateFont = true;
      hasVisibleImpact = true;
      continue;
    }

    if (patch.path === HEADING_FONT_PATCH_PATH || patch.path === HEADING_WEIGHT_PATCH_PATH) {
      updateHeadingFont = true;
      hasVisibleImpact = true;
      continue;
    }

    if (patch.path === BUTTON_STYLES_PATCH_PATH) {
      // When the first button edit flips hasButtonRootRules false→true (or a
      // reset flips it back), the strip decision changes: a surgical override
      // push would leave stale, unstripped section button rules in the iframe.
      // Bail to a full re-render so the sections get re-stripped in lockstep.
      if (hasButtonRootRules(oldState?.buttonStyles) !== hasButtonRootRules(state.buttonStyles)) {
        return false;
      }
      updateButtons = true;
      hasVisibleImpact = true;
      continue;
    }

    if (patch.path === FORM_STYLES_PATCH_PATH) {
      updateForms = true;
      hasVisibleImpact = true;
      continue;
    }

    const overridesMatch = patch.path.match(STYLE_OVERRIDE_PATCH_RE);
    if (overridesMatch) {
      const pageIndex = Number.parseInt(overridesMatch[1], 10);
      if (!Number.isInteger(pageIndex)) return false;
      if (pageIndex === activePageIndex) {
        updateOverrides = true;
        hasVisibleImpact = true;
      }
      continue;
    }

    const sharedOverrideMatch = patch.path.match(SHARED_OVERRIDE_PATCH_RE);
    if (sharedOverrideMatch) {
      updateOverrides = true;
      hasVisibleImpact = true;
      continue;
    }

    return false;
  }

  if (!hasVisibleImpact) return true;

  const theme = state.theme || {};
  for (const varName of themeVarsToUpdate) {
    sendSurgicalBridgeUpdate('update-theme-var', { name: varName, value: theme[varName] ?? '' });
  }

  if (updateFont) {
    sendSurgicalBridgeUpdate('update-font', buildFontUpdatePayload(state.font || 'Roboto'));
  }

  if (updateHeadingFont) {
    sendSurgicalBridgeUpdate(
      'update-heading-font',
      buildFontUpdatePayload(state.headingFont ?? state.font ?? 'Roboto', {
        weight: state.headingFontWeight || '700'
      })
    );
  }

  if (updateOverrides) {
    sendSurgicalBridgeUpdate('update-overrides', {
      css: assembleOverridesCSS(collectPreviewOverrideSections(state))
    });
  }

  if (updateButtons) {
    const buttonSelector = getLastRenderedButtonSelector();
    sendSurgicalBridgeUpdate('update-button-styles', {
      css: assembleButtonCSS(state.buttonStyles, buttonSelector)
    });
    sendSurgicalBridgeUpdate('update-button-effect-js', {
      js: getButtonEffectJS(state, buttonSelector)
    });
  }

  if (updateForms) {
    sendSurgicalBridgeUpdate('update-form-styles', { css: assembleFormCSS(state.formStyles) });
  }

  // Theme/font/override/button/form changes can shift layout metrics.
  bridge.send('query-section-positions', {});
  bridge.send('query-element-rect', {});
  return true;
}

async function tryApplySectionHtmlSurgicalSync({
  source,
  label,
  patches,
  oldState: _oldState = null,
  newState = null
}) {
  if (source !== 'undo' && source !== 'redo' && source !== 'mutation') return false;
  if (!Array.isArray(patches) || patches.length === 0) return false;
  const isWhitelisted = SECTION_HTML_SURGICAL_LABELS.has(label);
  if (requiresFullRenderForLabel(label)) return false;

  const allPatchesAreSyncableShape = patches.every(
    (patch) =>
      patch &&
      patch.op === 'replace' &&
      typeof patch.path === 'string' &&
      (SECTION_HTML_PATCH_RE.test(patch.path) ||
        SHARED_HTML_PATCH_RE.test(patch.path) ||
        SECTION_OVERRIDE_PATCH_RE.test(patch.path) ||
        SHARED_OVERRIDE_PATCH_RE.test(patch.path) ||
        NON_VISUAL_SURGICAL_PATCH_PATHS.has(patch.path))
  );
  if (!isWhitelisted && !allPatchesAreSyncableShape) return false;

  const state = newState || store.getState();
  if (!state || !Array.isArray(state.pages) || !state.pages.length) return false;
  const activePageIndex = getActivePageIndex(state);
  if (activePageIndex === -1) return false;

  const affectedInstanceIds = new Set();
  let sawRelevantPatch = false;
  let sawHtmlPatch = false;
  let sawOverridePatch = false;

  for (const patch of patches) {
    if (!patch || patch.op !== 'replace' || typeof patch.path !== 'string') return false;

    const pageHtmlMatch = patch.path.match(SECTION_HTML_PATCH_RE);
    if (pageHtmlMatch) {
      const pageIndex = Number.parseInt(pageHtmlMatch[1], 10);
      const sectionIndex = Number.parseInt(pageHtmlMatch[2], 10);
      if (!Number.isInteger(pageIndex) || !Number.isInteger(sectionIndex)) return false;
      if (pageIndex === activePageIndex) {
        const section = state.pages[pageIndex]?.sections?.[sectionIndex];
        if (!section?.instanceId) return false;
        // Replacing innerHTML destroys listeners attached by the section's
        // scoped script OR by a behaviour runtime (AG-4: a stamped section can
        // carry empty js). A normal render recreates both DOM and runtime
        // without stacking document/window listeners from scripts that lack
        // teardown.
        if (sectionHasRuntime(section)) return false;
        affectedInstanceIds.add(section.instanceId);
        sawRelevantPatch = true;
        sawHtmlPatch = true;
      }
      continue;
    }

    const sharedHtmlMatch = patch.path.match(SHARED_HTML_PATCH_RE);
    if (sharedHtmlMatch) {
      const section = state.sharedSections?.[sharedHtmlMatch[1]];
      if (!section?.instanceId) return false;
      if (sectionHasRuntime(section)) return false;
      affectedInstanceIds.add(section.instanceId);
      sawRelevantPatch = true;
      sawHtmlPatch = true;
      continue;
    }

    const pageOverrideMatch = patch.path.match(SECTION_OVERRIDE_PATCH_RE);
    if (pageOverrideMatch) {
      const pageIndex = Number.parseInt(pageOverrideMatch[1], 10);
      const sectionIndex = Number.parseInt(pageOverrideMatch[2], 10);
      if (!Number.isInteger(pageIndex) || !Number.isInteger(sectionIndex)) return false;
      if (pageIndex === activePageIndex) {
        const section = state.pages[pageIndex]?.sections?.[sectionIndex];
        if (section?.instanceId) affectedInstanceIds.add(section.instanceId);
        sawRelevantPatch = true;
        sawOverridePatch = true;
      }
      continue;
    }

    const sharedOverrideMatch = patch.path.match(SHARED_OVERRIDE_PATCH_RE);
    if (sharedOverrideMatch) {
      const section = state.sharedSections?.[sharedOverrideMatch[1]];
      if (section?.instanceId) affectedInstanceIds.add(section.instanceId);
      sawRelevantPatch = true;
      sawOverridePatch = true;
      continue;
    }

    if (NON_VISUAL_SURGICAL_PATCH_PATHS.has(patch.path)) {
      continue;
    }

    return false;
  }

  if (!sawHtmlPatch) {
    if (isWhitelisted && sawOverridePatch) queueOverridePush();
    return isWhitelisted;
  }
  if (!sawRelevantPatch) return true;

  for (const instanceId of affectedInstanceIds) {
    const result = await pushPreviewSectionHTML(instanceId, state, label);
    if (result === PREVIEW_SECTION_SYNC_MISSING) return false;
    // Defense in depth: this loop already skips JS-bearing sections above
    // (returns false before reaching here), but if one slips through, refuse the
    // surgical path so the caller full-renders rather than orphaning listeners.
    if (result === PREVIEW_SECTION_SYNC_NEEDS_FULL_RENDER) return false;
    if (result === PREVIEW_SECTION_SYNC_STALE) return true;
  }

  // Phase 11: reassemble override CSS only when an override-affecting patch was
  // actually present. HTML-only surgical syncs (element edits, variant swaps,
  // image replaces) no longer trigger a full-catalog override reassembly.
  if (sawOverridePatch) {
    queueOverridePush();
  }
  return true;
}

function _isSurgicalSyncDebugEnabled() {
  return typeof window !== 'undefined' && window.PB_DEBUG_SURGICAL === true;
}

function _logSurgicalMiss(reason, { source, label, patches } = {}) {
  if (!_isSurgicalSyncDebugEnabled()) return;
  const paths = (patches || []).map((p) => p?.path).slice(0, 6);
  console.warn('[surgical-miss]', { reason, source, label, paths });
}

async function _tryApplySurgicalSyncInner({
  source,
  label,
  patches,
  oldState = null,
  newState = null
}) {
  if (source !== 'undo' && source !== 'redo' && source !== 'mutation') return false;
  if (!Array.isArray(patches) || patches.length === 0) return false;

  const state = newState || store.getState();
  if (!state || !Array.isArray(state.pages) || !state.pages.length) return false;
  const activePageIndex = getActivePageIndex(state);
  if (activePageIndex === -1) return false;

  if (label === 'Edit text') {
    if (!isTextOnlyDiff(patches, oldState, newState)) return false;

    const updates = new Map(); // instanceId -> html

    for (const patch of patches) {
      if (!patch || patch.op !== 'replace' || typeof patch.path !== 'string') return false;

      const pageMatch = patch.path.match(SECTION_HTML_PATCH_RE);
      if (pageMatch) {
        const pageIndex = Number.parseInt(pageMatch[1], 10);
        const sectionIndex = Number.parseInt(pageMatch[2], 10);
        if (!Number.isInteger(pageIndex) || !Number.isInteger(sectionIndex)) return false;

        // Off-page text changes don't affect current preview.
        if (pageIndex !== activePageIndex) continue;

        const section = state.pages[pageIndex]?.sections?.[sectionIndex];
        if (!section || typeof section.instanceId !== 'string' || typeof section.html !== 'string')
          return false;
        updates.set(section.instanceId, section.html);
        continue;
      }

      const sharedMatch = patch.path.match(SHARED_HTML_PATCH_RE);
      if (sharedMatch) {
        const sharedType = sharedMatch[1];
        const sharedSection = state.sharedSections?.[sharedType];
        if (
          !sharedSection ||
          typeof sharedSection.instanceId !== 'string' ||
          typeof sharedSection.html !== 'string'
        )
          return false;
        updates.set(sharedSection.instanceId, sharedSection.html);
        continue;
      }

      // Unknown patch shape: fall back to full render.
      return false;
    }

    // No visible section was affected on the active page.
    if (updates.size === 0) return true;

    for (const [instanceId, html] of updates) {
      sendSurgicalBridgeUpdate('sync-section-text-html', { instanceId, html, label });
    }
    // Keep glass overlays/panels synchronized with any text-size/layout changes.
    bridge.send('query-section-positions', {});
    bridge.send('query-element-rect', {});
    return true;
  }

  if (await tryApplySectionHtmlSurgicalSync({ source, label, patches, oldState, newState })) {
    return true;
  }

  return tryApplyCssSurgicalSync({ patches, state, activePageIndex, oldState });
}

async function tryApplySurgicalSync(args = {}) {
  if (typeof window !== 'undefined' && window.PB_FORCE_FULL_RENDER === true) return false;
  try {
    const ok = await _tryApplySurgicalSyncInner(args);
    if (!ok) _logSurgicalMiss('full-render-fallback', args);
    return ok;
  } catch (err) {
    reportError('Preview surgical sync', err, {
      user: 'silent',
      detail: `incremental preview update failed for ${args.label || 'unlabelled change'}; using full render`
    });
    return false;
  }
}

function _normalizeRenderRevision(value = store?._revision) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.trunc(parsed);
}

function _cloneRenderRequest(opts = {}) {
  return {
    preserveScroll: opts.preserveScroll !== false,
    transition: opts.transition ?? null,
    hideDuringLoad: !!opts.hideDuringLoad,
    source: opts.source || 'direct',
    label: opts.label || '',
    patches: Array.isArray(opts.patches) ? opts.patches : null,
    oldState: opts.oldState || null,
    newState: opts.newState || null,
    review: opts.review
      ? {
          detachedState: opts.review.detachedState,
          assetSource: opts.review.assetSource || null,
          pageId: opts.review.pageId,
          reviewToken: opts.review.reviewToken,
          side: opts.review.side
        }
      : null
  };
}

function _getHighestQueuedRenderEntry() {
  if (!_renderQueue.length) return null;
  let highest = _renderQueue[0];
  for (let i = 1; i < _renderQueue.length; i++) {
    const candidate = _renderQueue[i];
    if ((candidate.revision || 0) >= (highest.revision || 0)) {
      highest = candidate;
    }
  }
  return highest;
}

function _dequeueHighestQueuedRenderEntry() {
  const highest = _getHighestQueuedRenderEntry();
  if (!highest) return null;
  _renderQueue = _renderQueue.filter((entry) => (entry.revision || 0) > (highest.revision || 0));
  return highest;
}

function _queueRenderRequest(opts, revision = _normalizeRenderRevision()) {
  const normalizedRevision = _normalizeRenderRevision(revision);
  if (_renderQueue.some((entry) => (entry.revision || 0) >= normalizedRevision)) {
    return { queued: false, revision: normalizedRevision };
  }

  _renderQueue.push({
    opts: _cloneRenderRequest(opts),
    revision: normalizedRevision
  });
  return { queued: true, revision: normalizedRevision };
}

function _abortActiveRenderIfSuperseded(incomingRevision) {
  if (!rendering) return;
  if (!_currentRenderAbortController || _currentRenderAbortController.signal.aborted) return;
  if (_normalizeRenderRevision(incomingRevision) <= _normalizeRenderRevision(_activeRenderRevision))
    return;
  _currentRenderAbortController.abort();
}

async function flushPendingEdit(editSessionId) {
  if (!bridge?.nonce) return false;
  return await new Promise((resolve) => {
    let settled = false;
    const off = eventBus.on('iframe:flush-pending-edit-ack', (data) => {
      if (
        typeof editSessionId === 'number' &&
        typeof data?.editSessionId === 'number' &&
        data.editSessionId !== editSessionId
      ) {
        return;
      }
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      off();
      resolve(true);
    });
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      off();
      resolve(false);
    }, 50);
    bridge.send('flush-pending-edit', { editSessionId });
  });
}

function _logAgentReviewRender(reason, detail = {}) {
  if (!_isSurgicalSyncDebugEnabled()) return;
  console.warn('[agent-review-render]', { reason, ...detail });
}

/**
 * HR-2 — the detached-preview seam, parameterised by OWNER.
 *
 * AR-5 built one machine for "show the canvas a state that is not the store's":
 * flush the active edit, hold a non-destructive write blocker, force a
 * token-guarded double-buffered render, restore live state on every exit. The
 * History Rail needs exactly that machine, so it gets it — by adding an `owner`
 * to the session, NOT by renaming anything. `agent:review-*` event names,
 * `wireAgentReviewEvents`, `_agentReviewSession` and `AGENT_REVIEW_BASELINE_SHA`
 * all stay put so `agent-census.js` and the agent frame-vocabulary gate do not
 * move, and `npm run test:agent` must stay at its pre-HR-2 21/21.
 *
 * The two owners are MUTUALLY EXCLUSIVE and the exclusion is a property of this
 * seam rather than an agreement between callers: a request from either owner
 * supersedes whatever is on the canvas, because there is one canvas.
 */
const PREVIEW_OWNERS = Object.freeze({
  agent: WRITE_BLOCKER.AGENT_REVIEW,
  history: WRITE_BLOCKER.DETACHED_PREVIEW
});

function _previewBlockerFor(owner) {
  return PREVIEW_OWNERS[owner] || WRITE_BLOCKER.AGENT_REVIEW;
}

/** Whichever detached-preview blocker is currently held, or null. */
function _heldPreviewBlocker() {
  for (const blocker of Object.values(PREVIEW_OWNERS)) {
    if (store.hasBlocker(blocker)) return blocker;
  }
  return null;
}

function _isAgentReviewTokenCurrent(reviewToken, projectId = null) {
  if (!_agentReviewSession || _agentReviewSession.reviewToken !== reviewToken) return false;
  const expectedProjectId = projectId || _agentReviewSession.projectId;
  return !expectedProjectId || store.getState()?.projectId === expectedProjectId;
}

function _prepareAgentReviewRequest(payload = {}) {
  const owner = payload.owner === 'history' ? 'history' : 'agent';
  const side = payload.side === 'after' ? 'after' : payload.side === 'before' ? 'before' : null;
  const reviewToken = String(payload.reviewToken || '').trim();
  if (!side || !reviewToken) throw new Error('Review render requires a side and review token');
  if (side === 'after') return { side, reviewToken, owner };
  if (!payload.detachedState || typeof payload.detachedState !== 'object') {
    throw new Error('Before review requires detached project state');
  }
  if (!payload.assetSource || typeof payload.assetSource.get !== 'function') {
    throw new Error('Before review requires its version bundle asset source');
  }

  // Defensive idempotence: the panel's readProjectBundle already canonicalizes,
  // but the shell is the render boundary and refuses phantom pre-migration DOM
  // identity deltas even if a future intent producer bypasses that reader.
  const detachedState = structuredClone(payload.detachedState);
  canonicalizeLoadedState(detachedState);
  const requestedPageId = String(payload.pageId || detachedState.activePage || '').trim();
  const hasRequested =
    requestedPageId && detachedState.pages?.some((page) => page.id === requestedPageId);

  // HR-2 — "the page that did not exist yet".
  //
  // Agent review compares two states of the SAME turn, so a missing page is a
  // real inconsistency and throwing is right. History does not have that
  // guarantee: a version from before the About page was created legitimately has
  // no About page, and surfacing that as an error would make old versions read
  // as broken. So for the history owner a missing page RESOLVES to the version's
  // own first page and reports what it did, and the rail says so in the review
  // bar. Scoped by owner, so agent behaviour is byte-identical.
  let pageId = requestedPageId;
  let pageFallback = null;
  if (!hasRequested) {
    if (owner !== 'history') {
      throw new Error('The requested Before page is not present in that version');
    }
    const substitute = detachedState.pages?.[0]?.id;
    if (!substitute) throw new Error('That version has no pages to show');
    pageFallback = {
      requested: requestedPageId || null,
      used: substitute,
      // The NAME, not just the id: the review bar has to say "showing Home",
      // and only this side of the seam can see the version's own page list.
      usedName: detachedState.pages.find((page) => page.id === substitute)?.name || null
    };
    pageId = substitute;
  }
  detachedState.activePage = pageId;
  const projectId = String(detachedState.projectId || '').trim();
  if (!projectId || projectId !== store.getState()?.projectId) {
    throw new Error('The requested Before version belongs to a different project');
  }
  return {
    detachedState,
    assetSource: payload.assetSource,
    pageId,
    pageFallback,
    projectId,
    reviewToken,
    side,
    owner
  };
}

function releaseAgentReview(reason = 'cancelled', { preserveWriteBlocker = false } = {}) {
  const previous = _agentReviewSession;
  // HR-2: release whichever preview blocker is actually held. Hardcoding
  // AGENT_REVIEW here would leak the history blocker on every exit path — a
  // read-only editor with no way back, which is the worst failure this seam has.
  const heldBlocker = _heldPreviewBlocker();
  const heldWriteBlocker = heldBlocker !== null;
  _agentReviewSession = null;
  _renderQueue = _renderQueue.filter((entry) => !entry?.opts?.review);
  if (
    previous &&
    _currentRenderReviewToken === previous.reviewToken &&
    _currentRenderAbortController &&
    !_currentRenderAbortController.signal.aborted
  ) {
    _currentRenderAbortController.abort();
  }
  _activeRenderVisualToken += 1;
  if (_doubleBufferSwapCancel) _doubleBufferSwapCancel();
  if (heldWriteBlocker && !preserveWriteBlocker) {
    store.removeBlocker(heldBlocker);
  }
  if (previous || heldWriteBlocker) {
    syncIframeInteractionMode();
    eventBus.emit('agent:review-state-changed', {
      active: false,
      reason,
      reviewToken: previous?.reviewToken || null,
      side: 'after'
    });
    _logAgentReviewRender(reason, { reviewToken: previous?.reviewToken || null, side: 'after' });
  }
  return !!(previous || heldWriteBlocker);
}

async function exitAgentReview(reason = 'close', { reviewToken = null, restoreAfter = true } = {}) {
  if (
    reviewToken &&
    _agentReviewSession &&
    String(reviewToken) !== _agentReviewSession.reviewToken
  ) {
    _logAgentReviewRender('stale-exit-ignored', { reason, reviewToken });
    return false;
  }
  const released = releaseAgentReview(reason);
  if (!released || !restoreAfter || !previewIframe) return released;
  await renderPreview({
    preserveScroll: true,
    transition: null,
    hideDuringLoad: false,
    source: 'agent-review-exit',
    label: `Agent review ${reason}`,
    _agentReviewExit: true
  });
  return true;
}

function _queueAgentReviewRenderRequest(review) {
  _renderQueue = _renderQueue.filter((entry) => !entry?.opts?.review);
  _renderQueue.push({
    opts: _cloneRenderRequest({
      preserveScroll: true,
      hideDuringLoad: true,
      source: 'agent-review',
      label: 'Agent review Before',
      review
    }),
    revision: _normalizeRenderRevision(store?._revision)
  });
  if (_currentRenderAbortController && !_currentRenderAbortController.signal.aborted) {
    _currentRenderAbortController.abort();
  }
}

async function requestAgentReviewRender(payload = {}) {
  const request = _prepareAgentReviewRequest(payload);
  if (request.side === 'after') {
    return exitAgentReview('after-request', { reviewToken: request.reviewToken });
  }

  // Already previewing — from EITHER owner — is not "read-only", it is this
  // seam's own state, and a handoff between owners must not be refused as one.
  const existingReviewOnly = _heldPreviewBlocker() !== null && store.getBlockers().length === 1;
  if (!store.isWritable() && !existingReviewOnly) {
    throw new Error('A detached preview cannot start while the project is read-only');
  }

  // A newer Before intent supersedes the old one without flashing through
  // After. The write blocker stays held across that handoff — but only when the
  // OWNER is unchanged: an agent→history handoff has to swap which blocker is
  // held, and preserving the old one would leak it.
  const sameOwner = _agentReviewSession?.owner === request.owner;
  releaseAgentReview('superseded', { preserveWriteBlocker: sameOwner });
  _agentReviewSession = {
    reviewToken: request.reviewToken,
    projectId: request.projectId,
    pageId: request.pageId,
    pageFallback: request.pageFallback || null,
    owner: request.owner,
    side: 'before',
    status: 'flushing'
  };

  try {
    const activeEdit =
      typeof _agentReviewTestHooks?.getActiveEdit === 'function'
        ? _agentReviewTestHooks.getActiveEdit()
        : getActiveEdit();
    if (activeEdit) {
      const flush =
        typeof _agentReviewTestHooks?.flushPendingEdit === 'function'
          ? _agentReviewTestHooks.flushPendingEdit
          : flushPendingEdit;
      const flushed = await flush(activeEdit.editSessionId);
      if (!flushed) throw new Error('The active text edit could not be flushed for review');
    }
    if (!_isAgentReviewTokenCurrent(request.reviewToken, request.projectId)) return false;

    store.addBlocker(_previewBlockerFor(request.owner));
    syncIframeInteractionMode();
    _agentReviewSession.status = 'rendering';
    eventBus.emit('agent:review-state-changed', {
      active: true,
      reviewToken: request.reviewToken,
      side: 'before',
      pageId: request.pageId,
      owner: request.owner,
      pageFallback: request.pageFallback || null
    });
    _queueAgentReviewRenderRequest(request);
    await _drainRenderQueue();
    return _isAgentReviewTokenCurrent(request.reviewToken, request.projectId);
  } catch (error) {
    if (_isAgentReviewTokenCurrent(request.reviewToken, request.projectId)) {
      await exitAgentReview('failure');
    }
    throw error;
  }
}

/**
 * The page the CANVAS is showing, which is not always the store's active page.
 *
 * During a detached preview the canvas renders the detached state's page, and
 * changed-section chrome rows are keyed to THAT page. Filtering them against
 * `store.getActivePage()` drops every row whenever the two ids differ — which
 * they routinely do when previewing an older version, because page ids are per
 * project and the live store may be sitting on a different page entirely.
 * (Found in HR-4's browser pass: the live store was on `page-1` while the
 * rendered version's page was `home`, and both correct rows were discarded.)
 */
function _renderedPageId() {
  return _agentReviewSession?.pageId || store.getActivePage()?.id;
}

function wireAgentReviewEvents() {
  if (_agentReviewEventsWired) return;
  _agentReviewEventsWired = true;
  eventBus.on('agent:review-render-requested', (payload) => {
    void requestAgentReviewRender(payload).catch((error) => {
      reportError('Agent review render', error, {
        user: 'status',
        message: 'Could not show that version on the canvas',
        detail: error?.message || 'review render failed'
      });
    });
  });
  eventBus.on('agent:review-close-requested', ({ reviewToken, reason = 'close' } = {}) => {
    void exitAgentReview(reason, { reviewToken });
  });
  // HR-2 — owner-neutral aliases onto the same machine. The History Rail emits
  // these rather than importing shell.js, which would close an import cycle
  // (shell imports the rail); and rather than emitting `agent:review-*`, which
  // would read as the agent doing something it is not.
  eventBus.on('preview:detached-requested', (payload = {}) => {
    void requestAgentReviewRender({
      ...payload,
      side: 'before',
      owner: payload.owner || 'history',
      reviewToken: payload.previewToken || payload.reviewToken
    }).catch((error) => {
      reportError('Version preview', error, {
        user: 'status',
        message: 'Could not show that version on the canvas',
        detail: error?.message || 'detached preview failed'
      });
    });
  });
  eventBus.on('preview:detached-close-requested', ({ previewToken, reason = 'close' } = {}) => {
    void exitAgentReview(reason, { reviewToken: previewToken });
  });
  // HR-4 — owner-neutral alias for AR-6's changed-section chrome. Same rows,
  // same full-replacement semantics, same bridge-ready replay; only the name is
  // neutral, because the History Rail highlighting what a version changed is
  // not the agent doing anything. `_agentReviewChromeRows` is deliberately the
  // SAME slot: there is one canvas, the two owners are mutually exclusive, and
  // two slots would let a stale set from one outlive the other.
  eventBus.on('preview:changed-sections', ({ active = false, rows = [] } = {}) => {
    eventBus.emit('agent:review-chrome-changed', { active, rows });
  });
  eventBus.on('agent:review-chrome-changed', ({ active = false, rows = [] } = {}) => {
    _agentReviewChromeRows = active && Array.isArray(rows) ? rows : [];
    const activePageId = _renderedPageId();
    bridge.send('set-changed-sections', {
      rows: _agentReviewChromeRows.filter(
        (row) => row?.pageId === 'shared' || !row?.pageId || row.pageId === activePageId
      )
    });
  });
  // A replacement iframe has no runtime attrs. Replay the complete current
  // set, including an empty set on teardown, exactly like collab locks.
  eventBus.on('iframe:bridge-ready', () => {
    const activePageId = _renderedPageId();
    bridge.send('set-changed-sections', {
      rows: _agentReviewChromeRows.filter(
        (row) => row?.pageId === 'shared' || !row?.pageId || row.pageId === activePageId
      )
    });
  });
  // Inspect-only row hover mirrors context-chip hover: point and flash only on
  // the active page, never turn a hover gesture into navigation.
  eventBus.on('agent:change-row-hovered', ({ instanceId, pageId } = {}) => {
    if (!instanceId) return;
    if (pageId !== 'shared' && pageId !== store.getActivePage()?.id) return;
    bridge.send('scroll-to-eid', { instanceId });
    bridge.send('flash-section', { instanceId });
  });
  // Activation is navigation, unlike hover. Page switching settles first, then
  // the canonical section:landed seam owns selection and scroll-after-render.
  eventBus.on('agent:review-view-on-page', async ({ instanceId, pageId } = {}) => {
    if (!instanceId) return;
    if (pageId && pageId !== 'shared' && pageId !== store.getActivePage()?.id) {
      await pageManager.setActivePage(pageId);
      if (store.getActivePage()?.id !== pageId) return;
    }
    eventBus.emit('section:landed', { instanceId });
  });
  eventBus.on('project:restored', () => {
    void exitAgentReview('project-change');
  });
  eventBus.on('state-changed', (state) => {
    if (!_agentReviewSession || state?.projectId === _agentReviewSession.projectId) return;
    void exitAgentReview('project-change');
  });
  window.addEventListener(
    'keydown',
    (event) => {
      if (!_agentReviewSession || event.key !== 'Escape' || event.defaultPrevented) return;
      // The canvas popover owns Escape while present; its focus trap closes
      // only that surface, restores the Review button, and emits review close.
      if (document.querySelector('[data-agent-review-popover]')) return;
      // HR-2: the History Rail owns Escape LAYERING (previewing → browsing →
      // closed, contract §1). This handler is capture-phase and would collapse
      // both steps into one, discarding the user's position in the timeline.
      if (_agentReviewSession.owner === 'history') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void exitAgentReview('escape');
    },
    true
  );
}

/**
 * Render the main preview iframe.
 * Use `preserveScroll=true` for in-page edits, and `false` when content scope changes
 * (page switch/import/template/reset) so stale scroll positions are not restored.
 */
async function _runQueuedRender(entry) {
  const request = _cloneRenderRequest(entry?.opts || {});
  const reviewRequest = request.review;
  if (
    reviewRequest &&
    !_isAgentReviewTokenCurrent(reviewRequest.reviewToken, reviewRequest.detachedState?.projectId)
  ) {
    _logAgentReviewRender('stale-queued-request', {
      reviewToken: reviewRequest.reviewToken,
      side: reviewRequest.side
    });
    return;
  }
  const renderRevision = _normalizeRenderRevision(entry?.revision);
  const renderVisualToken = ++_activeRenderVisualToken;
  let waitingForSettle = false;
  let restoreAfterFailure = false;
  const forceDoubleBuffer = !!reviewRequest;
  disableDoubleBufferIfNeeded(forceDoubleBuffer);
  const doubleBufferMode = forceDoubleBuffer || isDoubleBufferEnabled();
  const renderTarget = doubleBufferMode
    ? getInactivePreviewIframe(forceDoubleBuffer)
    : previewIframe;
  if (!renderTarget) return;
  if (doubleBufferMode) {
    syncInactivePreviewSizing(renderTarget);
  }
  // Full iframe renders replace the selected DOM node identity. Clear
  // element-level chrome before the old rect can become a stale parent overlay.
  eventBus.emit('iframe:selected-editable-cleared', { reason: 'full-render' });

  rendering = true;
  _activeRenderRevision = renderRevision;
  _currentRenderReviewToken = reviewRequest?.reviewToken || null;
  const renderAbortController = new AbortController();
  _currentRenderAbortController = renderAbortController;

  renderTarget.setAttribute('title', 'Website preview');
  renderTarget.setAttribute('aria-busy', 'true');
  if (previewIframe && previewIframe !== renderTarget) {
    previewIframe.setAttribute('aria-busy', 'true');
  }
  _loaderPhase = null;
  _loaderPhaseProgress = null;
  if (shouldShowRevealOverlay(renderTarget, request, doubleBufferMode)) {
    showRevealOverlay(renderVisualToken);
  }

  // Avoid flash for quick renders; show loader only when render takes long
  // enough. start() is a no-op while already armed, so the previous clear-then-
  // re-arm is gone: a back-to-back render must not restart the anti-flash clock
  // on a user who has already been waiting.
  _renderLoader.start();

  if (request.transition === 'page-switch' && !prefersReducedMotion()) {
    renderTarget.classList.remove('pb-preview-page-switch');
    void renderTarget.offsetWidth;
    renderTarget.classList.add('pb-preview-page-switch');
    renderTarget.addEventListener(
      'animationend',
      () => {
        renderTarget.classList.remove('pb-preview-page-switch');
      },
      { once: true }
    );
  }

  try {
    const state = reviewRequest?.detachedState || store.getState();
    if (!state) return;

    const renderImpl =
      typeof _agentReviewTestHooks?.renderToIframe === 'function'
        ? _agentReviewTestHooks.renderToIframe
        : renderToIframe;
    const { nonce } = await renderImpl(renderTarget, state, {
      preserveScroll: request.preserveScroll,
      hideDuringLoad: doubleBufferMode ? true : request.hideDuringLoad,
      signal: renderAbortController.signal,
      deferPendingRevoke: doubleBufferMode,
      assetSource: reviewRequest?.assetSource || null,
      onPhase: ({ phase, progress }) => {
        _loaderPhase = phase || null;
        _loaderPhaseProgress = progress || null;
        const phasePayload = { phase };
        if (progress && Number.isFinite(Number(progress.total))) {
          phasePayload.progress = progress;
        }
        eventBus.emit('renderer:phase', phasePayload);
        updateIframeLoadingLabel(_loaderPhase, _loaderPhaseProgress);
      },
      onComplete: (payload) => {
        if (
          reviewRequest &&
          !_isAgentReviewTokenCurrent(
            reviewRequest.reviewToken,
            reviewRequest.detachedState.projectId
          )
        ) {
          return;
        }
        emitRendererComplete(payload);
      },
      onSettled: ({ nonce: settledNonce }) => {
        const hadIframeError = _iframeErrorNonces.has(settledNonce);
        _iframeErrorNonces.delete(settledNonce);
        if (_activeRenderVisualToken !== renderVisualToken) return;
        if (
          reviewRequest &&
          !_isAgentReviewTokenCurrent(
            reviewRequest.reviewToken,
            reviewRequest.detachedState.projectId
          )
        ) {
          _logAgentReviewRender('stale-settle-suppressed', {
            reviewToken: reviewRequest.reviewToken,
            side: reviewRequest.side
          });
          return;
        }
        renderTarget.setAttribute('aria-busy', 'false');
        if (previewIframe && previewIframe !== renderTarget) {
          previewIframe.setAttribute('aria-busy', 'false');
        }
        renderTarget.dataset.pbRendered = 'true';
        if (doubleBufferMode && renderTarget !== previewIframe) {
          startDoubleBufferSwap(renderTarget, renderVisualToken);
        } else {
          hideRevealOverlay(renderVisualToken);
        }
        if (reviewRequest && _agentReviewSession) {
          _agentReviewSession.status = 'before';
          eventBus.emit('agent:review-rendered', {
            reviewToken: reviewRequest.reviewToken,
            side: reviewRequest.side,
            pageId: reviewRequest.pageId
          });
        }
        if (!hadIframeError) _iframeErrorCoalescer.reset();
      }
    });
    if (
      reviewRequest &&
      !_isAgentReviewTokenCurrent(reviewRequest.reviewToken, reviewRequest.detachedState.projectId)
    ) {
      return;
    }
    waitingForSettle = true;
    bridge.attach(renderTarget, nonce);
    // Re-send interaction mode to every replacement iframe. Secondary tabs use
    // the same iframe-side editing gate as Preview mode, even when the toolbar's
    // visual preview toggle is off.
    const targetForInteractionMode = renderTarget;
    const onLoad = () => {
      targetForInteractionMode.removeEventListener('load', onLoad);
      syncIframeInteractionMode();
    };
    targetForInteractionMode.addEventListener('load', onLoad);
  } catch (err) {
    const aborted = err?.name === 'AbortError' || renderAbortController.signal.aborted;
    if (!aborted) {
      reportError('Preview render', err, {
        user: 'toast',
        message: 'Failed to render preview',
        detail: err?.message || 'preview render failed',
        id: 'preview-render-failed'
      });
      restoreAfterFailure = !!reviewRequest;
    }
    renderTarget.setAttribute('aria-busy', 'false');
    if (previewIframe && previewIframe !== renderTarget) {
      previewIframe.setAttribute('aria-busy', 'false');
    }
    hideRevealOverlay(renderVisualToken, { immediate: true, force: true });
    if (doubleBufferMode) flushPendingBlobRevoke();
  } finally {
    rendering = false;
    if (_currentRenderAbortController === renderAbortController) {
      _currentRenderAbortController = null;
    }
    if (_currentRenderReviewToken === reviewRequest?.reviewToken) {
      _currentRenderReviewToken = null;
    }
    _activeRenderRevision = -1;
    _renderLoader.stop();
    if (!waitingForSettle) {
      renderTarget.setAttribute('aria-busy', 'false');
      if (previewIframe && previewIframe !== renderTarget) {
        previewIframe.setAttribute('aria-busy', 'false');
      }
      hideRevealOverlay(renderVisualToken, { immediate: true, force: true });
      if (doubleBufferMode) flushPendingBlobRevoke();
    }
    _loaderPhase = null;
    _loaderPhaseProgress = null;
    showIframeLoading(false);
    if (
      restoreAfterFailure &&
      reviewRequest &&
      _isAgentReviewTokenCurrent(reviewRequest.reviewToken, reviewRequest.detachedState.projectId)
    ) {
      await exitAgentReview('failure');
    } else {
      void _drainRenderQueue();
    }
  }
}

async function _drainRenderQueue() {
  if (rendering) return;
  const highest = _getHighestQueuedRenderEntry();
  if (getActiveEdit() && !highest?.opts?.review) return;
  const next = _dequeueHighestQueuedRenderEntry();
  if (!next) return;
  await _runQueuedRender(next);
}

export async function renderPreview({
  preserveScroll = true,
  transition = null,
  hideDuringLoad = false,
  source = 'direct',
  label = '',
  patches = null,
  oldState = null,
  newState = null,
  _agentReviewExit = false
} = {}) {
  const releasedReview =
    !_agentReviewExit && (_agentReviewSession || _heldPreviewBlocker() !== null)
      ? releaseAgentReview('normal-render')
      : false;
  const forceFullRender = releasedReview || source === 'agent-review-exit';
  if (!previewIframe) return 'skipped';

  const request = {
    preserveScroll,
    transition,
    hideDuringLoad,
    source,
    label,
    patches,
    oldState,
    newState
  };

  const activeEdit = getActiveEdit();
  if (activeEdit) {
    await flushPendingEdit(activeEdit.editSessionId);
    if (!forceFullRender && (await tryApplySurgicalSync(request))) return 'surgical';
    const queued = _queueRenderRequest(request, _normalizeRenderRevision(store?._revision));
    if (queued.queued) _abortActiveRenderIfSuperseded(queued.revision);
    return 'queued';
  }

  if (!forceFullRender && (await tryApplySurgicalSync(request))) return 'surgical';

  const queued = _queueRenderRequest(request, _normalizeRenderRevision(store?._revision));
  if (queued.queued) _abortActiveRenderIfSuperseded(queued.revision);
  await _drainRenderQueue();
  return queued.queued ? 'render' : 'queued';
}

async function renderHistoryChangeWithFeedback(payload) {
  const { patches, oldState, newState } = payload;
  const affected = deriveHistoryAffectedInstanceIds(patches, oldState, newState);
  const currentSections = previewSectionsById(newState || store.getState());
  const primary = selection.getPrimary();
  const selectedStillExists = primary && currentSections.has(primary);
  const targetInstanceId = affected.includes(primary)
    ? primary
    : affected[0] || (selectedStillExists ? primary : null);
  const restoreElement =
    selectedElementPayload?.instanceId === targetInstanceId
      ? { instanceId: targetInstanceId, eid: selectedElementPayload.eid || null }
      : null;
  const feedback = targetInstanceId
    ? selectAfterRender(targetInstanceId, { restoreElement })
    : null;

  try {
    const mode = await renderPreview({
      preserveScroll: true,
      transition: null,
      hideDuringLoad: true,
      ...payload
    });
    // Surgical sync keeps the current iframe, so no bridge-ready event follows.
    if (mode === 'surgical') feedback?.fire({ restoreSelection: false });
  } catch (err) {
    feedback?.cancel();
    reportError('History preview', err, {
      user: 'toast',
      message: 'Could not show that history change in the preview',
      detail: err?.message || 'history preview update failed',
      id: 'history-preview-failed'
    });
  }
}

function wireEvents() {
  // Agent Review emits render intent only. This shell-owned listener keeps the
  // detached canvas inside the same queue/abort/bridge lifecycle as every
  // normal render and registers project/Escape teardown before their generic
  // handlers below.
  wireAgentReviewEvents();

  // A whole-project swap landed (version restore, boot cloud restore, switcher
  // Open). The store already holds the new state; refresh the canvas — clear the
  // now-stale selection/scroll and re-render. Without this, version restore left
  // the previous project painted on screen (project:restored had no listener).
  eventBus.on('project:restored', async () => {
    // The restored bundle carries its own updatedAt, newer than our last sync
    // stamp — tickCloudSync would read that as unsaved work and re-upload a
    // re-serialization of content the cloud already has. The server's same-version
    // dedupe can't catch it (our zip is not byte-identical to the one that minted
    // the version), so each restore would mint a duplicate. Set synchronously,
    // before the awaits below, so a tick can't slip in. Mirrors the resets on
    // collab:save-adopted and manual cloud save.
    _lastCloudSyncedAt = Number(store.getState()?.updatedAt || Date.now());
    resetSelection('project-restored');
    resetActiveEditable('project-restored');
    selectedElementPayload = null;
    if (isPickModeActive()) exitPickMode();
    bridge.clearScroll();
    await renderPreview({ preserveScroll: false, transition: null, hideDuringLoad: false });
  });

  // Add section from palette — delegate to section manager, then auto-select new section
  eventBus.on('palette:add-section', async ({ catalogKey, category, position }) => {
    const instanceId = await sectionManager.addSection(catalogKey, category, position);
    if (instanceId) {
      selectAfterRender(instanceId);
    }
  });

  // Glass overlay: "Add Section" at specific position
  eventBus.on('glass:add-section-at', ({ position, category }) => {
    if (!sectionList) return;
    // Phase 3 — forward the optional category hint from the glass-overlay
    // gap button through to the modal so it pre-selects the relevant tab.
    sectionList.openAddModal(position, category ? { category } : undefined);
  });

  // Glass overlay: "Add Blank Section" at specific position
  eventBus.on('glass:add-freeform-at', async ({ position }) => {
    const instanceId = await sectionManager.addFreeformSection(position);
    if (instanceId) selectAfterRender(instanceId);
  });

  // Glass overlay: "Add SVG Divider" at a specific gap.
  //
  // DS-6 FIXES WHAT THIS OFFER COULD NOT DO. Decorative SVGs live inside a
  // section's HTML, so a gap has to resolve to a section AND an edge — and the
  // engine could only ever place at a section's BOTTOM. Position 0 was mapped
  // to `targetIndex = 0`, so asking for a shape ABOVE the hero attached one
  // BELOW it: an affordance offered at every gap that was wrong at the first
  // one and silently right everywhere else.
  //
  // The mapping is now the one a user would draw:
  //   position 0     ⇒ the TOP edge of section 0
  //   position P > 0 ⇒ the BOTTOM edge of section P−1
  eventBus.on('glass:add-divider-at', ({ position, triggerEl = null }) => {
    const state = store.getState();
    const page = store.getActivePage();
    if (!page || !page.sections.length) return;
    const edge = position > 0 ? 'bottom' : 'top';
    const targetIndex = position > 0 ? position - 1 : 0;
    const section = page.sections[targetIndex];
    if (!section) return;
    const pageIndex = state.pages.indexOf(page);
    // The path is derived PER PICK from the slot's own section index, not from
    // the gap that opened the dialog — under a queue those differ.
    openSvgLibraryForInsert({
      triggerEl,
      // DS-5 / D1: the header names the target. `targetIndex` and `edge` are
      // both resolved above, so the dialog says which section AND which edge it
      // will attach to instead of asking the user to remember which gap they
      // clicked.
      context: `Adding a shape on the ${formatSectionName(section)}'s ${edge} edge`,
      // DS-7: one slot per section EDGE, so dressing five boundaries is one
      // dialog. Below two the builder returns [] and this is unchanged.
      slots: buildPageEdgeSlots(
        page,
        { sectionIndex: targetIndex, edge, pageIndex },
        { displayNameOf: (s) => formatSectionName(s) }
      ),
      onPick: (svgMarkup, indexEntry, slot) => {
        // Under a queue the pick is for whichever edge the cursor is on, which
        // may be a DIFFERENT section from the gap that opened the dialog.
        const at = Number.isInteger(slot?.data?.sectionIndex)
          ? slot.data.sectionIndex
          : targetIndex;
        const atEdge = slot?.data?.edge || edge;
        const latestPage = store.getActivePage();
        const latestSection = latestPage?.sections?.[at];
        if (!latestSection) return;
        if (!editableIdRepairAllocationReady(latestSection, store.getState())) return;
        const result = insertDecorativeSvg(latestSection, svgMarkup, indexEntry, {
          edge: atEdge
        });
        if (!result.updatedHtml) return;
        // DS-6: the shared helper. This block used to be a hand-copy of the
        // panel's `applySectionMutation`, and DS-6's own CSS-append change is
        // exactly the kind one copy receives and the other does not.
        const atPath = `pages.${pageIndex}.sections.${at}`;
        applyDecorativeMutation(
          { path: `${atPath}.html`, sectionPath: atPath, section: latestSection },
          result.updatedHtml,
          result.addedScopedCss,
          'Insert decorative SVG'
        );
        // D8: the undo IS the confirmation. Per pick under a queue (G8).
        showUndoToast('Decorative SVG added');
      }
    });
  });

  // Preview mode toggle
  eventBus.on('toolbar:toggle-preview', () => {
    isPreviewMode = !isPreviewMode;
    // Hide/show glass overlay
    if (_glassOverlay) {
      const overlayEl = _glassOverlay.getOverlayElement();
      if (overlayEl) overlayEl.classList.toggle('pb-preview-mode', isPreviewMode);
    }
    // Hide/show left panel
    const leftPanel = document.getElementById('pb-palette');
    if (leftPanel) {
      if (isPreviewMode) {
        // Remember user preference before forcing collapse in preview mode.
        _sidebarWasOpenBeforePreview = !leftPanel.classList.contains('pb-collapsed');
        leftPanel.classList.add('pb-collapsed');
        eventBus.emit('sidebar:state-changed', { open: false });
      } else {
        // Restore prior state instead of always opening.
        const shouldOpen = _sidebarWasOpenBeforePreview === true;
        leftPanel.classList.toggle('pb-collapsed', !shouldOpen);
        eventBus.emit('sidebar:state-changed', { open: shouldOpen });
        _sidebarWasOpenBeforePreview = null;
      }
    }
    // Tell iframe to toggle click interception + editing CSS
    syncIframeInteractionMode();
    // Notify toolbar to update button state
    eventBus.emit('preview:state-changed', { enabled: isPreviewMode });
  });

  // MM-3 preview guidance: enter preview mode at the mobile viewport and open
  // the drawer with a real toggle click, so an applied fragment is seen doing
  // its job immediately. The viewport the user had is restored when they
  // leave preview mode (once — a normal preview session is untouched).
  eventBus.on('mobile-menu:preview', () => {
    if (!store.getState()?.sharedSections?.navigation) return;
    _mobileMenuRestoreViewport = getActiveViewportName();
    if (!isPreviewMode) eventBus.emit('toolbar:toggle-preview');
    eventBus.emit('toolbar:set-viewport', { name: 'mobile' });
    // Two frames: let the iframe width change land so the drawer's max-width
    // media block is active before the toggle handler runs.
    requestAnimationFrame(() => requestAnimationFrame(() => bridge.send('mm-toggle-menu', {})));
  });
  eventBus.on('preview:state-changed', ({ enabled } = {}) => {
    if (enabled || !_mobileMenuRestoreViewport) return;
    const restore = _mobileMenuRestoreViewport;
    _mobileMenuRestoreViewport = null;
    eventBus.emit('toolbar:set-viewport', { name: restore });
  });

  // Open template selector. Forwards options (`{ preselect: siteName }`) so
  // a command-palette template result lands directly on its preview instead
  // of bouncing through the grid.
  eventBus.on('toolbar:open-templates', (options) => {
    void openTemplateSelectorLazy(options);
  });
  eventBus.on('agent:toggle', async () => {
    // Ctrl+J has no trigger to hide, so the reachability gate has to live here
    // too. Say why rather than opening a panel whose every request is blocked
    // by the browser before it leaves the tab.
    if (!isAgentReachable()) {
      showToast(
        'The PageStitch Agent runs on localhost — open the builder locally to use it.',
        'info'
      );
      return;
    }
    try {
      const panel = await ensureAgentPanel();
      panel.toggle();
    } catch (err) {
      reportError('Open agent', err, {
        user: 'status',
        message: 'Could not open the agent panel',
        detail: err?.message || 'agent panel failed to load'
      });
    }
  });

  // The agent owns the project's version chain for the length of a turn: it
  // captures an optimistic-concurrency base when it downloads the project and
  // validates it only when it saves. Any version this tab mints in between
  // guarantees a 409, so auto-sync and manual save both stand down.
  eventBus.on('agent:run-started', () => {
    _agentRunActive = true;
  });
  eventBus.on('agent:run-finished', () => {
    _agentRunActive = false;
    // Deliberately NOT stamping _lastCloudSyncedAt here. The live updatedAt at
    // this moment may include edits the user made DURING the run, which no save
    // has uploaded — stamping it would mark them synced and silently drop them.
    // The tick's own dirty check decides, and this flush runs it immediately.
    void tickCloudSync();
  });
  // The panel's own saves (pre-run capture, Undo/Reapply) bypass every site that
  // advances _lastCloudSyncedAt, which would leave the tick believing the
  // project is dirty and re-saving seconds later — mid-run, that is the 409.
  // The payload always carries what the save actually uploaded, never a
  // post-await live read.
  eventBus.on('agent:cloud-synced', ({ syncedUpdatedAt } = {}) => {
    _lastCloudSyncedAt = Number(syncedUpdatedAt) || _lastCloudSyncedAt;
  });

  // Open the Import brand from URL wizard — accepts optional { url, autoSubmit }
  // payload (used by the empty-canvas stage's Door 3 to pre-fill and
  // auto-submit) and { templateSiteName, templateLabel } (template library).
  eventBus.on('toolbar:skin-from-url', (options) => {
    if (collabBlocksWholeStateReplace('Skinning a new site from a URL')) return;
    void showSkinWizardLazy(options || {});
  });

  // Phase 6 UI: auth + project switcher events.
  eventBus.on('toolbar:auth-show', (options = {}) => {
    showAuthModal(options).catch((err) => console.warn('[Shell] auth modal:', err));
  });
  eventBus.on('toolbar:logout', async () => {
    // FN-5: the whole decision matrix lives in core/logout-flow.js so it can be
    // driven headlessly. What it guarantees, and what the code it replaced did
    // not: the local copy is deleted ONLY on proof of cloud durability, never on
    // a timeout, a failure, or the local dirty flag. Everything here is a
    // dependency, including the dialog — core/ must not import ui/.
    await runLogoutSequence({
      store,
      isRealAccount: isRealUser(currentUser),
      projectNeedsName,
      cloudSave,
      getCloudSyncRecord,
      confirmUnsaved: (ctx) => showUnsavedOnLogoutDialog(ctx),
      downloadBackup: fullSave,
      disconnectCollab,
      postSignedOut,
      logoutAccount,
      reportError,
      // Kept as a literal call in this handler: auth-first-tests pins that
      // sign-out ends in a reload, and indirection through a name would make
      // that pin unverifiable from source.
      reload: () => window.location.reload()
    });
  });
  eventBus.on('toolbar:open-projects', () => {
    if (!isRealUser(currentUser)) {
      showToast('Sign in to open cloud projects', 'info');
      eventBus.emit('toolbar:auth-show');
      return;
    }
    showProjectSwitcherModal({
      canOpenProject: () => !collabBlocksWholeStateReplace('Opening another project'),
      beforeOpenProject: ({ id }) => prepareTabGuardForProject(id)
    });
  });
  eventBus.on('toolbar:open-history', () => {
    if (!isRealUser(currentUser)) {
      showToast('Sign in to view cloud history', 'info');
      eventBus.emit('toolbar:auth-show');
      return;
    }
    // HR-7 — the rail IS Version History now; the modal it replaced is gone.
    // There is nothing to fall through to, so openHistoryRail() owns telling
    // the user why it declined on every path that returns false. Do not add a
    // toast here: two surfaces reporting one refusal is how a message ends up
    // said twice or, worse, not at all.
    openHistoryRail();
  });
  // Optional pre-export review — never a gate. Baseline = last EXPORT publish
  // ('export-html'/'export-11ty'); the module owns the race rules.
  let reviewChangesPending = false;
  const syncReviewChangesPending = () => {
    const item = document.getElementById('pb-review-changes-btn');
    if (!item) return;
    item.setAttribute('aria-busy', String(reviewChangesPending));
    item.textContent = reviewChangesPending
      ? 'Preparing visual review…'
      : 'Review changes since last export…';
    item.disabled = reviewChangesPending || !store.isWritable();
  };
  eventBus.on('toolbar:review-changes', () => {
    if (!isRealUser(currentUser)) {
      showToast('Sign in to review published changes', 'info');
      eventBus.emit('toolbar:auth-show');
      return;
    }
    if (reviewChangesPending) return;
    const expectedProjectId = store.getState()?.projectId || null;
    const expectedEpoch = store.getEpoch();
    reviewChangesPending = true;
    syncReviewChangesPending();
    void import('./review-changes.js')
      .then((mod) => mod.openReviewChangesSinceLastPublish({ expectedProjectId, expectedEpoch }))
      .catch((err) => {
        reportError('Review changes', err, {
          user: 'toast',
          message: 'Could not open the visual review.'
        });
      })
      .finally(() => {
        reviewChangesPending = false;
        syncReviewChangesPending();
      });
  });
  // The diff compares CLOUD versions; a read-only tab may be displaying older
  // state than the cloud head, so offering the action there would lie about
  // what "now" means. Disabled with the reason, not hidden.
  eventBus.on('toolbar:export-menu-open', ({ menu }) => {
    const item = menu?.querySelector?.('#pb-review-changes-btn');
    if (!item) return;
    const writable = store.isWritable();
    syncReviewChangesPending();
    item.title = writable ? '' : 'Available in the editing tab — this tab is read-only';
  });
  eventBus.on('toolbar:open-fleet', async () => {
    if (!isRealUser(currentUser)) {
      showToast('Sign in to view your client fleet', 'info');
      eventBus.emit('toolbar:auth-show');
      return;
    }
    try {
      const { showFleetModal } = await import('./fleet-modal.js');
      showFleetModal();
    } catch (err) {
      reportError('Open fleet', err, {
        user: 'status',
        message: 'Could not open the fleet view'
      });
    }
  });
  // Collab Phase 2: sharing needs a signed-in account AND a project row in D1
  // (membership FKs projects) — cloudVersion is null until the first cloud
  // save/open, so gate on it with a save nudge instead of surfacing a 404.
  let activeShareModal = null;
  let activeShareProjectId = null;
  eventBus.on('toolbar:share-project', ({ access } = {}) => {
    if (!isRealUser(currentUser)) {
      showToast('Sign in to share projects', 'info');
      eventBus.emit('toolbar:auth-show');
      return;
    }
    const state = store.getState();
    if (!state?.projectId || state.cloudVersion == null) {
      showToast('Save this project to the cloud first, then share it.', 'info');
      return;
    }
    // The cached handle is a reference to a dialog that may already be gone:
    // an ordinary dismissal (backdrop, Escape, the header close) tears the
    // overlay out of the document without telling the shell. Treating a stale
    // handle as "already open" made Share openable exactly once per project —
    // and the `focus?.()` that was supposed to soften that does not exist on
    // the handle, so the second click did nothing at all. Liveness is read from
    // the DOM, which cannot go stale.
    if (activeShareModal && !activeShareModal.overlay?.isConnected) {
      activeShareModal = null;
      activeShareProjectId = null;
    }
    // A genuinely live dialog is focused rather than duplicated — two Share
    // dialogs over one project is two sources of truth for the same access
    // list. `showShareSurface` owns that path: it finds its own overlay and
    // focuses the recipient input, which is the control someone reopening
    // Share is reaching for.
    const handle = showShareModal({
      projectId: state.projectId,
      projectName: state.projectName || '',
      currentUser,
      // FN-8: an explicit Edit/Review choice overrides the remembered access
      // mode for THIS opening only. Plain Share passes nothing and keeps the
      // preference, which is what makes the preference still worth having.
      access
    });
    if (handle) {
      activeShareModal = handle;
      activeShareProjectId = state.projectId;
    }
  });
  eventBus.on('project:restored', ({ projectId } = {}) => {
    if (!activeShareModal) return;
    if (projectId && projectId === activeShareProjectId) return;
    activeShareModal.close?.();
    activeShareModal = null;
    activeShareProjectId = null;
  });
  // Collab Phase 4: re-evaluate the asset mirror when the open project or its
  // membership changes (share-modal add/remove/leave emits the latter).
  eventBus.on('project:restored', () => {
    void maybeActivateCollabAssets(currentUser);
  });
  eventBus.on('collab:membership-changed', () => {
    void maybeActivateCollabAssets(currentUser);
  });
  // Collab Phase 8: re-join the realtime room for the newly-open project, and
  // let the shell snapshot local state before adopting a room copy.
  eventBus.on('project:restored', () => {
    void maybeConnectCollab(currentUser);
  });
  eventBus.on('collab:membership-changed', () => {
    void maybeConnectCollab(currentUser);
  });
  // SB-1: the room told us outright (close 4404/4403). This is the ONLY signal
  // in the system that states the cause, so it is the one the banner can name.
  eventBus.on('collab:project-unavailable', ({ projectId, signal } = {}) => {
    if (projectId && store.getState()?.projectId !== projectId) return;
    void enterProjectUnavailable(signal);
  });
  eventBus.on('collab:editor-changed', ({ editorClaim, selfConnId } = {}) => {
    if (editorClaim?.holderConn === selfConnId) removeSessionClaimBanner();
    else {
      showSessionClaimBanner(editorClaim);
      void renderPreview({ preserveScroll: true, transition: null, hideDuringLoad: false });
    }
    syncIframeInteractionMode();
  });
  configureCollabSession({
    snapshotBeforeAdopt: () => revisionStore.save('Before joining session', '', 'auto')
  });
  // Collab Phase 10: presence UI (cursors in the glass content element, avatar
  // stack beside the save pill, section badges). Inert until connected.
  //
  // CMD-3: an EXPLICIT mount, resolved lazily. `collab-ui.js` used to query
  // `.pb-toolbar-left` and insert before `#pb-auth-chip` — two structural
  // assumptions about a bar that no longer has either arrangement. This runs
  // before the command bar is constructed, which is why it is a getter.
  initCollabUI({
    getContentElement: () => _glassOverlay?.getContentElement?.() || null,
    getPresenceMount: () => commandBar?.presenceMount || null,
    sendPresence: collabSendPresence,
    getSelfConnId: () => null
  });
  // A remote collaborator's op just applied to the store — repaint WITHOUT
  // disturbing selection/caret (never renderHistoryChangeWithFeedback). Coalesce
  // bursts to one render per frame; renderPreview auto-picks surgical vs full.
  let _remoteRenderQueued = false;
  let _remoteRenderBatch = [];
  eventBus.on('collab:remote-applied', ({ patches, label } = {}) => {
    if (Array.isArray(patches)) _remoteRenderBatch.push(...patches);
    if (_remoteRenderQueued) return;
    _remoteRenderQueued = true;
    requestAnimationFrame(() => {
      _remoteRenderQueued = false;
      const patchBatch = _remoteRenderBatch;
      _remoteRenderBatch = [];
      void renderPreview({
        source: 'mutation',
        label: label || 'Remote update',
        patches: patchBatch,
        newState: store.getState(),
        preserveScroll: true,
        transition: null,
        hideDuringLoad: false
      });
    });
  });
  // Save-notify adoption already ran setCloudVersionAndPersist in collab-session;
  // keep the auto-sync clock in step so this client doesn't re-save immediately.
  eventBus.on('collab:save-adopted', () => {
    _lastCloudSyncedAt = Number(store.getState()?.updatedAt || Date.now());
  });
  // Collab Phase 9: push peer-lock treatment into the iframe on every lock
  // change and re-apply on bridge (re)ready (the wrapper attrs don't survive a
  // reload). Solo → empty rows clear any stale treatment.
  const pushCollabLockTreatment = () => {
    try {
      bridge.send('set-collab-locks', { locks: collabLockTreatmentRows() });
    } catch {
      /* iframe not ready */
    }
  };
  eventBus.on('collab:locks-changed', pushCollabLockTreatment);
  eventBus.on('iframe:bridge-ready', pushCollabLockTreatment);
  // A remotely-fetched blob just landed: repaint every active-page section
  // that references it (their previous render left the pb-asset:// URI
  // unresolved). handlePreviewSyncSectionHTML owns the JS-section
  // full-render fallback; a full render repaints everything, so stop there.
  eventBus.on('collab:asset-fetched', async ({ id } = {}) => {
    if (!id) return;
    const state = store.getState();
    const page = state?.pages?.find((p) => p.id === state.activePage) || state?.pages?.[0];
    if (!page) return;
    const uri = `pb-asset://${id}`;
    for (const section of page.sections || []) {
      const html = typeof section.html === 'string' ? section.html : '';
      const css = typeof section.css === 'string' ? section.css : '';
      if (html.includes(uri) || css.includes(uri)) {
        const surgical = await handlePreviewSyncSectionHTML({
          instanceId: section.instanceId,
          label: 'Remote asset',
          syncOverrides: false
        });
        if (surgical === false) break;
      }
    }
  });
  eventBus.on('auth:changed', (payload) => {
    clearListTemplatesCache();
    setCurrentUser(payload);
  });

  // FN-8: ONE authoritative guard for preview saving, here rather than on a
  // control. The desktop row checked `isOperationActive` but never acquired
  // anything, the phone row checked nothing, and the modal starts a fresh
  // progress task per invocation — so two clicks produced two uploads and two
  // dialogs. A backstop on the command covers every entry point, including the
  // command palette and any future one.
  eventBus.on('toolbar:save-preview', async () => {
    const state = store.getState();
    // The capability the shared action definition declares. Neither layout was
    // consuming it, so a named local-only project opened the modal and failed
    // later instead of saying why now.
    if (!isRealUser(currentUser) || !state?.projectId || state.cloudVersion == null) {
      showToast('Save this project to the cloud first, then share a preview.', 'info');
      return;
    }
    // The SHARED operation guard, not a local flag: the desktop Share row
    // already refuses while `isOperationActive('save')`, so acquiring the same
    // id is what makes that check mean something. A null token means one is
    // already running.
    const operationToken = beginOperation('save', { label: 'Saving a preview…' });
    if (operationToken === null) return;
    try {
      if (!(await ensureProjectNamedBeforeAction())) return;
      await showSavePreviewModal();
    } finally {
      endOperation(operationToken);
    }
  });

  eventBus.on('toolbar:my-previews', () => {
    showMyPreviewsModal();
  });

  eventBus.on('toolbar:save-template', async () => {
    if (!(await ensureProjectNamedBeforeAction())) return;
    showSaveTemplateModal();
  });

  // Load template — delegate to section manager, then auto-select first section.
  // Multi-page templates first prompt the user to pick which pages to load.
  eventBus.on('palette:load-template', async ({ siteName, templateCatalog }) => {
    if (!ensureWritableTab()) return;
    if (collabBlocksWholeStateReplace('Loading a template')) return;
    if (document.querySelector('.pb-page-selection-overlay')) return;

    const template = templateCatalog.find((t) => t.siteName === siteName);
    const skipModal = !template;

    const afterLoad = () => {
      const state = store.getState();
      const page = state.pages.find((p) => p.id === state.activePage);
      if (page && page.sections.length > 0) {
        selectAfterRender(page.sections[0].instanceId);
      }
      sectionManager.startBackgroundPrefetch();
    };

    if (skipModal) {
      if (!ensureWritableTab()) return;
      await sectionManager.loadTemplate(siteName, templateCatalog);
      afterLoad();
      return;
    }

    const pageMap = await sectionManager.loadPageMap(siteName);
    const pages = Array.isArray(pageMap?.pages) ? pageMap.pages : [];
    if (pages.length <= 1) {
      if (!ensureWritableTab()) return;
      await sectionManager.loadTemplate(siteName, templateCatalog);
      afterLoad();
      return;
    }

    showPageSelectionModal({
      title: `Pages to load from ${siteName}`,
      pages,
      mode: 'multi',
      confirmLabel: 'Load',
      defaultSelection: 'all',
      onConfirm: async (filenames) => {
        if (!ensureWritableTab()) return;
        await sectionManager.loadTemplate(siteName, templateCatalog, {
          pageFilter: new Set(filenames)
        });
        afterLoad();
      }
    });
  });

  eventBus.on('page:hydrating', ({ name } = {}) => {
    _pendingPageHydrationLoads++;
    _loaderPhase = 'hydrating-page';
    _loaderPhaseProgress = { name };
    showIframeLoading(true, _loaderPhase, _loaderPhaseProgress);
  });

  eventBus.on('page:hydrated', () => {
    _pendingPageHydrationLoads = Math.max(0, _pendingPageHydrationLoads - 1);
    if (_pendingPageHydrationLoads > 0) return;
    _loaderPhase = null;
    _loaderPhaseProgress = null;
    showIframeLoading(false);
  });

  // Switch page
  eventBus.on('toolbar:switch-page', async ({ pageId }) => {
    _iframeErrorCoalescer.reset();
    _iframeErrorNonces.clear();
    await pageManager.setActivePage(pageId);
  });

  // Navigate page from preview mode link clicks
  eventBus.on('iframe:navigate-page', async ({ href }) => {
    if (!href) return;
    const state = store.getState();
    const match = findPageForHref(href, state);
    if (!match) return;
    _iframeErrorCoalescer.reset();
    _iframeErrorNonces.clear();
    await pageManager.setActivePage(match.id);
  });

  // Links the iframe classified as off-site. A crawled template keeps whatever
  // its author wrote, and roughly a third of the corpus writes its OWN nav with
  // absolute hrefs (`https://codestitch.app/app/…`) rather than `/app/`. Those
  // are internal links wearing an external shape: opening the live site in a new
  // tab is the one thing preview mode must not do. So the scheme does not
  // decide — the HOST does, checked here where the project state is.
  eventBus.on('iframe:open-external-link', async ({ href }) => {
    if (!href || !(href.startsWith('http://') || href.startsWith('https://'))) return;
    const state = store.getState();
    if (isOwnSiteHref(href, state)) {
      const match = findPageForHref(href, state);
      if (match) {
        _iframeErrorCoalescer.reset();
        _iframeErrorNonces.clear();
        await pageManager.setActivePage(match.id);
        return;
      }
      // A link to this site's own domain that the project has no page for. The
      // live site is not this project — say so rather than navigating away to it.
      showToast('That page is not part of this project yet', 'info');
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  });

  // Add page — chooser modal: blank page (today's default) or a page
  // from another template. From-template path keeps current shared
  // nav/footer; only the new page's content sections are imported.
  eventBus.on('toolbar:add-page', () => {
    // BC-1: refuse before offering a choice that cannot be carried out.
    if (!ensureWritableTab()) return;
    showAddPageChooserModal({
      onBlank: () => {
        pageManager.addPage();
      },
      onFromTemplate: () => {
        void openTemplateSelectorLazy({
          mode: 'add-page',
          onPicked: ({ siteName, pageMap }) => {
            const filenames = Array.isArray(pageMap?.pages)
              ? pageMap.pages.map((p) => p.filename)
              : [];

            const handleResult = (result) => {
              if (result?.firstInstanceId) selectAfterRender(result.firstInstanceId);
            };

            if (filenames.length <= 1) {
              sectionManager
                .addTemplatePage({
                  siteName,
                  filename: filenames[0] ?? 'index'
                })
                .then(handleResult);
              return;
            }

            showPageSelectionModal({
              title: `Add a page from ${siteName}`,
              pages: pageMap.pages,
              mode: 'single',
              confirmLabel: 'Add Page',
              defaultSelection: 'first',
              onConfirm: ([filename]) => {
                sectionManager.addTemplatePage({ siteName, filename }).then(handleResult);
              }
            });
          }
        });
      }
    });
  });

  // Reorder pages
  eventBus.on('toolbar:reorder-pages', async ({ orderedIds } = {}) => {
    const state = store.getState();
    if (!state || !Array.isArray(state.pages) || !Array.isArray(orderedIds)) return;
    if (orderedIds.length !== state.pages.length) return;

    const byId = new Map(state.pages.map((page) => [page.id, page]));
    if (orderedIds.some((id) => !byId.has(id))) return;
    const currentOrder = state.pages.map((page) => page.id).join('|');
    const nextOrder = orderedIds.join('|');
    if (currentOrder === nextOrder) return;

    const nextPages = orderedIds.map((id) => structuredClone(byId.get(id)));
    // BC-1: announce only a reorder the store took (a read-only refusal already
    // names its reason through `store:write-rejected`).
    if (!store.update('pages', nextPages, 'Reorder pages')) return;
    await renderPreview({ preserveScroll: true, transition: null, hideDuringLoad: false });
    showToast('Pages reordered', 'success');
  });

  // Rename page
  eventBus.on('toolbar:rename-page', ({ pageId, name }) => {
    pageManager.renamePage(pageId, name);
  });

  // Duplicate page
  eventBus.on('toolbar:duplicate-page', async ({ pageId }) => {
    await pageManager.duplicatePage(pageId);
  });

  // Delete page
  eventBus.on('toolbar:delete-page', async ({ pageId }) => {
    const state = store.getState();
    const page = state.pages.find((p) => p.id === pageId);
    if (!page) return;
    if (!ensureWritableTab()) return;
    // BC-8: act, then Undo — the page manager records one history entry and
    // shows the Undo toast, the same contract as deleting a section.
    await sectionService.removePage(pageId);
  });

  /**
   * The event boundary is the authoritative single-flight guard. Commands can
   * arrive from the visible button, the palette, a shortcut, or a retry toast;
   * guarding only the button leaves every other route re-entrant. A visible
   * control may hand its already-started token across with the event so this
   * wrapper continues that exact operation instead of rejecting its first run.
   */
  async function runCommandOperation(type, label, run, handedToken = null) {
    const token =
      handedToken == null
        ? beginOperation(type, { label })
        : isOperationTokenActive(type, handedToken)
          ? handedToken
          : null;
    if (token === null) return;
    eventBus.emit('toolbar:operation-state', { type, busy: true, label });
    try {
      return await run();
    } finally {
      endOperation(token);
      eventBus.emit('toolbar:operation-state', { type, busy: false });
    }
  }

  // Download .json
  eventBus.on('toolbar:quick-save', ({ operationToken = null } = {}) =>
    runCommandOperation(
      'save',
      'Saving…',
      async () => {
        if (!(await ensureProjectNamedBeforeAction())) return;
        try {
          const filename = await quickSave();
          eventBus.emit('save:completed', { kind: 'quick' });
          showBackupDownloadedToast(filename);
        } catch (err) {
          console.error('[Shell] Download .json failed:', err);
          showToast('Download failed: ' + err.message, {
            type: 'error',
            action: {
              label: 'Retry',
              onClick: () => eventBus.emit('toolbar:quick-save')
            }
          });
        }
      },
      operationToken
    )
  );

  // Phase 6 UI: cloud save — POST current project to /api/projects/save.
  // Creates a new D1-backed version row + R2 bundle. Requires real auth.
  eventBus.on('toolbar:cloud-save', ({ operationToken = null } = {}) =>
    runCommandOperation(
      'save',
      'Cloud saving…',
      async () => {
        if (!isRealUser(currentUser)) {
          showToast('Sign in to save to the cloud', 'info');
          eventBus.emit('toolbar:auth-show');
          return;
        }
        if (!(await ensureProjectNamedBeforeAction())) return;
        // A manual save mid-run mints exactly the version the agent's held base is
        // about to fail against — the same 409 the auto-sync gate prevents.
        if (_agentRunActive) {
          showToast('The agent is editing this project — save again when it finishes', {
            type: 'info',
            id: 'agent-run-blocks-save'
          });
          return;
        }
        // Collab Phase 8: a manual save in a live session must describe converged
        // room state — flush the outbox first (bounded), else defer.
        if (collabSessionActive() && (!collabConnected() || !(await collabFlushOutbox(3000)))) {
          showToast('Still syncing — try again in a moment', {
            type: 'info',
            id: 'collab-still-syncing'
          });
          return;
        }
        _cloudSyncInFlight = true;
        refreshCloudSyncState();
        try {
          const result = await cloudSave({ captureSnapshotContext: collabCaptureSaveCheckpoint });
          _cloudSyncLastError = '';
          const v = result?.version?.version;
          // Keep the auto-sync tick from minting a duplicate version seconds later.
          // Stamp what the save uploaded — reading the live updatedAt here would also
          // swallow any edit made while the upload was in flight, leaving it unsynced
          // until the next mutation happens to bump updatedAt again.
          _lastCloudSyncedAt = Number(result?.syncedUpdatedAt) || _lastCloudSyncedAt;
          collabNotifySaved(Number(v), result?.snapshotContext);
          eventBus.emit('save:completed', { kind: 'cloud' });
          showToast(`Saved to cloud${v ? ` (v${v})` : ''}`, 'success');
        } catch (err) {
          console.error('[Shell] Cloud save failed:', err);
          if (err instanceof TemplateApiError && err.status === 409) {
            // The cloud advanced past our base on another device — resolve, don't retry
            // blindly (a bare retry would 409 again).
            void resolveCloudConflict(err.data?.project || null);
          } else if (err instanceof TemplateApiError && err.status === 401) {
            // Session expired mid-save. The shared unauthorized handler already
            // fired pauseCloudSync during the fetch; upgrade that toast into an
            // explicit sign-in-and-retry path for this manual save.
            pauseCloudSync({ retryCloudSave: true });
          } else {
            const msg =
              err instanceof TemplateApiError ? err.message : err?.message || 'Cloud save failed';
            _cloudSyncLastError = msg;
            showToast(`Cloud save failed: ${msg}`, {
              type: 'error',
              action: { label: 'Retry', onClick: () => eventBus.emit('toolbar:cloud-save') }
            });
          }
        } finally {
          _cloudSyncInFlight = false;
          refreshCloudSyncState();
        }
      },
      operationToken
    )
  );

  // Download .zip (ZIP with assets)
  eventBus.on('toolbar:full-save', ({ operationToken = null } = {}) =>
    runCommandOperation(
      'save',
      'Packaging…',
      async () => {
        if (!(await ensureProjectNamedBeforeAction())) return;
        try {
          showToast('Packaging project…', { type: 'info', duration: 4500 });
          const filename = await fullSave();
          eventBus.emit('save:completed', { kind: 'full' });
          showBackupDownloadedToast(filename);
        } catch (err) {
          console.error('[Shell] Download .zip failed:', err);
          showToast('Download failed: ' + err.message, {
            type: 'error',
            action: {
              label: 'Retry',
              onClick: () => eventBus.emit('toolbar:full-save')
            }
          });
        }
      },
      operationToken
    )
  );

  // ── BC-2: retry retries the thing that failed ──
  //
  // The save indicator's "Retry" used to emit toolbar:quick-save, which
  // DOWNLOADS a file. These two answer it instead. Both are single-flight
  // through their own owners: saveToIDB's token guard, tickCloudSync's
  // in-flight flag.
  eventBus.on('toolbar:retry-save', () => {
    if (!ensureWritableTab()) return;
    // flushAutoSave no-ops on a clean store; a failed write can leave the flag
    // cleared, and a retry the user asked for must actually attempt the write.
    void (store.isDirty?.() ? store.flushAutoSave() : store.saveToIDB()).catch((err) =>
      reportError('Retry save', err, { user: 'toast' })
    );
  });
  eventBus.on('toolbar:retry-cloud-sync', () => {
    _cloudSyncLastError = '';
    refreshCloudSyncState();
    void tickCloudSync();
  });
  eventBus.on('toolbar:resolve-cloud-conflict', () => {
    void resolveCloudConflict(_cloudConflictProject);
  });
  eventBus.on('autosave:error', () => {
    _localSaveFailing = true;
  });
  for (const name of ['autosave:saved', 'autosave:dirty']) {
    eventBus.on(name, () => {
      if (name === 'autosave:saved') _localSaveFailing = false;
      refreshCloudSyncState();
    });
  }
  for (const name of [
    'writability-changed',
    'auth:state',
    'project:restored',
    'collab:save-adopted',
    'agent:cloud-synced'
  ]) {
    // Deferred a tick: several of these adjust `_lastCloudSyncedAt` in their
    // own listener, registered elsewhere, and the derivation must read the
    // stamp they write rather than race it.
    eventBus.on(name, () => queueMicrotask(refreshCloudSyncState));
  }

  // Load from file
  eventBus.on('toolbar:load-file', async ({ file }) => {
    if (!ensureWritableTab()) return;
    // Fire-and-forget auto-snapshot before load
    revisionStore.save('Before load', '', 'auto');
    // A plain info toast expired after 4s while a large project was still
    // loading, so the longer the load the less feedback the user had. The
    // progress toast lives until the load settles, whichever way it settles.
    const progress = showProgressToast('Loading project…', { id: 'load-project' });
    try {
      sectionManager.stopBackgroundPrefetch({ invalidate: true });
      const result = await loadFromFile(file, { requireWritable: true });
      bridge.clearScroll();
      progress.update('Rendering preview…');
      await renderPreview({ preserveScroll: false, transition: null, hideDuringLoad: false });
      let msg = `Loaded "${result.state.projectName}"`;
      if (result.assetCount > 0) msg += ` with ${result.assetCount} asset(s)`;
      if (result.warningAssetRefs > 0)
        msg += ` (${result.warningAssetRefs} unresolved asset ref(s))`;
      progress.success(msg);
    } catch (err) {
      if (isLoadSupersededError(err)) {
        // Superseded is an expected flow (a newer load won, or the tab is
        // read-only), not a failure — resolve the toast rather than erroring it.
        progress.dismiss();
        if (err.reason === 'read-only') ensureWritableTab();
        else showToast(err.message, 'info');
        return;
      }
      progress.error('Load failed: ' + err.message);
      reportError('Load project', err, { user: 'silent' });
    }
  });

  // Start over — reset to default state
  eventBus.on('toolbar:start-over', async () => {
    if (!ensureWritableTab()) return;
    if (collabBlocksWholeStateReplace('Starting a new project')) return;
    // FN-8: named for what it produces. The consequence is stated with the
    // project's OWN name, because "all unsaved changes" is abstract at exactly
    // the moment it needs not to be.
    const currentName = store.getState()?.projectName?.trim() || 'this project';
    const confirmed = await showConfirmModal({
      title: 'New project',
      message: `Start a new project? Unsaved changes to “${currentName}” will be lost.`,
      confirmLabel: 'New project',
      cancelLabel: 'Keep editing',
      danger: true
    });
    if (!confirmed) return;
    sectionManager.stopBackgroundPrefetch({ invalidate: true });
    await applyLoadedProjectState(createDefaultState(), {
      label: 'start over',
      requireWritable: true
    });
    resetSelection('start-over');
    resetActiveEditable('start-over');
    selectedElementPayload = null;
    bridge.clearScroll();
    renderPreview({ preserveScroll: false, transition: null, hideDuringLoad: false });
  });

  // Code injection modal
  eventBus.on('toolbar:open-code-injection', () => {
    showCodeInjectionModal();
  });

  // Link manager modal
  eventBus.on('toolbar:open-link-manager', () => {
    showLinkManager();
  });

  // Revision history modal
  eventBus.on('toolbar:open-revision-history', () => {
    showRevisionPanel(revisionStore);
  });

  /**
   * History (TL-8). ONE merged timeline over four sources, LAZY-IMPORTED.
   *
   * The lazy import is not a nicety: `boot-tests.js` walks this file's real
   * static graph and fails by name if `ui/history-timeline/index.js`,
   * `ui/state-poster.js` or `preview/preview-bundler.js` appear on it — the
   * poster drags the export graph.
   *
   * THE ESCAPE HATCH (restored after the TL-10 audit). `localStorage[
   * 'pb-history-timeline'] === '0'` falls back to the dialog this replaces,
   * exactly because no browser has driven the replacement checklist yet and
   * this is a RESTORE surface. The new timeline remains the default.
   */
  eventBus.on('toolbar:open-history-panel', (opts = {}) => {
    // Opportunistic: tag the CURRENTLY-SYNCED cloud version with the given
    // name via the dedicated label endpoint. A version is only labeled, never
    // minted; signed-out/local/never-synced is a clean skip. Fire-and-forget —
    // failure never fails the name.
    const tagCloudVersion = async (label) => {
      const state = store.getState();
      const projectId = state?.projectId;
      const version = Number(state?.cloudVersion) || 0;
      if (!isRealUser(currentUser) || !projectId || version < 1) return;
      const { labelProjectVersion } = await import('../templates/auth-api.js');
      await labelProjectVersion(projectId, version, label);
    };

    void (async () => {
      let disabled = false;
      try {
        disabled = localStorage.getItem('pb-history-timeline') === '0';
      } catch {
        // Expected-flow fallback: private browsing denies localStorage. The
        // default is the new surface, so a storage failure never strands a
        // user on the old one.
      }
      if (disabled) {
        const { showHistoryPanel } = await import('./history-panel.js');
        showHistoryPanel({
          store,
          revisionStore,
          showToast,
          showUndoToast,
          focusCheckpoint: !!opts?.focusCheckpoint,
          tagCloudVersion
        });
        return;
      }

      const projectId = store.getState()?.projectId || null;
      const signedIn = isRealUser(currentUser) && !!projectId;
      const { showHistoryTimeline } = await import('./history-timeline/index.js');
      await showHistoryTimeline({
        store,
        revisionStore,
        projectId,
        showToast,
        focusCheckpoint: !!opts?.focusCheckpoint,
        // BC-7: every history entry point opens THIS surface, on its scope —
        // and, from a section's History tab, on that section's newest step.
        scope: typeof opts?.scope === 'string' ? opts.scope : 'all',
        focusSection: typeof opts?.focusSection === 'string' ? opts.focusSection : null,
        tagCloudVersion,
        // A null (not an empty array) is what tells the model there are three
        // sources and no "Saved versions" chip. Signed-out is not an error.
        listVersions: signedIn
          ? async () => {
              const { listProjectVersions } = await import('../templates/auth-api.js');
              const result = await listProjectVersions(projectId, { limit: 20 });
              return result?.versions || [];
            }
          : async () => null,
        getVersionState: signedIn
          ? async (version) => {
              const { getVersionState } = await import('../core/version-state-cache.js');
              return getVersionState(projectId, version);
            }
          : null,
        // ALREADY PROJECT-BOUND: the pin module has no other reason to hold a
        // project id, and binding it here is what keeps it out of one.
        labelProjectVersion: signedIn
          ? async (version, label) => {
              const { labelProjectVersion } = await import('../templates/auth-api.js');
              await labelProjectVersion(projectId, version, label);
            }
          : null,
        // A THUNK, because `collabBlocksWholeStateReplace` is module-private
        // here — and because the answer must be read at the moment of the
        // click, not at the moment the dialog opened.
        isCollabBlocked: () => collabBlocksWholeStateReplace('Restoring a point in time')
      });
    })().catch((err) => {
      reportError('History', err, { user: 'toast', message: 'Could not open History' });
    });
  });

  // Export static site as ZIP — progress modal with AbortController
  /**
   * Publish-log hook (migration 006): after an export or preview save
   * SUCCEEDS, record which cloud version went out. Best-effort by design —
   * exports work signed-out and offline, so every failure here is silent
   * (reportError user:'silent'; the ship already happened and must never look
   * like it failed because bookkeeping did).
   *
   * Version discipline: `cloudSave({skipIfUnchanged:true})` then ALWAYS read
   * `project.latestVersion` — `version.version` is null on {skipped}/{deduped}
   * responses. If the version being marked carries no contentHash (a pre-006
   * row), re-save WITHOUT skipIfUnchanged to mint a hashed one: a publish
   * baseline without a content hash cannot anchor "unpublished changes since".
   */
  async function stageManifestBeforeShip(kind, expectedHash, lock) {
    if (!lock?.text || !lock.artifactHash || !isRealUser(currentUser) || !store.isWritable()) {
      return null;
    }
    // A hosted publish ships the FLAT artifact, so it records as exportMode
    // 'flat'. Without this branch the kind falls to null and the fleet manifest
    // is silently never staged — the site would ship with no artifact record.
    const exportMode =
      kind === 'export-11ty'
        ? '11ty'
        : kind === 'export-astro'
          ? 'astro'
          : kind === 'export-html' || kind === 'site'
            ? 'flat'
            : null;
    const projectId = store.getState()?.projectId;
    if (!exportMode || !projectId) return null;
    const staged = {
      eventId: crypto.randomUUID(),
      projectId,
      principalSub: String(currentUser.sub),
      exportMode,
      lockText: lock.text,
      artifactHash: lock.artifactHash,
      contentHash: expectedHash || null
    };
    await stageExportEvent(staged);
    return staged;
  }

  async function publishAfterShip(kind, target = null, expectedHash = null, stagedManifest = null) {
    try {
      if (!isRealUser(currentUser)) return;
      if (stagedManifest && String(currentUser.sub) !== stagedManifest.principalSub) return;
      if (
        stagedManifest &&
        (!store.isWritable() || store.getState()?.projectId !== stagedManifest.projectId)
      ) {
        await finalizeExportEvent(stagedManifest.eventId, {
          projectPublishId: null,
          projectVersion: null,
          contentHash: expectedHash || null
        });
        return;
      }
      if (!store.isWritable()) return;
      const projectId = stagedManifest?.projectId || store.getState()?.projectId;
      if (!projectId) return;
      const saved = await cloudSave({ skipIfUnchanged: true });

      // THE DRIFT GUARD: the exporter snapshotted state when it STARTED; the
      // save above serialized whatever is live NOW. A collab edit, a queued
      // autosave or a project switch in between makes those different
      // projects' worth of pixels — so the marker only lands on a version
      // whose contentHash equals the hash of the state that actually shipped.
      // No match → no marker (silently): a missing marker is recoverable, a
      // lying "up to date" is not.
      const resolveVersion = async () => {
        const savedRow = saved?.version || null;
        if (!expectedHash) {
          // Defensive fallback (no hash captured): the pre-verification
          // behavior — mark the save's latest.
          return Number(saved?.project?.latestVersion);
        }
        if (savedRow?.contentHash === expectedHash) return Number(savedRow.version);
        // Skipped/deduped saves return no fresh row, and the shipped state may
        // have been minted by the 30s auto-sync earlier — search recent rows.
        const { listProjectVersions } = await import('../templates/auth-api.js');
        const recent = await listProjectVersions(projectId, { limit: 20 });
        const match = (recent?.versions || []).find((row) => row.contentHash === expectedHash);
        if (match) return Number(match.version);
        // Pre-006 baseline: the latest save predates content hashing, so
        // nothing CAN match. If the state hasn't moved since the ship, one
        // unconditional save mints a hashed version — verified like any other
        // (a mint whose hash differs means the state DID move → no marker).
        if (saved?.skipped && !recent?.versions?.[0]?.contentHash) {
          const minted = await cloudSave();
          if (minted?.version?.contentHash === expectedHash) {
            return Number(minted.version.version);
          }
        }
        return NaN;
      };

      // Fleet manifest recording (docs/fleet-contracts.md §4): rides the same
      // resolution as the publish marker, but a failed marker must NOT lose
      // the manifest — the export artifact is real either way, so the record
      // lands with null linkage instead of not at all.
      const recordManifest = async (publishId, version) => {
        if (!stagedManifest) return;
        await finalizeExportEvent(stagedManifest.eventId, {
          projectPublishId: publishId || null,
          projectVersion: Number.isInteger(version) ? version : null,
          contentHash: expectedHash || null
        });
      };

      const version = await resolveVersion();
      if (!Number.isInteger(version) || version < 1) {
        reportError('Publish log', new Error('no version matches the shipped state'), {
          user: 'silent',
          detail: `kind=${kind}: state changed between ${kind} and save — marker skipped`
        });
        await recordManifest(null, null);
        return;
      }
      const published = await markProjectPublished(projectId, version, { kind, target });
      await recordManifest(published?.publish?.id || null, version);
    } catch (err) {
      // Expected-flow fallback: publishing bookkeeping must never surface as
      // an export/preview failure — the artifact already shipped.
      reportError('Publish log', err, { user: 'silent' });
      // The manifest still records (unlinked): the artifact shipped even when
      // the save/marker path failed — losing the record would be silent drift.
      try {
        if (stagedManifest) {
          await finalizeExportEvent(stagedManifest.eventId, {
            projectPublishId: null,
            projectVersion: null,
            contentHash: expectedHash || null
          });
        }
      } catch {
        // Outbox unavailable (private-mode IDB) — nothing more to do here.
      }
    }
  }

  eventBus.on('preview:saved', ({ slug, contentHash }) => {
    void publishAfterShip('preview', slug || null, contentHash || null);
  });

  eventBus.on('toolbar:export', ({ operationToken = null } = {}) =>
    runCommandOperation(
      'export',
      'Preparing…',
      async () => {
        if (!(await ensureProjectNamedBeforeAction())) return;
        const controller = new AbortController();
        const progress = createAmbientExportProgress(controller);

        try {
          const readiness = await readProjectReadiness({ signal: controller.signal });
          if (!readiness) return;
          revisionStore.save('Before export', '', 'auto');
          const { exportSite } = await loadFlatExporterModule();
          progress.update('Exporting…', 0);
          const {
            zipBlob,
            summary,
            report,
            lock,
            contentHash: shippedHash
          } = await exportSite({
            signal: controller.signal,
            stateSnapshot: readiness.snapshot,
            auditReport: readiness.report,
            preflightMode: DOWNLOAD_PREFLIGHT_MODE,
            onFindings: (payload) => eventBus.emit('ship:export-findings', payload),
            onProgress: ({ message, percent }) => {
              progress.update(message, percent);
            }
          });

          let stagedManifest = null;
          try {
            stagedManifest = await stageManifestBeforeShip('export-html', shippedHash, lock);
          } catch (err) {
            reportError('Fleet manifest', err, {
              user: 'silent',
              detail: 'durable staging failed'
            });
          }

          progress.close();

          // Trigger download
          const state = store.getState();
          const name =
            (state.projectName || 'Untitled').replace(/[^a-zA-Z0-9_\- ]/g, '').trim() || 'Untitled';
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${name}.zip`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            URL.revokeObjectURL(url);
            a.remove();
          }, 100);

          if (summary.warnings.length > 0) {
            console.warn('[Export] Warnings:', summary.warnings);
          }
          presentExportReport(report, summary, 'flat');
          void publishAfterShip('export-html', null, shippedHash, stagedManifest);
        } catch (err) {
          // close(), not a dismissal: the export has already failed or already
          // aborted, so re-firing onCancel would abort a dead controller. Idempotent
          // when the user is the one who cancelled and the dialog is already gone.
          progress.close();
          reportExportFailure(err, 'Export');
        }
      },
      operationToken
    )
  );

  // Publish to <slug>.pagestitch.site. Structurally the flat-export handler,
  // with the download swapped for an upload: same audit gate, same exporter,
  // same fleet staging, same publishAfterShip bookkeeping — so a published site
  // and a downloaded ZIP are provably the same bytes.
  // `toolbar:publish-manage` is the destination card's route — "change the
  // address" and "unpublish" live in the modal, which owns slug availability
  // and claimed-site management. It is the SAME handler with the direct branch
  // switched off, never a second implementation.
  eventBus.on('toolbar:publish-manage', ({ operationToken = null } = {}) =>
    runCommandOperation(
      'export',
      'Preparing…',
      () => runPublishFlow({ forceModal: true }),
      operationToken
    )
  );
  eventBus.on('toolbar:publish-site', ({ operationToken = null } = {}) =>
    runCommandOperation(
      'export',
      'Preparing…',
      () => runPublishFlow({ forceModal: false }),
      operationToken
    )
  );

  /**
   * CMD-4 — contracts §5.
   *
   * The BRANCH is `decidePublishRoute()`, a pure function in the command bar.
   * The EFFECTS stay here, because publishing reaches `cloudSave`, the
   * exporter, `revisionStore` and the fleet outbox — none of which a
   * command-bar module may import.
   *
   * The republish path reuses the SAME `onPublish` closure the modal calls.
   * That is the whole point: two publish implementations would drift, and the
   * one that drifted would be the one nobody watches.
   */
  async function runPublishFlow({ forceModal }) {
    try {
      if (!(await ensureProjectNamedBeforeAction())) return;
      const state = store.getState();

      const { decidePublishRoute, describePublishDestination } =
        await import('./command-bar/publish-branch.js');
      const { staticIncompatibleFeatures, projectHasAdoptableForm } =
        await import('../export/feature-registry.js');

      // Read the site BEFORE deciding. `undefined` means the read never
      // happened or failed, and the decider treats that as "open the modal" —
      // never as "never published", which would invite a second address for a
      // project that already has a live one.
      let site;
      const expectedProjectId = state?.projectId || null;
      const expectedEpoch = store.getEpoch();
      const isExpectedProject = () => (store.getState()?.projectId || null) === expectedProjectId;
      const assertExpectedProject = () => {
        if (isExpectedProject()) return;
        const err = new Error('Project changed while publishing');
        err.name = 'AbortError';
        throw err;
      };
      const abortOnProjectSwitch = (controller) =>
        store.subscribe((nextState) => {
          if ((nextState?.projectId || null) !== expectedProjectId) controller.abort();
        });
      assertExpectedProject();
      if (isRealUser(currentUser) && expectedProjectId) {
        const siteReadController = new AbortController();
        const stopWatchingSiteRead = abortOnProjectSwitch(siteReadController);
        try {
          const { getSite } = await import('../publish/publish-api.js');
          const read = await getSite(expectedProjectId, { signal: siteReadController.signal });
          assertExpectedProject();
          site = read?.site || null;
        } catch (err) {
          // A project switch is cancellation, not an unreadable site. Returning
          // here prevents the fallback modal from opening against the old id.
          if (err?.name === 'AbortError' || !isExpectedProject()) return;
          // Expected flow: an unreadable site is "unknown", which routes to the
          // modal. It is deliberately not reported — the modal reports it, and
          // reporting twice for one click is noise.
          site = undefined;
        } finally {
          stopWatchingSiteRead();
        }
      }

      const decision = decidePublishRoute({
        signedIn: isRealUser(currentUser),
        projectId: expectedProjectId,
        writable: store.isWritable(),
        incompatible: staticIncompatibleFeatures(state?.features || {}),
        site
      });

      if (decision.route === 'auth') {
        showToast(decision.reason, 'error');
        eventBus.emit('toolbar:auth-show');
        return;
      }
      if (decision.route === 'no-project' || decision.route === 'read-only') {
        showToast(decision.reason, decision.route === 'read-only' ? 'info' : 'error');
        return;
      }

      // Keep the destination card current whatever route we take.
      // BC-3: a failed read is "could not check", never "not published" — the
      // decider above already refuses to treat `undefined` as null, and the card
      // it sits beside must not contradict it.
      commandBar?.destination?.setPublishDestination(
        site === undefined
          ? { status: 'error' }
          : {
              site,
              cloudVersion: Number.isInteger(state?.cloudVersion) ? state.cloudVersion : null,
              publishedVersion: Number.isInteger(site?.version) ? site.version : null
            }
      );
      void describePublishDestination;

      const initialReadiness = await readProjectReadiness();
      assertExpectedProject();
      if (!initialReadiness) return;

      const publishDirectly = decision.route === 'direct' && !forceModal;

      const { openPublishSiteModal } = await import('./publish-site-modal.js');
      assertExpectedProject();
      const modalOptions = {
        projectId: state.projectId,
        projectName: state.projectName,
        features: state.features,
        // The flag says `forms` is on for every untouched project; only the
        // content says whether a form is actually there to break.
        hasAdoptableForm: projectHasAdoptableForm(state),
        readiness: initialReadiness,
        readReadiness: async () => {
          assertExpectedProject();
          const nextReadiness = await readProjectReadiness();
          assertExpectedProject();
          return nextReadiness;
        },
        assertCurrent: assertExpectedProject,
        onNavigate: (finding) => eventBus.emit('pre-publish-audit:navigate', finding),
        // Runs only after the user commits, after a slug exists, and after the
        // modal has re-read the exact snapshot it showed.
        onPublish: async ({ readiness, recognition, activateAt = null }) => {
          assertExpectedProject();
          const recognizedReport = recognition
            ? { ...readiness.report, publishRecognition: recognition }
            : readiness.report;
          revisionStore.save('Before publish', '', 'auto');
          // Save BEFORE exporting, unlike the download paths which save after.
          // The server resolves which cloud version shipped by looking up the
          // state's content_hash; if the state was never saved, nothing matches
          // and the publish would be recorded with a null version. A failed
          // pre-ship save must stop the publish rather than create that drift.
          const controller = new AbortController();
          const stopWatchingPublish = abortOnProjectSwitch(controller);
          try {
            await cloudSave({
              skipIfUnchanged: true,
              expectedEpoch,
              expectedProjectId
            });
            assertExpectedProject();

            const progress = createAmbientExportProgress(controller);
            try {
              const { exportSite } = await loadFlatExporterModule();
              const {
                files,
                assetBlobs,
                lock,
                report,
                summary,
                contentHash: shippedHash
              } = await exportSite({
                collectFiles: true,
                signal: controller.signal,
                stateSnapshot: readiness.snapshot,
                auditReport: recognizedReport,
                preflightMode: EXPORT_PREFLIGHT_MODE,
                onFindings: (payload) => eventBus.emit('ship:export-findings', payload),
                onProgress: ({ message, percent }) => {
                  // The export owns the first 40% of the bar; the upload owns
                  // the rest, so the bar never goes backwards.
                  progress.update(message, percent * 0.4);
                }
              });
              assertExpectedProject();

              let stagedManifest = null;
              try {
                stagedManifest = await stageManifestBeforeShip('site', shippedHash, lock);
              } catch (err) {
                reportError('Fleet manifest', err, {
                  user: 'silent',
                  detail: 'durable staging failed'
                });
              }
              assertExpectedProject();

              const { publishSite } = await import('../publish/publish-api.js');
              assertExpectedProject();
              const result = await publishSite({
                projectId: state.projectId,
                files,
                assetBlobs,
                contentHash: shippedHash,
                artifactHash: lock?.artifactHash || null,
                lockText: lock?.text || null,
                activateAt,
                signal: controller.signal,
                onProgress: ({ message, percent }) => {
                  progress.update(message, 40 + percent * 0.6);
                }
              });
              assertExpectedProject();
              progress.close();

              presentExportReport(report, summary, 'publish', {
                message: result?.scheduled ? 'Your site is scheduled' : 'Your site is ready'
              });
              // NOT publishAfterShip: it would call markProjectPublished and mint
              // a SECOND publish row. The server already wrote the `site` row
              // inside the same D1 batch that flipped the site live — that is
              // what makes "live" and "logged" impossible to disagree — so all
              // that remains here is linking the fleet manifest to it. A publish
              // the server recorded with no version resolves to null linkage,
              // which is the same honest outcome the export drift guard produces.
              if (stagedManifest) {
                void finalizeExportEvent(stagedManifest.eventId, {
                  projectPublishId: result?.publishId || null,
                  projectVersion: Number.isInteger(result?.version) ? result.version : null,
                  contentHash: shippedHash || null
                }).catch((err) =>
                  reportError('Fleet manifest', err, { user: 'silent', detail: 'finalize failed' })
                );
              }
              return result;
            } catch (err) {
              progress.close();
              throw err;
            }
          } finally {
            stopWatchingPublish();
          }
        }
      };

      // ── the direct branch ──
      // A republish of a site that already exists, at an address the user
      // already chose, with compatible packs and a writable tab. There is no
      // decision left to put a dialog in front of.
      //
      // It still runs the readiness gate: `preparePublishAttempt` is what makes
      // "reveal the blockers, then publish that reviewed snapshot" work, and
      // skipping it would ship a project whose blocking findings nobody saw.
      if (publishDirectly) {
        const attempt = await prepareDirectPublish(modalOptions, initialReadiness);
        assertExpectedProject();
        if (!attempt) {
          // Blocking findings the user has not acknowledged. The modal is the
          // only surface that can SHOW them, so this falls through to it rather
          // than failing silently.
          const revealed = await openPublishSiteModal(modalOptions);
          assertExpectedProject();
          if (revealed?.published) showToast('Your site is live', 'success');
          else if (revealed?.scheduled) showToast('Publish scheduled', 'success');
          return;
        }
        const result = await modalOptions.onPublish(attempt);
        assertExpectedProject();
        eventBus.emit('publish:result', { ok: true, result });
        showToast('Your site is live', 'success');
        return;
      }

      const outcome = await openPublishSiteModal(modalOptions);
      assertExpectedProject();
      if (outcome?.published) {
        eventBus.emit('publish:result', { ok: true, result: outcome });
        showToast('Your site is live', 'success');
      } else if (outcome?.scheduled) {
        eventBus.emit('publish:result', { ok: true, result: outcome });
        showToast('Publish scheduled', 'success');
      }
    } catch (err) {
      eventBus.emit('publish:result', { ok: false, error: err });
      if (err?.name !== 'AbortError') reportExportFailure(err, 'Publish');
    }
  }

  /**
   * The readiness gate, for the branch that has no modal to host it.
   *
   * Returns `{readiness, recognition}` when the snapshot is publishable, or
   * null when there are blocking findings the user has not seen — which is a
   * signal to fall through to the modal, NOT a failure. Blocking findings that
   * nobody has been shown must never publish, and must never be swallowed.
   */
  async function prepareDirectPublish(options, readiness) {
    const { disposeFindings } = await import('../shared/finding-disposition.js');
    const disposed = disposeFindings(
      readiness?.report?.findings || [],
      readiness?.snapshot?.auditAccepts
    );
    if ((disposed.counts?.blocking || 0) > 0) return null;
    void options;
    return { readiness, recognition: null };
  }

  // Export as 11ty project — show settings modal first, then export
  eventBus.on('toolbar:export-11ty', async ({ operationToken = null } = {}) => {
    const token =
      operationToken == null
        ? beginOperation('export', { label: 'Preparing…' })
        : isOperationTokenActive('export', operationToken)
          ? operationToken
          : null;
    if (token === null) return;
    let operationSettled = false;
    // The toolbar goes busy on click (symmetry with the html branch), so every
    // exit route from here on — including "user cancelled Export Settings" —
    // has to clear it or the Export button stays busy for the rest of the session.
    const clearExportBusy = () => {
      if (operationSettled) return;
      operationSettled = true;
      endOperation(token);
      eventBus.emit('toolbar:operation-state', { type: 'export', busy: false });
    };
    eventBus.emit('toolbar:operation-state', {
      type: 'export',
      busy: true,
      label: 'Preparing…'
    });
    if (!(await ensureProjectNamedBeforeAction())) {
      clearExportBusy();
      return;
    }
    const exportState = store.getState();
    const currentFeatures = { ...(exportState.features || {}) };
    const currentPages = Array.isArray(exportState.pages) ? exportState.pages : [];
    let showExportSettingsModal;
    try {
      ({ showExportSettingsModal } = await loadExportSettingsModule());
    } catch (err) {
      clearExportBusy();
      reportError('Open export settings', err, {
        user: 'status',
        message: 'Could not open export settings',
        detail: err?.message || 'export settings failed to load'
      });
      return;
    }
    showExportSettingsModal(
      currentFeatures,
      currentPages,
      async ({ features, pageIds, pwaInstall, esignTemplates }) => {
        if (!(await ensureProjectNamedBeforeAction())) {
          clearExportBusy();
          return;
        }
        // features is null if no toggles changed — skip the state mutation
        if (features) store.update('features', features, 'Update export features');
        // Must land BEFORE exportSiteAs11ty(): the exporter reads the app icon and
        // screenshots straight off state, so committing after would export the
        // previously-saved ones and silently discard what the user just picked.
        if (pwaInstall) store.update('pwaInstall', pwaInstall, 'Update PWA app identity');
        // Same ordering requirement: the exporter reads esignTemplates straight
        // off state to build the seed payload, so committing after the export
        // would ship the previously-saved templates.
        if (esignTemplates)
          store.update('esignTemplates', esignTemplates, 'Update contract templates');
        eventBus.emit('toolbar:operation-state', {
          type: 'export',
          busy: true,
          label: 'Preparing…'
        });

        const controller = new AbortController();
        const progress = createAmbientExportProgress(controller);

        try {
          const readiness = await readProjectReadiness({ pageIds, signal: controller.signal });
          if (!readiness) return;
          revisionStore.save('Before export', '', 'auto');
          const { exportSiteAs11ty } = await loadEleventyExporterModule();
          progress.update('Exporting…', 0);
          const {
            zipBlob,
            summary,
            report,
            lock,
            contentHash: shippedHash
          } = await exportSiteAs11ty({
            signal: controller.signal,
            pageIds,
            stateSnapshot: readiness.snapshot,
            auditReport: readiness.report,
            preflightMode: DOWNLOAD_PREFLIGHT_MODE,
            onFindings: (payload) => eventBus.emit('ship:export-findings', payload),
            onProgress: ({ message, percent }) => {
              progress.update(message, percent);
            }
          });

          let stagedManifest = null;
          try {
            stagedManifest = await stageManifestBeforeShip('export-11ty', shippedHash, lock);
          } catch (err) {
            reportError('Fleet manifest', err, {
              user: 'silent',
              detail: 'durable staging failed'
            });
          }

          progress.close();

          // Trigger download — use slug for consistent folder name (matches SETUP.html commands)
          const state = store.getState();
          const name =
            (state.projectName || 'untitled')
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '') || 'untitled';
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${name}.zip`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            URL.revokeObjectURL(url);
            a.remove();
          }, 100);

          if (summary.warnings.length > 0) {
            console.warn('[Export 11ty] Warnings:', summary.warnings);
          }
          presentExportReport(report, summary, '11ty');
          void publishAfterShip('export-11ty', null, shippedHash, stagedManifest);
        } catch (err) {
          progress.close();
          reportExportFailure(err, '11ty export');
        } finally {
          clearExportBusy();
        }
      },
      clearExportBusy
    );
  });

  // The Astro handler mirrors the 11ty one exactly, INCLUDING its ordering
  // rule: pwaInstall and esignTemplates are committed to the store BEFORE
  // the export runs, because the exporter reads them straight off state and
  // committing after would ship the previously-saved values.
  eventBus.on('toolbar:export-astro', async ({ operationToken = null } = {}) => {
    const token =
      operationToken == null
        ? beginOperation('export', { label: 'Preparing…' })
        : isOperationTokenActive('export', operationToken)
          ? operationToken
          : null;
    if (token === null) return;
    let operationSettled = false;
    // The toolbar goes busy on click (symmetry with the html branch), so every
    // exit route from here on — including "user cancelled Export Settings" —
    // has to clear it or the Export button stays busy for the rest of the session.
    const clearExportBusy = () => {
      if (operationSettled) return;
      operationSettled = true;
      endOperation(token);
      eventBus.emit('toolbar:operation-state', { type: 'export', busy: false });
    };
    eventBus.emit('toolbar:operation-state', {
      type: 'export',
      busy: true,
      label: 'Preparing…'
    });
    if (!(await ensureProjectNamedBeforeAction())) {
      clearExportBusy();
      return;
    }
    const exportState = store.getState();
    const currentFeatures = { ...(exportState.features || {}) };
    const currentPages = Array.isArray(exportState.pages) ? exportState.pages : [];
    let showExportSettingsModal;
    try {
      ({ showExportSettingsModal } = await loadExportSettingsModule());
    } catch (err) {
      clearExportBusy();
      reportError('Open export settings', err, {
        user: 'status',
        message: 'Could not open export settings',
        detail: err?.message || 'export settings failed to load'
      });
      return;
    }
    showExportSettingsModal(
      currentFeatures,
      currentPages,
      async ({ features, pageIds, pwaInstall, esignTemplates }) => {
        if (!(await ensureProjectNamedBeforeAction())) {
          clearExportBusy();
          return;
        }
        // features is null if no toggles changed — skip the state mutation
        if (features) store.update('features', features, 'Update export features');
        // Must land BEFORE exportSiteAsAstro(): the exporter reads the app icon and
        // screenshots straight off state, so committing after would export the
        // previously-saved ones and silently discard what the user just picked.
        if (pwaInstall) store.update('pwaInstall', pwaInstall, 'Update PWA app identity');
        // Same ordering requirement: the exporter reads esignTemplates straight
        // off state to build the seed payload, so committing after the export
        // would ship the previously-saved templates.
        if (esignTemplates)
          store.update('esignTemplates', esignTemplates, 'Update contract templates');
        eventBus.emit('toolbar:operation-state', {
          type: 'export',
          busy: true,
          label: 'Preparing…'
        });

        const controller = new AbortController();
        const progress = createAmbientExportProgress(controller);

        try {
          const readiness = await readProjectReadiness({ pageIds, signal: controller.signal });
          if (!readiness) return;
          revisionStore.save('Before export', '', 'auto');
          const { exportSiteAsAstro } = await loadAstroExporterModule();
          progress.update('Exporting…', 0);
          const {
            zipBlob,
            summary,
            report,
            lock,
            contentHash: shippedHash
          } = await exportSiteAsAstro({
            signal: controller.signal,
            pageIds,
            stateSnapshot: readiness.snapshot,
            auditReport: readiness.report,
            preflightMode: DOWNLOAD_PREFLIGHT_MODE,
            onFindings: (payload) => eventBus.emit('ship:export-findings', payload),
            onProgress: ({ message, percent }) => {
              progress.update(message, percent);
            }
          });

          let stagedManifest = null;
          try {
            stagedManifest = await stageManifestBeforeShip('export-astro', shippedHash, lock);
          } catch (err) {
            reportError('Fleet manifest', err, {
              user: 'silent',
              detail: 'durable staging failed'
            });
          }

          progress.close();

          // Trigger download — use slug for consistent folder name (matches SETUP.html commands)
          const state = store.getState();
          const name =
            (state.projectName || 'untitled')
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '') || 'untitled';
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${name}.zip`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            URL.revokeObjectURL(url);
            a.remove();
          }, 100);

          if (summary.warnings.length > 0) {
            console.warn('[Export Astro] Warnings:', summary.warnings);
          }
          presentExportReport(report, summary, 'astro');
          void publishAfterShip('export-astro', null, shippedHash, stagedManifest);
        } catch (err) {
          progress.close();
          reportExportFailure(err, 'Astro export');
        } finally {
          clearExportBusy();
        }
      },
      clearExportBusy
    );
  });

  // Re-render only for undo/redo — regular mutations already trigger their own re-renders.
  // Without this filter, every mutation (theme color commit, text edit commit, section ops)
  // would cause a redundant full re-render via history-changed, resetting scroll position.
  eventBus.on('history-changed', ({ source, label, patches, oldState, newState }) => {
    // Dismiss stale undo toasts on any history position change. Every undo chip id
    // carries the `undo-` prefix (D9), so ONE broadened sweep covers section-delete,
    // text-edit, image/logo/history-restore chips — and the engine helper also catches
    // QUEUED undo records. With transactional records, dismiss = commit = no-op, and the
    // current event creates its own chip AFTER this sweep, so ordering stays correct.
    dismissToastsByPrefix('undo-');
    if (
      source === 'mutation' &&
      label === 'Edit text' &&
      Array.isArray(patches) &&
      patches.length > 0
    ) {
      _showTextEditUndoChip(patches);
    }
    const hasPatches = Array.isArray(patches) && patches.length > 0;
    const isSurgicalMutation =
      source === 'mutation' && SECTION_HTML_SURGICAL_LABELS.has(label) && hasPatches;
    const isHistoryRestore =
      source === 'mutation' && String(label || '').startsWith('Restore:') && hasPatches;
    if (
      source === 'undo' ||
      source === 'redo' ||
      isSurgicalMutation ||
      isHistoryRestore ||
      (source === 'mutation' && label === 'Edit text' && hasPatches)
    ) {
      void renderHistoryChangeWithFeedback({ source, label, patches, oldState, newState });
    }
  });

  // Image/SVG replacement feedback
  eventBus.on('editing:image-replaced', (data = {}) => {
    // Phase 11: replace/swap mints a new assetId and orphans the previous one.
    scheduleSessionAssetGC();
    if (data?.undoToastHandled) return;
    const noun = kindLabel(data.kind || readActiveEditable()?.kind || 'photo');
    showUndoToast(`${noun} updated`, { id: 'undo-image-replace' });
  });
  eventBus.on('editing:image-removed', () => scheduleSessionAssetGC());
  eventBus.on('editing:decorative-removed', () => scheduleSessionAssetGC());
  eventBus.on('editing:logo-replaced', (data = {}) => {
    scheduleSessionAssetGC();
    // A section commonly holds a desktop mark AND a mobile-menu mark. The
    // replace touches both, so the undo — which IS the confirmation here — says
    // how far it reached rather than letting the user discover the second one
    // on the shipped site.
    const slotCount = Number(data?.slotCount) || 1;
    const message = slotCount > 1 ? `Logo updated in ${slotCount} places` : 'Logo updated';
    showUndoToast(message, { id: 'undo-logo-replace' });
  });
  eventBus.on('editing:logo-mutated', (data = {}) => {
    showUndoToast(data.label || 'Logo updated', { id: 'undo-logo-edit' });
  });
  eventBus.on('editing:svg-replaced', (data = {}) => {
    const noun = kindLabel(data.kind || readActiveEditable()?.kind || 'icon');
    showUndoToast(`${noun} updated`, { id: 'undo-svg-replace' });
  });
  eventBus.on('editing:error', (payload = {}) => {
    const message = payload.message || 'Could not complete the editing action';
    reportError('Visual editor', new Error(message), { user: 'toast', message });
  });

  // Command palette actions — relay to appropriate handlers
  eventBus.on('palette:open-add-section', () => {
    if (sectionList) sectionList.openAddModal();
  });
  eventBus.on('cmd:duplicate-section', () => {
    const primary = selection.getPrimary();
    if (!primary) return;
    if (selection.isMulti()) {
      eventBus.emit('selection:request', {
        intent: 'replace',
        id: primary,
        source: 'single-target-collapse'
      });
    }
    sectionManager.duplicateSection(primary);
  });
  eventBus.on('cmd:copy-section', () => {
    copySelectedSection();
  });
  eventBus.on('cmd:paste-section', () => {
    pasteSectionAfterSelection();
  });
  eventBus.on('cmd:delete-section', async () => {
    // Captured before the awaits below — removeSection awaits a confirm modal
    // and a preview render, either of which can outlive the current selection.
    const primary = selection.getPrimary();
    if (primary && selection.isMulti()) {
      eventBus.emit('selection:request', {
        intent: 'replace',
        id: primary,
        source: 'single-target-collapse'
      });
    }
    if (primary) {
      const { ran: removed } = await sectionService.remove(primary, { announce: true });
      if (removed) {
        eventBus.emit('section-deselected');
      }
    }
  });
  eventBus.on('cmd:move-section-up', () => {
    moveSelectedSectionByShortcut('up');
  });
  eventBus.on('cmd:move-section-down', () => {
    moveSelectedSectionByShortcut('down');
  });
  // Current-page palette commands resolve the active page here, then reuse the
  // existing toolbar:* page-op handlers (rename is handled by the toolbar
  // itself — it owns the inline tab editor).
  eventBus.on('cmd:duplicate-current-page', () => {
    const page = store.getActivePage();
    if (page) eventBus.emit('toolbar:duplicate-page', { pageId: page.id });
  });
  eventBus.on('cmd:delete-current-page', () => {
    const page = store.getActivePage();
    if (page) eventBus.emit('toolbar:delete-page', { pageId: page.id });
  });
  eventBus.on('toolbar:show-shortcuts', () => showShortcutsHelp());
  eventBus.on('toolbar:open-command-palette', () => openCommandPalette());

  /**
   * IR-6 — "a copy of the page as it looks to you".
   *
   * Built from the UNREDACTED state, deliberately. A structure-only preview
   * renders a blank page and would be worthless, so this is the one artifact in
   * the whole bundle that carries the client's real content — which is exactly
   * why it is Tier 2, opt-in per report, never remembered, and shown to the
   * user before it is sent.
   *
   * `staticCapture` freezes motion so a below-fold section is not captured at
   * opacity 0. It does NOT touch `SHOT_REV`, which versions the saved-preview
   * shot key and has nothing to do with this.
   *
   * The 20 MB cap throws with the bundler's own legible message ("Reduce image
   * sizes or remove sections…"), and the dialog shows that sentence rather than
   * a generic failure — it tells the user something they can act on.
   */
  async function buildReportPreviewBlob() {
    const { buildPreviewBundle } = await import('../preview/preview-bundler.js');
    const { html } = await buildPreviewBundle(store.getState(), { staticCapture: true });
    return new Blob([html], { type: 'text/html' });
  }

  // IR-4. Dynamic so a dialog that opens rarely does not sit in the boot graph.
  eventBus.on('toolbar:report-issue', async ({ cause = null } = {}) => {
    try {
      const [{ openIssueReportDialog }, { captureScreenFrame, screenCaptureSupported }] =
        await Promise.all([
          import('./issue-report-dialog.js'),
          import('../core/screen-capture.js')
        ]);
      // IR-5. The seam is supplied ONLY where the browser can honour it: the
      // dialog renders no row for an absent capture function, so a build
      // without `getDisplayMedia` offers no control rather than a control that
      // does nothing.
      openIssueReportDialog({
        cause,
        capture: {
          ...(screenCaptureSupported() ? { screen: () => captureScreenFrame() } : {}),
          preview: buildReportPreviewBlob
        }
      });
    } catch (err) {
      reportError('Issue report', err, { user: 'toast', message: 'Could not open the reporter.' });
    }
  });
  eventBus.on('toolbar:open-issue-reports', async () => {
    try {
      const { openIssueReportsDialog } = await import('./issue-report-dialog.js');
      openIssueReportsDialog();
    } catch (err) {
      reportError('Issue report', err, { user: 'toast', message: 'Could not open your reports.' });
    }
  });

  // Incrementally update section overrides CSS without full re-render.
  // Batched at rAF cadence to prevent visual churn during slider/input drags.
  eventBus.on('theme:override-changed', queueOverridePush);

  // Nav HTML changed (structural — needs full re-render)
  eventBus.on('nav:html-changed', () => {
    renderPreview({ preserveScroll: true, transition: null, hideDuringLoad: false });
  });

  // Explicit preview re-render requests from editing flows that mutate
  // section structure (e.g., SVG replace/swap) and cannot be patched in-place.
  eventBus.on('preview:render-request', ({ preserveScroll = true } = {}) => {
    if (!preserveScroll) bridge.clearScroll();
    renderPreview({ preserveScroll, transition: null, hideDuringLoad: false });
  });

  eventBus.on('preview:sync-section-html', handlePreviewSyncSectionHTML);

  eventBus.on('iframe:bridge-ready', syncIframeInteractionMode);

  eventBus.on('store:write-rejected', () => {
    ensureWritableTab();
  });

  // ── Agent chat context tags ──
  // The composer owns the list; the canvas mirrors it. Full-set pushes keep
  // the iframe from having to reason about adds vs removes.
  let aiContextTargets = [];
  eventBus.on('agent:context-refs-changed', ({ refs = [] } = {}) => {
    aiContextTargets = refs.map((ref) => ({
      instanceId: ref.instanceId,
      ...(ref.eid ? { eid: ref.eid } : {})
    }));
    bridge.send('set-ai-context', { targets: aiContextTargets });
  });
  // A re-render builds a fresh document, so the tags have to be re-pushed.
  // Off-page refs simply find no wrapper and are skipped.
  eventBus.on('iframe:bridge-ready', () => {
    if (aiContextTargets.length) bridge.send('set-ai-context', { targets: aiContextTargets });
    if (isPickModeActive()) bridge.send('set-ai-pick-mode', { active: true });
    // Selection chrome vanishes with the old document too (16A0.6). Re-stamp
    // the current selection — no flash and no scroll, because this is
    // restoration, not navigation. selectAfterRender's one-shot (undo, add,
    // template load) still performs its own send; the iframe-side stamp is
    // idempotent, so the overlap is harmless.
    const primary = selection.getPrimary();
    if (primary) bridge.send('set-selected-section', { instanceId: primary, flash: false });
    const selectedIds = selection.getIds();
    if (selectedIds.length > 1) {
      bridge.send('set-multi-selected-sections', {
        instanceIds: selectedIds,
        leadId: selection.getLead()
      });
    }
  });

  // 16A1 — canvas reflection of a multi-selection. `data-pb-section-selected`
  // stays single-valued (the lead) so every existing consumer keeps working;
  // the co-selected wrappers get `data-pb-section-multi` and the lead a
  // `data-pb-multi-count` suffix, stamped iframe-side from this one message.
  // An empty list clears the multi attrs — that path runs on every collapse.
  eventBus.on('selection:changed', (change) => {
    if (change.size > 1) {
      bridge.send('set-multi-selected-sections', {
        instanceIds: change.ids,
        leadId: change.lead
      });
      // The scalar follows the lead. flash:false — a range sweep must not
      // strobe; navigation flashes stay owned by the legacy click paths.
      bridge.send('set-selected-section', { instanceId: change.lead, flash: false });
      return;
    }
    bridge.send('set-multi-selected-sections', { instanceIds: [], leadId: null });
    if (change.size === 1) {
      // A collapse from a request gesture never travels a legacy event, so
      // stamp the survivor here. Legacy paths re-send with their own flash —
      // same id, idempotent.
      bridge.send('set-selected-section', { instanceId: change.lead, flash: false });
    }
  });

  // Hovering a chip points the canvas at what it refers to. Never switches
  // pages — a hover should not navigate.
  eventBus.on('agent:context-ref-hovered', ({ ref } = {}) => {
    if (!ref?.instanceId) return;
    if (ref.pageId !== 'shared' && ref.pageId !== store.getActivePage()?.id) return;
    bridge.send('scroll-to-eid', {
      instanceId: ref.instanceId,
      ...(ref.eid ? { eid: ref.eid } : {})
    });
    bridge.send('flash-section', { instanceId: ref.instanceId });
  });

  // Pick mode and the format painter both reinterpret every canvas click, so
  // only one may be armed at a time.
  eventBus.on('ai-picker:entered', () => {
    if (isPaintModeActive()) exitPaintMode();
    bridge.send('set-ai-pick-mode', { active: true });
  });
  eventBus.on('ai-picker:exited', () => bridge.send('set-ai-pick-mode', { active: false }));
  eventBus.on('format-painter:entered', () => {
    if (isPickModeActive()) exitPickMode();
  });

  // Section-click side effects. The selection store itself is written by the
  // controller (registered before wireEvents, so it has already run); this
  // handler keeps the paint/pick side effects and the iframe chrome send.
  eventBus.on('iframe:section-clicked', (data) => {
    if (isPaintModeActive()) exitPaintMode();
    // Pick mode consumes the click: clicking section chrome tags the whole
    // section rather than selecting it, and the mode stays on for the next
    // pick. The controller skips its store write in the same case.
    if (isPickModeActive() && data?.instanceId) {
      eventBus.emit('agent:add-context-ref', {
        ref: { kind: 'section', instanceId: data.instanceId }
      });
      return;
    }
    selectedElementPayload = null;
    if (data?.instanceId) {
      bridge.send('set-selected-section', {
        instanceId: data.instanceId,
        flash: !data.fromIframe
      });
    }
  });
  eventBus.on('section-deselected', () => {
    selectedElementPayload = null;
    if (isPickModeActive()) exitPickMode();
    bridge.send('clear-selected-section', { reason: 'parent-deselected' });
  });
  // Element-level selection tracker — feeds the keyboard shortcut precedence
  // so Cmd+C/X/V acts on the focused editable when one is selected, falling
  // through to the section clipboard otherwise.
  eventBus.on('iframe:element-clicked', (data) => {
    const isDomTarget = data?.targetRef?.domain === 'dom' && !!data?.eid;
    // Pick mode outranks the painter and consumes the click entirely: the
    // element is tagged, not selected, and the mode stays on so several
    // places can be tagged in a row.
    if (isPickModeActive() && data?.instanceId && (isDomTarget || data?.targetRef)) {
      eventBus.emit('agent:add-context-ref', {
        ref: isDomTarget
          ? {
              kind: 'element',
              instanceId: data.instanceId,
              eid: data.eid,
              editableType: data.editableType || '',
              elementKind: data.kind || '',
              tagName: data.tagName || '',
              label: data.label || ''
            }
          : { kind: 'section', instanceId: data.instanceId }
      });
      return;
    }
    // Format painter: while paint mode is active, every editable click is
    // interpreted as "paint that target", not "select that element". The
    // painter handler runs first so the panel registry's selection effect
    // still fires after — paint commits the override, then the painted
    // element is naturally re-selected by the existing flow.
    if (isPaintModeActive() && data?.instanceId && isDomTarget) {
      applyPaintFromPayload(data);
    }
    selectedElementPayload = isDomTarget ? normalizeElementClipboardPayload(data) : null;
    // The section-store side of this event (including the sticky no-instanceId
    // fallback this line used to carry) now lives in the selection controller.
    if (data?.instanceId) {
      bridge.send('set-selected-section', { instanceId: data.instanceId, flash: false });
    }
  });
  eventBus.on('iframe:selected-editable-cleared', () => {
    selectedElementPayload = null;
  });
  // 16C2 — a restyle that could not reach every selected unit says so. Fired
  // once per resolution (not per write), so a 60 Hz scrub does not repeat it.
  eventBus.on('element-restyle:partial', (data) => {
    const applied = data?.applied || 0;
    const skipped = data?.skipped || 0;
    if (!skipped) return;
    showToast(
      `Applied to ${applied} of ${applied + skipped} — ${skipped} differ${skipped === 1 ? 's' : ''} in structure`,
      { id: 'element-restyle-partial', type: 'info' }
    );
  });
  eventBus.on('section:hover', (data) => {
    if (data?.fromIframe) return;
    bridge.send('set-hovered-section', { instanceId: data?.instanceId || null });
  });

  // Click on empty canvas — cancel paint mode if active. The deselect that
  // used to live here moved into the selection controller: it runs before this
  // handler (registered first) and has already cleared the store, so a guard
  // here on the current selection would always read empty and the
  // `section-deselected` echo would never fire. The controller emits it
  // instead, with the same was-anything-selected guard this site used to have.
  eventBus.on('iframe:canvas-clicked', () => {
    if (isPaintModeActive()) exitPaintMode();
  });

  // Track editing state so shortcuts don't fire during text editing
  eventBus.on('editing:text-started', () => {
    isEditing = true;
    announceEditStatus('Editing started');
  });
  eventBus.on('editing:text-ended', (data) => {
    isEditing = false;
    announceEditStatus(data?.cancelled ? 'Edit reverted' : 'Edit saved');
    void _drainRenderQueue();
  });
  eventBus.on('iframe:edit-cancel-confirmation-requested', () => {
    showToast('Press Escape again to revert this edit.', {
      id: 'text-edit-cancel-warning',
      duration: 3000
    });
  });
  eventBus.on('iframe:format-noop', () => {
    showToast('Select text or place the caret in a word to format it.', {
      id: 'text-format-noop',
      duration: 2600
    });
  });

  eventBus.on('iframe:iframe-error', (payload) => {
    __routeIframeErrorEventForTests(payload);
  });

  // Global keyboard shortcuts (single source of truth).
  window.addEventListener('keydown', (e) => {
    void handleGlobalShortcut(e);
  });

  // Forwarded shortcuts from iframe focus context.
  eventBus.on('iframe:shortcut-keydown', (data) => {
    void handleGlobalShortcut(data, { fromIframe: true });
  });

  // Section control actions from iframe — delegate to section manager
  eventBus.on('iframe:section-action', async (data) => {
    if (data.action === 'delete') {
      // Captured BEFORE the removal awaits: once the section is gone,
      // state-changed prunes it out of the selection store, so a post-await
      // read of the primary would always be null and the deselect below would
      // never fire for the section the user actually had selected.
      const wasSelected = selection.getPrimary() === data.instanceId;
      const { ran: removed } = await sectionService.remove(data.instanceId, { announce: true });
      if (removed && wasSelected) {
        eventBus.emit('section-deselected');
      }
    } else if (data.action === 'duplicate') {
      await sectionManager.duplicateSection(data.instanceId);
    } else if (data.action === 'reset') {
      await sectionService.reset(data.instanceId, { announce: true });
    } else if (data.action === 'swap') {
      // BC-5: one Replace browser (ui/replace-browser.js), anchored to the
      // control that asked when the emitter had one.
      eventBus.emit('section:request-swap', {
        instanceId: data.instanceId,
        triggerEl: data.triggerEl || null
      });
    }
  });
}

// ── Unified search registry ──────────────────────────────────────────────
//
// Populates command-palette `_actions` with three additional sets so a
// single Cmd+K / `/` keystroke finds everything searchable in the app:
//
//   - 'page'     — every page in the current project (dynamic; refreshed
//                  on every state change).
//   - 'section'  — every section in section-index.json (~4.6k entries;
//                  hidden from empty-query render, capped at MAX_RESULTS).
//   - 'template' — every entry in template-catalog.json + the user's
//                  cloud-saved templates from /api/templates/list.
//
// All three sources go through `loadJSON` whose parsed cache is shared
// with Palette._load() / openTemplateSelector() — no double fetch.

function _syncPageActions(state) {
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  const actions = pages.map((page) => ({
    id: `switch-page:${page.id}`,
    label: page.name || 'Untitled page',
    shortcut: '—',
    section: 'Pages',
    keywords: ['page', 'switch', page.slug || ''].filter(Boolean),
    meta: { pageId: page.id },
    handler: () => eventBus.emit('toolbar:switch-page', { pageId: page.id })
  }));
  replaceActionsForType('page', actions);
}

function _buildSectionActions(sectionIndex) {
  if (!Array.isArray(sectionIndex)) return [];
  return sectionIndex.map((sec) => ({
    id: `add-section:${sec.catalogKey}`,
    label: sec.description || sec.catalogKey || sec.sectionId || 'Section',
    shortcut: '—',
    section: sec.category || 'Section',
    keywords: [
      sec.catalogKey,
      sec.category,
      sec.sectionId,
      ...(Array.isArray(sec.industries) ? sec.industries : [])
    ].filter(Boolean),
    meta: { catalogKey: sec.catalogKey, category: sec.category },
    handler: () =>
      eventBus.emit('palette:add-section', {
        catalogKey: sec.catalogKey,
        category: sec.category,
        position: null
      })
  }));
}

function _buildTemplateActions(entries) {
  if (!Array.isArray(entries)) return [];
  return entries
    .map((tpl) => {
      const siteName = tpl.siteName || tpl.name || '';
      if (!siteName) return null;
      return {
        id: `load-template:${tpl._slug || siteName}`,
        label: siteName,
        shortcut: '—',
        section: 'Templates',
        keywords: [siteName, tpl.siteUrl, tpl.industry].filter(Boolean),
        meta: { siteName, slug: tpl._slug || null },
        // Route through the modal so confirm-replace + custom-template
        // loading aren't bypassed. openTemplateSelector honours `preselect`
        // and jumps directly to the preview.
        handler: () => eventBus.emit('toolbar:open-templates', { preselect: siteName })
      };
    })
    .filter(Boolean);
}

let _paletteSearchPrefetched = false;

// The section index (2.2MB) and slim template catalog (~1MB) only feed the
// Cmd+K search surface — nothing at first paint needs them. Pull them OFF the
// critical boot path: schedule during idle, and also trigger on the first
// palette open so an early Cmd+K still populates. Idempotent via the guard.
function prefetchPaletteSearchEntries() {
  if (_paletteSearchPrefetched) return;
  _paletteSearchPrefetched = true;

  // Sections — load the index; loadJSON's cache means a later Palette/section-
  // list request is a hit, not a second fetch.
  loadJSON('data/section-index.json')
    .then((sectionIndex) => {
      replaceActionsForType('section', _buildSectionActions(sectionIndex));
    })
    .catch((err) => {
      console.warn('[Shell] palette search: section-index unavailable —', err);
    });

  // Templates — static catalog first, then merge cloud-saved templates when
  // listTemplates() resolves (SWR returns instantly from cache + revalidates).
  loadJSON('data/template-catalog-index.json')
    .catch(() => loadJSON('data/template-catalog.json'))
    .then((staticCatalog) => {
      const staticArr = Array.isArray(staticCatalog) ? staticCatalog : [];
      replaceActionsForType('template', _buildTemplateActions(staticArr));

      const { data: cachedListResp, fresh: freshListPromise } = listTemplates();
      const cachedCustom = Array.isArray(cachedListResp?.templates) ? cachedListResp.templates : [];
      if (cachedCustom.length) {
        replaceActionsForType('template', _buildTemplateActions([...staticArr, ...cachedCustom]));
      }
      freshListPromise
        ?.then((freshResp) => {
          if (!freshResp) return; // 304 / no change
          const freshCustom = Array.isArray(freshResp.templates) ? freshResp.templates : [];
          replaceActionsForType('template', _buildTemplateActions([...staticArr, ...freshCustom]));
        })
        .catch(() => {
          /* network failures are non-fatal for search */
        });
    })
    .catch((err) => {
      console.warn('[Shell] palette search: template-catalog unavailable —', err);
    });
}

function registerPaletteSearchEntries() {
  // Pages — synchronous, refresh on every state change. Cheap; stays eager.
  _syncPageActions(store.getState());
  eventBus.on('state-changed', _syncPageActions);

  // A very early Cmd+K should still get sections/templates — prefetch on the
  // first open too (guard makes it a no-op if idle already fired it).
  eventBus.on('command-palette:opened', prefetchPaletteSearchEntries);

  // Otherwise fold the two multi-MB fetches into idle time so they never
  // compete with first paint. Fallback to a macrotask where rIC is absent.
  const schedule =
    typeof requestIdleCallback === 'function'
      ? (fn) => requestIdleCallback(fn, { timeout: 3000 })
      : (fn) => setTimeout(fn, 0);
  schedule(prefetchPaletteSearchEntries);
}

export async function init() {
  // Scrub an invitation bearer before auth, API calls, asset loads, or any
  // other boot work can expose the current URL as a same-origin Referer. The
  // session-scoped copy survives an external authentication round-trip.
  inviteTokenForSession();

  // BC-14: one category map for every surface that names a section. The
  // section list and palette fetch the same pinned file at boot; this makes
  // the synchronous readers (command bar, editors' context lines) see it too.
  // Never rejects — a failed fetch falls back to title-cased category ids.
  void getCategoryMap();

  // Get DOM elements
  const toolbarEl = document.getElementById('pb-toolbar');
  const paletteEl = document.getElementById('pb-palette');
  const centerEl = document.getElementById('pb-center');
  previewIframe = document.getElementById('pb-preview');
  _primaryPreviewIframe = previewIframe;
  _secondaryPreviewIframe = null;

  if (!toolbarEl || !paletteEl || !centerEl || !previewIframe) {
    // Throw rather than return: index.html routes init() rejections to the
    // boot-failure panel, and a silent return would leave the user staring at
    // the loading placeholder forever.
    throw new Error('[Shell] Missing required DOM elements');
  }

  // Arm the boot entrance: hide the shell regions (opacity:0) so the empty
  // containers never flash through as the auth screen fades out. Revealed by
  // playAppEntrance() once the canvas has rendered. See builder.css.
  // index.html already carries `pb-app-booting` statically so the arming
  // happens on the first painted frame; this is an idempotent backstop.
  document.querySelector('.pb-app')?.classList.add('pb-app-booting');

  // Start the mobile viewport register before any boot-time phone decisions
  // (sidebar collapse, pointer-kind publication, safe-area/keyboard tokens).
  initViewportService();

  // Sidebar open/close — shell.js is the single source of truth. Respect the
  // persisted collapse state (default open on first run); during onboarding,
  // always start open so the section-list coach mark is visible. Phones
  // always boot collapsed: a persisted desktop "expanded" state would
  // otherwise cover the whole canvas with the drawer on load.
  //
  // Applied here, BEFORE revealBootShell() paints the loading skeleton below —
  // both localStorage reads and isPhoneViewport() are synchronous and need no
  // network/IDB round trip, so there is no reason to wait. Doing this after
  // auth (as it used to) let the skeleton always paint "open", then visibly
  // snap closed once auth/autosave resolved for any user whose real state is
  // collapsed — see boot-tests.js's ordering assertion on this line.
  const onboardingDone = (() => {
    try {
      const raw = localStorage.getItem('pb-onboarding');
      return raw ? JSON.parse(raw).completed === true : false;
    } catch {
      return false;
    }
  })();
  const startCollapsed = isPhoneViewport() || (onboardingDone && _readLeftPanelCollapsed());
  paletteEl.classList.toggle('pb-collapsed', startCollapsed);

  markPreviewIframeAsActive(previewIframe);

  // Keep modal overlays stacked and inert-managed when multiple dialogs are open.
  initModalStacking();

  // Flush pending autosaves on tab hide/close so the debounce window can't
  // swallow the last edit.
  initUnloadGuard(store);

  // Elect one writer tab so a second tab on the same project can't silently
  // clobber the shared autosave slot (Phase 2 multi-tab safety).
  initTabGuard();
  configureProjectLifecycleObserver(({ operation, lifecycle, signal }) => {
    recordSessionBoundaryMetric('lifecycle_classification', { operation, lifecycle, signal });
  });
  configureLoadedProjectGuard(async (loadedState) => {
    // A destination load is an Agent Review exit. Restore the live After state
    // and release the non-destructive review blocker before the load path's
    // post-claim writability assertion runs.
    if (_agentReviewSession || _heldPreviewBlocker() !== null) {
      await exitAgentReview('project-change');
    }
    const targetProjectId = loadedState?.projectId || null;
    const currentProjectId = store.getState()?.projectId || null;
    const nonTransitionable = store
      .getBlockers()
      .filter(
        (blocker) =>
          blocker !== WRITE_BLOCKER.TAB_GUARD &&
          blocker !== WRITE_BLOCKER.AGENT_REVIEW &&
          blocker !== WRITE_BLOCKER.DETACHED_PREVIEW &&
          blocker !== WRITE_BLOCKER.PROJECT_UNAVAILABLE &&
          blocker !== WRITE_BLOCKER.SESSION_CLAIM
      );
    if (nonTransitionable.length) return false;
    const acquired = await prepareTabGuardForProject(targetProjectId);
    if (acquired && targetProjectId !== currentProjectId) {
      clearProjectUnavailable();
      store.removeBlocker(WRITE_BLOCKER.SESSION_CLAIM);
      removeSessionClaimBanner();
    }
    return acquired;
  });

  // Initialize tooltip system (before components so dynamic elements are handled)
  initTooltips();
  initReportError({ showToast });
  // IR-1. Subscriptions and two global handlers — no call site anywhere is
  // edited to "add a breadcrumb", so there is no instrumentation to drift.
  // Nothing is sent: this is an in-memory buffer a diagnostics bundle reads if
  // a user ever files a report, and it dies with the tab otherwise. Wrapped
  // because a diagnostics buffer must never be able to break the app it
  // observes, nor block the shell reveal.
  try {
    installBreadcrumbTaps();
    installFetchTap();
    // IR-4: the same two global events, but the user-facing half — a toast
    // whose one action offers to file what just happened.
    installUnhandledErrorToasts();
  } catch (err) {
    reportError('Diagnostics', err, { user: 'silent' });
  }

  // SB-3a: keep the intended-project pointer current. Hooked to the EVENT
  // rather than to the six `project:restored` emit sites, so a seventh call site
  // cannot forget it and leave boot resolving a stale project.
  eventBus.on('project:restored', ({ projectId } = {}) => {
    if (projectId) writeLastOpenedProject(store.getPrincipalScope(), projectId);
    // SB-3b: the project changed, so the lock this tab should hold changed with
    // it. Same chokepoint as the pointer, for the same reason — a future
    // transition cannot forget to rebind.
    void rebindTabGuardForProject();
    // Carried debt from SB-1: the unavailable latch is per-PROJECT state living
    // in module scope. Without this, opening a healthy project after one went
    // unavailable inherits the previous project's banner and blocker.
    clearProjectUnavailable();
  });

  // SB-2: the cross-tab lifecycle channel. Principal is read at post/receive
  // time, never captured — this tab can sign in and out while the channel
  // lives. Configured before auth resolves so a message arriving during boot is
  // simply ignored (no principal ⇒ nothing accepted) rather than missed.
  configureLifecycleChannel({ getPrincipal: () => currentUser?.sub || null });
  subscribeLifecycle((msg) => {
    if (msg.kind === LIFECYCLE_MESSAGE.PROJECT_DELETED) {
      // Only OUR open project. A sibling tab deleting some other project is
      // none of this tab's business — that coupling is what the channel
      // deliberately does not create.
      if (!msg.projectId || store.getState()?.projectId !== msg.projectId) return;
      void enterProjectUnavailable(msg.signal);
      return;
    }
    if (msg.kind === LIFECYCLE_MESSAGE.SIGNED_OUT) {
      // SB-3b: slots and locks are per-principal, so this tab must stop writing
      // an account that is no longer signed in. Blocking is the whole action —
      // the work stays in memory, and the reload path decides what happens next.
      store.addBlocker(WRITE_BLOCKER.AUTH);
      _cloudSyncPaused = true;
      syncIframeInteractionMode();
      showToast('You signed out in another tab. This tab is now read-only.', {
        type: 'info',
        duration: 10000,
        id: 'signed-out-elsewhere'
      });
    }
  });

  // Start auth, then reveal a disabled chrome skeleton while the Worker checks
  // the session. The check has a transport deadline: an unreachable Worker
  // falls back to local/offline editing, while a real 401 still opens sign-in.
  // Project/editor state still waits below; this only replaces the full-screen
  // "Loading…" placeholder with the app frame.
  const authPromise = ensureAuthenticated();
  revealBootShell();
  await authPromise;
  eventBus.on('auth:state', () => void maybeShowInviteAccept());
  maybeShowInviteAccept();

  // Any authed call that 401s after this point (expired/revoked session) pauses
  // cloud sync and prompts re-auth. Registered post-gate so the expected
  // "not signed in yet" 401s during ensureAuthenticated don't trigger it.
  setUnauthorizedHandler(pauseCloudSync);

  // SB-3a. Bind local persistence to the signed-in account BEFORE any project
  // is loaded or any autosave can fire — a slot written under the wrong scope is
  // exactly the cross-account recovery leak the scoping exists to prevent. This
  // is safe here because project state does not exist until the lines below.
  store.setPrincipalScope(currentUser?.sub || LOCAL_PRINCIPAL_SCOPE);
  // SB-5: the unseen-shares count for the switcher entry point. Fire-and-forget
  // — a recipient with no notification needs a reason to open the switcher, and
  // a failed count must never delay boot.
  if (isRealUser(currentUser)) void refreshUnseenShares();
  // Copy the pre-SB-3 global slot into its own (principal, project) slot. The
  // legacy copy is deliberately NOT deleted in this release: an interrupted
  // migration must leave a readable slot, and a rollback to pre-SB-3 code must
  // still find one. SB-3b removes it.
  await store.migrateLegacyAutosaveSlot();

  // Always provide a usable baseline state after auth has been established.
  // This keeps the pre-auth phase free of project/editor state.
  store.init(createDefaultState());

  // Pre-set the stage-active body class so SectionList's first render picks
  // the compact mini empty-state instead of flashing the full hero before
  // the canvas stage mounts. The stage takes over class management as soon
  // as it shows/hides.
  const _firstPage = store.getActivePage();
  if (_firstPage && Array.isArray(_firstPage.sections) && _firstPage.sections.length === 0) {
    document.body.classList.add('pb-stage-active');
  }

  // Initialize section manager and page manager with render/toast callbacks
  sectionManager.init({
    renderPreview,
    showToast,
    showUndoToast,
    confirmDelete: showConfirmModal,
    promptSwapChoice: showSwapChoiceModal
  });
  pageManager.init({
    renderPreview,
    showToast,
    showUndoToast,
    finishPendingEdit: commitActiveTextEdit
  });

  // Initialize drag reorder (listens for iframe:section-moved)
  initDragReorder();

  // Initialize WYSIWYG editing (text + image/SVG)
  initTextEditor({ flushPendingEdit });
  initImageEditor();
  initLogoEditor();

  // CMD-1 — wire the command-bar action services to the real editors.
  //
  // Injected rather than imported directly by the services: `text-editor.js`
  // and `image-editor.js` both transitively reach `link-popover.js`, which
  // calls `window.addEventListener` at MODULE SCOPE, and `image-editor.js`
  // starts fetching site-effect assets on load. A service that imported them
  // would be unloadable under node — untestable in exactly the way the
  // initiative's own "no shell.js import" rule exists to prevent, just through
  // a different door. Same pattern as `editing/collab-guard.js`.
  //
  // Inert until CMD-5/CMD-6 build the panes that call them.
  setTextEditorImpl({
    applyFormatCommand,
    getLatestSelectionState,
    isBulkFormattingEnabled
  });
  setImageEditorImpl(imageEditorModule);
  setLogoEditorImpl(logoEditorModule);

  // Element-insertion engine: register pattern adapters, wire context menu,
  // and wire the menu→mutation action handlers. The library load is async
  // fire-and-forget; action handlers surface a user-facing toast if the user
  // tries to open the social picker before the library resolves.
  registerAdapter(socialLinkAdapter);
  registerAdapter(navLinkAdapter);
  registerAdapter(headerElementAdapter);
  registerAdapter(faqItemAdapter);
  registerAdapter(footerLinkAdapter);
  registerAdapter(footerColumnAdapter);
  registerAdapter(buttonGroupItemAdapter);
  registerAdapter(cardItemAdapter);
  registerAdapter(galleryImageAdapter);
  // Generic adapters follow every specialized family because resolution is
  // first-match-wins. The inferred repeat adapter is the final fallback.
  registerAdapter(csItemCardAdapter);
  // Last-resort structural adapter for inferred non-CodeStitch/Shopify cards.
  registerAdapter(genericRepeatItemAdapter);
  // Freeform blocks register LAST so no specialized adapter is ever shadowed;
  // chain payloads reach it via resolveAdapterKindForPayload's section-type
  // fallback (chainProbe cannot express section[id^="freeform-"]).
  registerAdapter(freeformBlockAdapter);
  // Phase 1: DERIVE the reorder members of the surgical-sync allow-set from the
  // registered adapters, so each new draggable kind's history label lights up
  // without hand-editing SECTION_HTML_SURGICAL_LABELS. Runs after registration
  // (adapters register at boot, after this module is evaluated).
  for (const historyLabel of getReorderableHistoryLabels()) {
    SECTION_HTML_SURGICAL_LABELS.add(historyLabel);
  }
  loadJSON('data/social-icon-library.json')
    .then((json) => {
      setSocialPlatforms(json?.platforms || []);
    })
    .catch((err) => {
      console.warn('[shell] social-icon-library.json failed to load:', err?.message || err);
    });
  initElementContextMenu();
  initDecorativeContextMenu();
  initSectionContextMenu();
  initDecorativeActionHandlers();
  initElementActions({ showToast, showUndoToast });

  // Find & Replace — widget is lazy-created on first open. Actions module
  // subscribes to widget events once the widget exists. Single instance per
  // session, kept around so its query/results persist across re-opens.
  let _findReplaceWidget = null;
  let _findReplaceActionsTeardown = null;
  eventBus.on('find-replace:open', (opts = {}) => {
    if (!_findReplaceWidget) {
      _findReplaceWidget = new FindReplaceWidget();
      _findReplaceActionsTeardown = initFindReplaceActions(_findReplaceWidget);
    }
    _findReplaceWidget.open(opts);
  });

  // Initialize theme editing (colors + fonts)
  initThemeEditor();
  initFontSelector();

  // Mirror the theme onto the parent document root so glass-panel swatch CSS
  // (which reads bare `var(--primary)` etc.) resolves the project's real palette
  // instead of hardcoded fallbacks. Catch-all: state-changed fires on load,
  // undo/redo, and every edit (init() → notify()). Live per-token pushes go
  // through setThemeVar; this keeps the parent root in sync for everything else.
  mirrorThemeToParentRoot(store.getState()?.theme);
  eventBus.on('state-changed', () => mirrorThemeToParentRoot(store.getState()?.theme));

  // Initialize UI components (registers state-changed listeners)
  // CMD-2 — the two-row frame lands atomically. `CommandBar` renders both rows
  // and their named compatibility slots; the legacy `Toolbar` then renders into
  // the `row1.legacy` slot, unchanged apart from the two controls that moved to
  // row 2's anchors (undo/redo and the viewport controls), which are now owned
  // there. Exactly one owner per control — asserted by command-bar-tests.
  //
  // CMD-3 — row 1 is now the command bar's: identity, pages, state and
  // destination all have real owners. The boot auth payload is INJECTED rather
  // than imported, because the gate signs the user in BEFORE this line runs, so
  // the `auth:state` emission from `ensureAuthenticated()` has no listener yet
  // and the account chip would stay hidden until the next auth event.
  // CMD-8b — the skeleton goes the moment the thing it was promising exists.
  // NOT at the entrance animation: on a phone both are `position: fixed` at the
  // bottom of the screen, so an overlap would stack two bars; and once the real
  // bar is on screen a skeleton of it is simply a lie.
  unmountBootSkeleton(toolbarEl);
  commandBar = new CommandBar(toolbarEl, previewIframe, {
    initialAuthPayload: getCurrentUser()
  });
  _toolbar = new Toolbar(commandBar.getSlot('row1.legacy'), previewIframe);

  // BC-3 — the Publish menu's destination card follows the active PROJECT. It
  // was only ever written from inside the publish flow, so it showed the last
  // project's address after a switch, and "Not published yet" for a live site
  // until someone clicked Publish.
  commandBar.destination?.setDestinationSource({
    getProjectId: () => store.getState()?.projectId || null,
    read: async (projectId, { signal }) => {
      if (!isRealUser(currentUser) || !projectId) {
        return { site: null, cloudVersion: null, publishedVersion: null };
      }
      const { getSite } = await import('../publish/publish-api.js');
      const read = await getSite(projectId, { signal });
      const site = read?.site || null;
      const cloudVersion = store.getState()?.cloudVersion;
      return {
        site,
        cloudVersion: Number.isInteger(cloudVersion) ? cloudVersion : null,
        publishedVersion: Number.isInteger(site?.version) ? site.version : null
      };
    }
  });
  onProjectIdentityChange(store, () => commandBar?.destination?.resetDestination());
  eventBus.on('auth:state', () => commandBar?.destination?.resetDestination());

  // CMD-4 — the save popover's three group summaries. INJECTED, not imported:
  // `revisionStore` lives here, and a command-bar module that imports the shell
  // is unloadable under node (gate 5). Each returns a cheap summary or null;
  // null renders the group's heading and route without a count, which is the
  // honest degradation rather than a missing group.
  setSavePopoverSources({
    sessionHistory: () => {
      const entries = store.history?.entries?.length || 0;
      return entries
        ? { count: entries, detail: `${entries} edit${entries === 1 ? '' : 's'} this session` }
        : { count: 0, detail: 'No edits yet this session.' };
    },
    // BC-2: `revisionStore.list()` is ASYNC. This read `.length` off the
    // Promise, which is undefined, so the group said "No checkpoints saved yet."
    // for every project that had some. The popover already renders an async
    // summary (the published group does), so returning the Promise is the fix.
    checkpoints: async () => {
      const list = typeof revisionStore.list === 'function' ? await revisionStore.list() : [];
      const rows = Array.isArray(list) ? list : [];
      return rows.length
        ? { count: rows.length, detail: `Most recent: ${rows[0]?.name || 'unnamed'}` }
        : { count: 0, detail: 'No checkpoints saved yet.' };
    },
    published: async () => {
      const expectedProjectId = store.getState()?.projectId || null;
      if (!isRealUser(currentUser) || !expectedProjectId) {
        return { detail: 'Not published yet.' };
      }
      const [{ listProjectVersions }, { EXPORT_PUBLISH_KINDS }] = await Promise.all([
        import('../templates/auth-api.js'),
        import('./review-changes.js')
      ]);
      const listing = await listProjectVersions(expectedProjectId, { limit: 1 });
      if ((store.getState()?.projectId || null) !== expectedProjectId) return null;
      const latest = listing?.latestExportPublish || null;
      if (!latest || !EXPORT_PUBLISH_KINDS.includes(latest.kind)) {
        return { detail: 'Not published yet.' };
      }
      const kindLabel =
        latest.kind === 'site'
          ? 'Website publish'
          : latest.kind === 'export-11ty'
            ? '11ty export'
            : 'HTML export';
      return {
        detail: `${kindLabel} · version ${Number(latest.version) || 'unknown'}`
      };
    }
  });

  // The compact (phone) toolbar is two rows tall, so the static
  // --pb-toolbar-height token understates it. Publish the real height for
  // layout that hangs below the toolbar (e.g. the sidebar drawer top edge).
  if (typeof ResizeObserver === 'function') {
    const toolbarHeightObserver = new ResizeObserver(() => {
      const height = toolbarEl.offsetHeight;
      if (height > 0) {
        document.documentElement.style.setProperty('--pb-toolbar-height-actual', `${height}px`);
      }
    });
    toolbarHeightObserver.observe(toolbarEl);
  }

  // NavSidebar: text tabs (Sections | Design | Client/SEO) and panes.
  // SectionList renders in the Sections pane; PropertyPanel and theme UI mount in Design.
  const navSidebar = new NavSidebar(paletteEl);
  sectionList = new SectionList(navSidebar.sectionsContainer, { mode: 'sidebar' });
  _propertyPanel = new PropertyPanel({
    themeContainer: navSidebar.themeContainer,
    sectionContainer: navSidebar.sectionContainer
  });
  new ClientSeoPanel(navSidebar.clientSeoContainer);
  initPrePublishAuditActions({ showToast, showProgressToast, getCurrentUser });

  // Phase 16A0 — the selection controller, sole writer of the selection store.
  //
  // Deliberately BEFORE wireEvents(): eventBus dispatches in registration
  // order, so the store is updated first and every shell handler that reads
  // `selection.getPrimary()` during the same event sees the settled value.
  // This ordering is also why the canvas-click deselect echo lives in the
  // controller — see its header.
  initSelectionController();

  // CMD-1 — the active-editable controller, sole writer of the parent-side
  // "which editable is selected" store. Same ordering rationale as the two
  // controllers around it: registered before wireEvents() so the store is
  // settled by the time any shell handler reads it during the same event.
  //
  // Additive for now. `glass-panels.js`, `property-panel.js` and
  // `glass-resize-handles.js` still keep their own private copies of this value
  // — converging them is deliberately out of this phase, because changing four
  // surfaces at once leaves no way to attribute a regression.
  initActiveEditableController();

  // Phase 16C1 — the element-selection controller, sole writer of the sibling
  // (repeat-root) selection store. Same ordering rationale: it must settle
  // before wireEvents()' `iframe:element-clicked` handler tracks the payload
  // the keyboard tier reads.
  initElementSelection();

  // Phase 16C2 — descendant restyling. Installs the fan-out resolver into the
  // shared element-override write path, so every panel that already calls
  // applyElementOverrides restyles the whole sibling selection without any of
  // them changing. No-op until a multi-selection exists.
  initElementRestyle();

  // UX Pack F4c — the align/distribute bar, docked over the preview wrap.
  // No-op until a freeform-block multi-selection exists.
  {
    const alignMount = previewIframe?.closest?.('.pb-iframe-wrap');
    if (alignMount) {
      initElementAlignBar({ mount: alignMount, showToast, showUndoToast });
    }
  }

  // UX Pack F5b — pinned review comments (member surface). Signed-in cloud
  // projects only; the first list call latches server-side disablement (the
  // kill switch) and the overflow items stay hidden for everyone else.
  if (isRealUser(currentUser)) {
    const memberCommentsApi = {
      list: async (opts) => {
        const projectId = store.getState()?.projectId;
        const { listProjectComments } = await import('../templates/auth-api.js');
        return listProjectComments(projectId, opts);
      },
      createThread: async (payload) => {
        const projectId = store.getState()?.projectId;
        const { createCommentThread } = await import('../templates/auth-api.js');
        return createCommentThread(projectId, payload);
      },
      reply: async (threadId, body) => {
        const projectId = store.getState()?.projectId;
        const { replyToCommentThread } = await import('../templates/auth-api.js');
        return replyToCommentThread(projectId, threadId, body);
      },
      setStatus: async (threadId, status) => {
        const projectId = store.getState()?.projectId;
        const { setCommentThreadStatus } = await import('../templates/auth-api.js');
        return setCommentThreadStatus(projectId, threadId, status);
      },
      deleteThread: async (threadId) => {
        const projectId = store.getState()?.projectId;
        const { deleteCommentThread } = await import('../templates/auth-api.js');
        return deleteCommentThread(projectId, threadId);
      }
    };
    initCommentController({
      api: memberCommentsApi,
      getProjectKey: (state) => state?.projectId || null
    });
    initCommentPins({ getContentElement: () => _glassOverlay?.getContentElement?.() || null });
    initCommentSidebar({ showToast });
    // Threads are project-wide but the canvas only shows one page, so going to
    // a thread may mean switching pages first. Injected rather than imported:
    // the guest review shell shares comment-controller and must not pull
    // section-manager (a mutation writer) into its graph.
    setCommentNavigator({ setActivePage: (pageId) => pageManager.setActivePage(pageId) });
    const syncCommentMenuItems = ({ disabled } = {}) => {
      const hasCloudProject = !!Number(store.getState()?.cloudVersion);
      const show = hasCloudProject && !disabled;
      const commentsItem = document.getElementById('pb-comments-menu-btn');
      if (commentsItem) commentsItem.hidden = !show;
      if (commentsItem && show) {
        const unseen = getUnseenOpenCount();
        // FN-8: write into the LABEL, not the button. The row carries a hint
        // describing what a recipient can do, and `textContent` on the button
        // would delete it along with the label's own element.
        const labelEl = commentsItem.querySelector('.pb-cb-menu-item-label') || commentsItem;
        labelEl.textContent = unseen > 0 ? `Comments... (${unseen} new)` : 'Comments...';
      }
    };
    eventBus.on('comments:changed', syncCommentMenuItems);
    eventBus.on('toolbar:open-comments', () => showCommentsPanel({ showToast }));
    syncCommentMenuItems();
  }

  // Wire up event handlers
  wireEvents();

  // Unified search registry. Sections + templates load from disk (cache-
  // backed via loadJSON), pages snapshot off state and refresh on changes.
  // All three populate command-palette's `_actions` so Cmd+K / `/` becomes
  // a single search surface.
  registerPaletteSearchEntries();

  // crm-bridge B2: ?openProject=<id> runs INSTEAD of autosave recovery when
  // present — the user (or the CRM's openUrl) explicitly chose a project.
  // Requires a signed-in user; otherwise it falls through to normal boot and
  // the param is stripped inside the helper either way.
  let deepLink = { opened: false, suppressAutosaveRestore: false };
  const openProjectParam = (() => {
    try {
      return new URLSearchParams(window.location.search || '').get('openProject') || '';
    } catch {
      return '';
    }
  })();
  if (openProjectParam && isRealUser(currentUser)) {
    deepLink = await openProjectFromUrl(openProjectParam);
  }

  // Check for auto-save recovery. checkAutoSave() is awaited here, upstream of
  // the first renderPreview below, so we restore the last session SYNCHRONOUSLY
  // now — no default-state flash, no race window (the old banner let the default
  // state paint and be edited behind it). A dismissible toast offers "Start fresh".
  // SB-1 (A7): also skipped when the LINKED project was authoritatively
  // unavailable — restoring a different project there reads as the link having
  // worked, which is how the deleted-project ghost stayed invisible.
  //
  // SB-3a: boot now resolves an INTENDED project (deep link → last-opened
  // pointer → none) and probes THAT slot, rather than restoring whichever
  // project happened to be autosaved last. `intendedProjectId` may be null on a
  // first run, in which case the legacy global slot is still the fallback.
  const skipAutosave = deepLink.opened || deepLink.suppressAutosaveRestore;
  const intendedProjectId = readLastOpenedProject(store.getPrincipalScope());
  const autoSave = skipAutosave ? null : await checkAutoSave({ projectId: intendedProjectId });

  if (autoSave) {
    try {
      await restoreAutoSave(autoSave.state);
      // SB-3a: record what boot actually landed on. Restoring from the LEGACY
      // slot is exactly the case with no pointer yet, so writing it here is what
      // makes the next boot precise instead of falling back again.
      writeLastOpenedProject(store.getPrincipalScope(), store.getState()?.projectId);
      // SB-3b: boot's guard was built before a project existed, so it is holding
      // the global lock. Move it onto this project's lock now that we have one.
      // This restore path does NOT emit project:restored, so it needs its own
      // call — the event chokepoint covers the other five transitions.
      await rebindTabGuardForProject();
      showToast(`Restored your last session (${new Date(autoSave.savedAt).toLocaleString()})`, {
        type: 'info',
        duration: 10000,
        id: 'autosave-restored',
        action: { label: 'Start fresh', onClick: () => void startFreshFromBoot() }
      });
    } catch (err) {
      reportError('Autosave recovery', err, {
        user: 'status',
        message: 'Could not restore your last session',
        detail: err?.message || 'restore of the autosaved session failed'
      });
    }

    // If signed in and the cloud copy is newer than the just-restored autosave,
    // fetch + apply it in the background (does not block first paint). Epoch-
    // guarded inside tryRestore: if the user edits during the download, their
    // work is preserved and the newer version is offered via toast instead.
    void (async () => {
      try {
        const result = await tryRestoreFromCloudIfFresher({ user: currentUser });
        if (result?.lifecycle === LIFECYCLE.UNAVAILABLE) {
          // SB-1 (I1). THE original bug: this branch did not exist, so a stale
          // tab restored a deleted project from the autosave slot and said
          // nothing. Note it fires only when the project was previously
          // confirmed in cloud — a never-synced project 404s correctly and must
          // stay fully editable.
          await enterProjectUnavailable(result.signal);
        } else if (result?.restored) {
          eventBus.emit('project:restored', {
            projectId: result.projectId,
            version: result.version
          });
          showToast(`Loaded cloud version v${result.version}`, 'info');
        } else if (result?.pendingNewer) {
          const { projectId, version: v } = result.pendingNewer;
          showToast(`A newer cloud version (v${v}) is available`, {
            type: 'info',
            duration: 10000,
            id: 'cloud-pending-newer',
            action: {
              label: 'Load cloud version',
              onClick: () => void loadPendingCloudVersion(projectId, v)
            }
          });
        }
      } catch (err) {
        console.warn('[Shell] cloud restore check failed:', err?.message || err);
      }
    })();
  }

  // sidebar:toggle (user-initiated) toggles. sidebar:ensure-open (automation) only opens.
  // startCollapsed/pb-collapsed were already applied to paletteEl above, before
  // revealBootShell(); this just announces the resolved state once listeners
  // (NavSidebar etc.) exist to hear it.
  eventBus.emit('sidebar:state-changed', { open: !startCollapsed });
  bindEmptyStageRail(paletteEl);

  const closeSidebarIfOpen = () => {
    if (paletteEl.classList.contains('pb-collapsed')) return false;
    paletteEl.classList.add('pb-collapsed');
    _writeLeftPanelCollapsed(true);
    eventBus.emit('sidebar:state-changed', { open: false });
    return true;
  };

  // The boot read above is a one-shot. Crossing INTO phone width later — a
  // tablet rotated to portrait, a desktop window dragged narrow — used to leave
  // the expanded ~372px drawer sitting over a ~360px canvas with no way to see
  // what was behind it. Only the into-phone direction acts: growing back out is
  // not a reason to reopen a drawer the user chose to close.
  onViewportChange(({ phone, reason }) => {
    if (reason !== 'breakpoint' || !phone) return;
    closeSidebarIfOpen();
  });

  eventBus.on('sidebar:toggle', () => {
    paletteEl.classList.toggle('pb-collapsed');
    const isOpen = !paletteEl.classList.contains('pb-collapsed');
    _writeLeftPanelCollapsed(!isOpen);
    eventBus.emit('sidebar:state-changed', { open: isOpen });
    if (isOpen) {
      eventBus.emit('props:request-tab', { tab: 'sections' });
    }
  });

  // Click/tap outside sidebar to close it. pointerdown (not mousedown) so a
  // touch on the phone drawer backdrop closes it without waiting for the
  // synthesized mouse event.
  document.addEventListener('pointerdown', (e) => {
    const targetEl = e.target instanceof Element ? e.target : e.target?.parentElement;
    if (e.target instanceof Node && paletteEl.contains(e.target)) return;
    if (targetEl?.closest('.pb-spi-drawer')) return;
    if (isOwnedPopoverTarget(e.target, paletteEl)) return;
    if (
      isOwnedPopoverTarget(
        e.target,
        document.querySelector('.pb-spi-drawer:not(.pb-spi-drawer-leaving)')
      )
    )
      return;
    if (targetEl?.closest('.pb-sl-popover-floating')) return;
    // A kit popover anchored beside a sidebar row (the variant picker) is not
    // "outside" the sidebar either — it is the sidebar's own errand.
    if (targetEl?.closest('.pb-coll-popover')) return;
    // Nor is a click inside a MODAL: a dialog stacked over everything is not
    // the canvas, and the sidebar behind it is not even interactable. BC-5
    // found this through the variant picker's keep-edits question — pressing
    // its Cancel collapsed the sidebar, and the popover dismissed itself on
    // that, so Cancel closed the picker it was meant to return to.
    if (targetEl?.closest('.pb-modal-overlay')) return;
    if (targetEl?.closest('#pb-sidebar-toggle')) return;
    // Nor is a press inside a glass element panel. Selecting an element keeps the rail open
    // (LG-1, unless the rail covers it), so the panel opens beside an open rail. Collapsing on
    // the PRESS shifted the canvas, the panel moved under the pointer, and the release missed
    // the tab it was pressed on: the click was lost every time, motion on or off (design
    // system SYS-6, finding 1; CP-E).
    if (targetEl?.closest('.pb-glass-panel')) return;
    // Guided-tour surfaces (scrim/card/hub) are not "outside" the sidebar for
    // dismissal purposes — clicking them must NOT collapse the sidebar, or a
    // tour step that highlights a sidebar target would invalidate its own
    // highlight the moment the user reaches for it.
    if (targetEl?.closest('.pb-tour-layer, .pb-tour-card, .pb-tour-hub')) return;
    closeSidebarIfOpen();
  });

  // The preview iframe is sandboxed without allow-same-origin, so parent code
  // must not access iframe.contentDocument. Use bridge events for inside-iframe
  // clicks to keep "click outside sidebar closes it" behavior.
  eventBus.on('iframe:canvas-clicked', () => {
    closeSidebarIfOpen();
  });
  eventBus.on('iframe:section-clicked', () => {
    closeSidebarIfOpen();
  });

  // LG-1 — the rail yields when it is covering what you just selected.
  //
  // The rail overlays 222px of live canvas by design (builder.css shifts the
  // canvas by only half the rail's width, betting the overlapped strip is empty
  // margin). That bet loses on a full-bleed header, whose logo is pinned to the
  // left edge — measured at every desktop width.
  //
  // Latched on `iframe:element-clicked` and consumed by the rect that follows,
  // for two reasons: the click payload carries no geometry, and the latch is what
  // makes this CANVAS-ORIGIN ONLY. A selection made from the rail itself — a
  // section-list row, a breadcrumb — must never collapse the rail the user is
  // working in, and cannot reach this handler.
  let _yieldRailOnNextRect = false;
  eventBus.on('iframe:element-clicked', () => {
    _yieldRailOnNextRect = true;
  });
  eventBus.on('iframe:element-rect', (data) => {
    if (!_yieldRailOnNextRect) return;
    _yieldRailOnNextRect = false;
    if (paletteEl.classList.contains('pb-collapsed')) return;
    const targetRect = toViewportRect(previewIframe, data?.logoSlotRect || data?.rect);
    if (!isRectOccludedByRail(targetRect, paletteEl.getBoundingClientRect())) {
      restoreLeftRailAfterCanvas();
      return;
    }
    yieldLeftRailForCanvas();
    _schedulePreviewLayoutRefresh();
  });
  eventBus.on('iframe:selected-editable-cleared', () => {
    restoreLeftRailAfterCanvas();
    _schedulePreviewLayoutRefresh();
  });

  eventBus.on('sidebar:ensure-open', () => {
    if (paletteEl.classList.contains('pb-collapsed')) {
      paletteEl.classList.remove('pb-collapsed');
      _writeLeftPanelCollapsed(false);
      eventBus.emit('sidebar:state-changed', { open: true });
    }
  });

  // Sidebar open/close shifts the canvas. Force a geometry + paint refresh
  // to keep iframe/overlay rendering stable.
  eventBus.on('sidebar:state-changed', () => {
    _schedulePreviewLayoutRefresh();
  });

  // Also wire the viewport label.
  //
  // CMD-3 fix, one phase late: this read `toolbar.viewportControls`, and CMD-2
  // moved the viewport controls to the command bar's row-2 right anchor. The
  // element it looks for DOES exist (index.html:170), so since that commit this
  // line has thrown `Cannot read properties of undefined` on every boot,
  // mid-`wireEvents`. Nothing caught it because no test boots the shell and the
  // app cannot boot offline at all.
  const vpLabel = document.getElementById('pb-viewport-label');
  if (vpLabel) commandBar?.viewportControls?.setLabelElement?.(vpLabel);

  // Initial render
  await renderPreview({ preserveScroll: true, transition: null, hideDuringLoad: false });

  const iframeWrap = previewIframe.closest('.pb-iframe-wrap');
  if (iframeWrap) {
    const emptyStage = new CanvasEmptyStage();
    emptyStage.mount(iframeWrap);
  }

  // Play the boot entrance once the toolbar, panels, autosave-restored state,
  // and first rendered canvas are present. The richer glass editing overlay
  // hydrates just after this so it no longer blocks the first usable shell.
  requestAnimationFrame(() => {
    playAppEntrance();
    if (iframeWrap) {
      setTimeout(() => {
        void initGlassEditorSurface(iframeWrap);
      }, 0);
    }
  });

  // First-run toolbar gate: body[data-state="first-run"] hides all toolbar
  // clusters except the project chip / Cmd+K hint / auth chip / next-step
  // chip until the user has at least one section on any page.
  const computeFirstRun = (s) =>
    Array.isArray(s?.pages) && s.pages.every((p) => !p.sections?.length);
  const applyFirstRunFlag = (s) => {
    document.body.dataset.state = computeFirstRun(s) ? 'first-run' : '';
  };
  applyFirstRunFlag(store.getState());
  eventBus.on('state-changed', applyFirstRunFlag);

  // "Restart Tour" command-palette action — clear onboarding + milestone
  // flags and re-run the guided coach-marks flow from Step 1.
  eventBus.on('cmd:restart-tour', async () => {
    const onboarding = await import('./onboarding.js');
    onboarding.restartTour();
  });

  // "Take the tour" — open the chaptered tour hub. Lazy-loaded so the tour bundle
  // stays off the boot path for users who never open it.
  eventBus.on('cmd:open-tour', async () => {
    try {
      const { openTourHub } = await import('./tour/tour-hub.js');
      openTourHub();
    } catch (err) {
      console.error('[Shell] Failed to open Take the tour', err);
    }
  });

  // First-run onboarding (lazy-loaded, skips fetch entirely for returning users)
  let skipOnboarding = false;
  // Playwright/WebDriver sessions should not be blocked by first-run onboarding.
  if (navigator.webdriver) skipOnboarding = true;
  try {
    const ob = JSON.parse(localStorage.getItem('pb-onboarding'));
    if (ob && ob.version >= 1) skipOnboarding = true;
  } catch {
    /* corrupt or missing — run onboarding, it will overwrite */
  }
  if (!skipOnboarding) {
    const onboarding = await import('./onboarding.js');
    // maybeStartOnboarding handles both branches internally — if sections
    // already exist it runs the post-stage coach marks immediately; if not,
    // it waits on the empty stage's `stage:active-changed` deactivation
    // event before firing. Either way the stage owns the "first action".
    onboarding.maybeStartOnboarding();
  }

  // Auth state is already established by ensureAuthenticated() at the top
  // of init(), so the post-mount auth check that used to live here is gone.

  console.log('[Shell] PageStitch initialized');
}

// ── Auth-first gate helpers ──────────────────────────────────────────────────

/**
 * Tear down the boot placeholder that `index.html` paints on the first frame.
 * The placeholder used to be removed in the auth `finally` while the shell was
 * still `opacity: 0`, which is what produced the 1–3 s blank window on large
 * projects. The full-screen sign-in does not need it either — it renders at
 * `--pb-z-modal`, one tier above `--pb-z-boot`.
 */
function removeAppLoadingPlaceholder() {
  const el = document.getElementById('pb-app-loading');
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * First reveal phase: show a disabled shell skeleton as soon as auth starts.
 * Auth, autosave recovery, state init, iframe render, and glass overlays keep
 * running afterwards; playAppEntrance() clears this loading shell when real
 * chrome/canvas content is ready.
 */
function revealBootShell() {
  if (_bootShellRevealed) return;
  _bootShellRevealed = true;
  removeAppLoadingPlaceholder();
  globalThis.__pbBoot?.reached();
  const appEl = document.querySelector('.pb-app');
  if (!appEl) return;
  appEl.classList.remove('pb-app-booting');
  appEl.classList.add('pb-app-shell-loading');
  appEl.setAttribute('aria-busy', 'true');
  // CMD-8b — the bar's shape, at the bar's size. The block this replaces was
  // sized by `--pb-toolbar-height` (56px) for a bar that measures 90, so the
  // canvas spent the whole load 34px too high and dropped when the bar landed.
  mountBootSkeleton(document.getElementById('pb-toolbar'));
  showIframeLoading(true, 'loading');
}

/**
 * Reveal the shell with the staggered boot entrance (toolbar → left panel →
 * canvas rise-in). Idempotent so repeated ready signals are harmless; the first
 * wins. Clears the early disabled shell state armed at auth start. Under reduced
 * motion, reveals instantly with no animation. See the `pb-app-enter`/
 * `pb-app-shell-loading` rules in builder.css.
 *
 * revealBootShell() is also called here defensively: if a future path skips the
 * early reveal, a missing shell root is still not a reason to leave "Loading…"
 * on screen forever.
 */
function playAppEntrance() {
  if (_appEntrancePlayed) return;
  _appEntrancePlayed = true;
  revealBootShell();
  showIframeLoading(false);
  const appEl = document.querySelector('.pb-app');
  if (!appEl) return;
  appEl.classList.remove('pb-app-shell-loading');
  appEl.removeAttribute('aria-busy');
  // Defensive, like the revealBootShell() call above: a boot path that never
  // built a bar is not a reason to leave its skeleton on screen forever.
  unmountBootSkeleton();

  const reduced = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    return;
  }

  appEl.classList.add('pb-app-enter');

  // Remove the one-shot class once the last region settles so no lingering
  // animation state fights the sidebar collapse / canvas-shift transforms.
  const canvasEl = appEl.querySelector('.pb-iframe-wrap'); // longest delay (150ms)
  let safetyTimer = 0;
  const cleanup = () => {
    clearTimeout(safetyTimer);
    canvasEl?.removeEventListener('animationend', onEnd);
    appEl.classList.remove('pb-app-enter');
  };
  // Filter to OUR animation on the canvas wrapper itself — animationend bubbles,
  // so a child overlay's pulse/spinner ending mid-entrance must not cut it short.
  function onEnd(e) {
    if (e.target === canvasEl && e.animationName === 'pb-app-enter-up') cleanup();
  }
  canvasEl?.addEventListener('animationend', onEnd);
  safetyTimer = setTimeout(cleanup, 1000); // net if animationend never fires
}

/**
 * Block init() until the initial auth probe settles. Loops between a reachable
 * auth check and the full-screen sign-in — the screen only resolves on
 * successful auth. An unavailable transport returns null after the bounded
 * probe so local editing can boot without claiming an authenticated session.
 * (Mid-session re-auth still uses the non-strict auth MODAL via
 * `toolbar:auth-show`; only the boot surface changed.)
 *
 * Accepts any server-confirmed auth response (including the single-user dev
 * mode the Worker returns when ALLOW_LEGACY_ADMIN is on). The server is the
 * source of truth: don't double-gate on the client-side __PB_ALLOW_LEGACY_ADMIN
 * flag here, or a deadlock results when only one side has it set.
 *
 * Dev affordance: `?auth-screen` / localStorage 'pb:force-auth-screen' forces
 * the screen even when the local gate self-passes (proxy single-user stub) —
 * the passing payload rides in as devAuthPayload so "Continue as admin"
 * resolves it directly.
 */
async function ensureAuthenticated() {
  let forceScreen = isAuthScreenForced();
  // Carried from a failed checkAuth into the sign-in screen. Only set for the
  // private-beta gate, where the session was VALID and the account was refused
  // — an ordinary "not signed in" 401 has nothing to explain.
  let initialNotice = '';
  while (true) {
    let devAuthPayload = null;
    try {
      const auth = await checkAuth({ timeoutMs: INITIAL_AUTH_TIMEOUT_MS });
      if (auth && (isRealUser(auth) || auth.authMode === 'single-user')) {
        if (!forceScreen) {
          setCurrentUser(auth);
          return auth;
        }
        devAuthPayload = auth;
      }
    } catch (err) {
      if (shouldDegradeInitialAuth(err, globalThis.location)) {
        console.warn('[Shell] auth service unavailable; continuing in offline mode');
        return null;
      }
      if (err instanceof TemplateApiError && err.data?.code === 'access_not_allowed') {
        initialNotice = err.message;
      } else if (!(err instanceof TemplateApiError && err.status === 401)) {
        console.warn('[Shell] auth check failed:', err?.message || err);
      }
    }
    // Full-screen sign-in — single-flight (see auth-screen.js), resolves only
    // on successful auth (password/signup, Google, passkey, or dev admin).
    // The boot placeholder is deliberately left in place beneath it (see
    // --pb-z-boot): the sign-in screen removes its own root before resolving,
    // so the placeholder is what covers the app build that follows instead of
    // a blank page. Boot has reached a real surface, so stand the watchdog down
    // — a user reading the sign-in form is not a stalled boot.
    globalThis.__pbBoot?.reached();
    const result = await showAuthScreen({ devAuthPayload, initialNotice });
    initialNotice = '';
    forceScreen = false;
    consumeAuthScreenForce();
    if (result && (isRealUser(result) || result.authMode === 'single-user')) {
      setCurrentUser(result);
      return result;
    }
  }
}

// ── Accessibility utilities ──

/**
 * Adds scroll-fade gradient edges and arrow-key scrolling to a
 * horizontally-scrollable container. wrapperEl gets the .pb-scroll-fade
 * class; scrollEl is the actual overflow element inside it.
 */
export function initScrollFade(wrapperEl, scrollEl) {
  wrapperEl.classList.add('pb-scroll-fade');
  const raf =
    typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (fn) => setTimeout(fn, 0);
  let updateRaf = 0;

  function update() {
    updateRaf = 0;
    const { scrollLeft, scrollWidth, clientWidth } = scrollEl;
    wrapperEl.classList.toggle('pb-can-scroll-left', scrollLeft > 2);
    wrapperEl.classList.toggle('pb-can-scroll-right', scrollLeft + clientWidth < scrollWidth - 2);
  }

  function scheduleUpdate() {
    if (updateRaf) return;
    updateRaf = raf(update);
  }

  scrollEl.addEventListener('scroll', scheduleUpdate, { passive: true });
  new ResizeObserver(scheduleUpdate).observe(scrollEl);
  update();

  // Make focusable for arrow-key scrolling
  if (!scrollEl.hasAttribute('tabindex')) {
    scrollEl.setAttribute('tabindex', '0');
  }

  scrollEl.addEventListener('keydown', (e) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (typeof scrollEl.scrollBy !== 'function') return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollEl.scrollBy({ left: 100, behavior: 'smooth' });
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollEl.scrollBy({ left: -100, behavior: 'smooth' });
    }
  });

  // Return update function so callers can refresh after content changes
  return update;
}

// gridKeyNav moved verbatim to ui/grid-key-nav.js (CB-3) so shell-free
// modules can import it without dragging this file into their graph.
// Re-exported here so existing `import { gridKeyNav } from './shell.js'`
// call sites are untouched.
export { gridKeyNav } from './grid-key-nav.js';
